import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Button from '../ui/Button';
import ActionRow from '../ui/ActionRow';
import { Link } from 'react-router';
import { ImageOff } from 'lucide-react';
import { rotationDegrees } from '../LabelPhotoImage';
import { LabelPhotoOpener, fileLinkTitle } from '../LabelPhotoViewer';
import RotatePhotoButton from './RotatePhotoButton';
import SideDrawer from '../SideDrawer';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../pilot-ui/tabs';
import { Checkbox } from '../pilot-ui/checkbox';
import { Input } from '../pilot-ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../pilot-ui/select';
import { ToggleGroup, ToggleGroupItem } from '../pilot-ui/toggle-group';
import ModelLink from '../ModelLink';
import { sourceLabel } from '../../utils/equipmentQuality';
import { WHOLE_HOUSE_FILTER } from '../../utils/clipLabel';
import { own } from '../../utils/ownKey';
import { refreshDeviceEnrichment } from '../../hooks/useFirestoreEnrichment';
import {
  COMPANION_FIELDS,
  EQUIPMENT_EDIT_ERRORS,
  EquipmentChange,
  EquipmentDocument,
  EquipmentEvidenceRemoved,
  EquipmentRemovedPhoto,
  PhotoLastUndo,
  PhotoRemovalNotice,
  PhotoRemovalReasonKind,
  PhotoRemovalResult,
  PhotoStorageState,
  dismissPhotoRemovalNotice,
  equipmentErrorCode,
  equipmentErrorDetails,
  photoRemovalErrorMessage,
  PhotoUndoResult,
  PhotoDetachResult,
  PhotoKeepReason,
  PhotoReviewPlacementDecision,
  detachPhoto,
  removePhoto,
  retryPhotoProtection,
  serverAnswered,
  undoPhotoRemoval,
  EquipmentPhotoAnalysis,
  EquipmentPhotoAnalysisRequest,
  EquipmentPhotoDisposition,
  EquipmentPhotoTargetKind,
  EquipmentRecord,
  EquipmentTargetKind,
  MIN_REASON_LENGTH,
  PhotoComponentHint,
  PhotoReview,
  PhotoReviewKind,
  PhotoReviewSummary,
  PhotoReviewStatus,
  PhotoReviewTab,
  declinePhotoReview,
  DismissedReviewSummary,
  rebindCatalogReview,
  reopenCatalogReview,
  ReviewFixResult,
  resyncOutcome,
  reviewKindOf,
  PHOTO_ACCEPT,
  PHOTO_DEFAULT_REASON,
  createFilterRecord,
  equipmentErrorMessage,
  getEquipment,
  getFilterBrands,
  isCalendarDate,
  newEditId,
  patchEquipment,
  photoFileError,
  photoReason,
  photoSizeLabel,
  photoTarget,
  photoUploadErrorMessage,
  uploadEquipmentPhoto,
  rereadEquipmentPhoto,
  photoRereadErrorMessage,
  PHOTO_REREAD_DEFAULT_REASON,
} from '../../api/equipmentEdit';
import {
  ABSENT_COMPONENT_OPTIONS,
  EquipmentFieldSpec,
  FILTER_FIELDS,
  FILTER_SLOT_FIELDS,
  FILTER_TYPE_OPTIONS,
  changeValue,
  displayValue,
  editableValue,
  fieldsFor,
  optionLabel,
  selectedComponents,
  specFor,
  specForFilterType,
  withFilterLocationWording,
} from './equipmentEditFields';
import PhotoReviewDialog, {
  IDLE_POLL,
  ReviewPollMode,
  ReviewPollState,
  pollErrorText,
  useReviewStatusPoll,
} from './PhotoReviewDialog';
import {
  CATALOG_SOURCE_WORDS,
  catalogHeldWords,
  heldWords,
  KIND_WORDS,
  kindOfPath,
  outcomeWords,
  TAB_WORDS,
} from './photoReviewWords';

/**
 * FS-502 — correct a homeowner's equipment and filter records on their behalf.
 *
 * Three things shape this screen. Every value on it is one the homeowner also
 * sees in the app, so each field shows what is on file and where that came
 * from before it offers a box to change it. Every write is refusable for
 * several very different reasons, so Preview runs the same request with
 * nothing written and shows the exact before/after the server computed. And a
 * write races the app: the version the drawer read is sent back with the
 * change, so a record somebody edited in between is refused rather than
 * overwritten.
 *
 * A device with duplicate app registrations is read-only here on purpose. The
 * resolver cannot say which record is the device's, and guessing would write a
 * correction onto the wrong home — the duplicates get cleaned up on the App
 * Claims tab first.
 */

interface EditEquipmentDrawerProps {
  device: { id: number; name: string };
  onClose: () => void;
  /**
   * Reloads the device after a write. FS-952: only for a caller that does not
   * follow the projection (`onWritten`), three times over the next 15
   * seconds; a caller that follows it reloads once the projection has moved.
   */
  onSaved?: () => void;
  /**
   * Plan 123 — the record to open on. The Missing tab sends an operator here
   * for one named thing ("enter the tonnage by hand", "this home has no
   * outdoor unit"), and landing them on the indoor unit to find it themselves
   * is how a two-click correction becomes a hunt.
   */
  initialTab?: EquipmentTargetKind;
  /**
   * The field to put the cursor in once the record has loaded, by its
   * document field name. Ignored when the record has no such box.
   */
  focusField?: string;
  /**
   * FS-593 — the caller's `projection_updated_at` as it stands now (null when
   * it has none). Read immediately before every write is sent, so the save
   * is later recognized by the server's own stamp moving, never by the
   * browser's clock.
   */
  projectionStamp?: () => string | null;
  /**
   * FS-593 — called once, the moment a write lands, with the stamp read just
   * before it was sent, so the caller can follow the projection until it
   * has moved past it.
   */
  onWritten?: (baseline: string | null) => void;
  /**
   * FS-593 — Clip serial (lower-case) → device id, for the Clips the caller
   * knows about, so a filter slot held by another Clip can link to its page.
   */
  clipLinks?: Record<string, number>;
}

/**
 * FS-593 — what the console may do with one filter record.
 * - `own`: this Clip's current filter; every field.
 * - `slot`: no Clip is recorded in it; its type and name only.
 * - `older_own`: an older record naming this Clip; its type and name only.
 * - `other`: another Clip is recorded in it; read-only here, edited from that
 *   Clip's page.
 * A row from an API before FS-593 carries no `own`, and `is_current` stands in.
 */
export type FilterRowAccess = 'own' | 'slot' | 'older_own' | 'other';

export function filterRowAccess(doc: EquipmentDocument, serial: string): FilterRowAccess {
  if (typeof doc.own === 'boolean' ? doc.own : doc.is_current === true) return 'own';
  const name = doc.device_name ? doc.device_name.trim().toLowerCase() : null;
  if (!name) return 'slot';
  if (name === serial.trim().toLowerCase()) return 'older_own';
  return 'other';
}

/** A filter record's type, read from the slot field or, failing that, the document. */
const filterTypeOf = (doc: EquipmentDocument): string | null => {
  const t = doc.filter_type !== undefined ? doc.filter_type : doc.fields.filterType;
  return typeof t === 'string' && t.trim() ? t : null;
};

const filterLocationOf = (doc: EquipmentDocument): string | null => {
  const l = doc.location !== undefined ? doc.location : doc.fields.location;
  return typeof l === 'string' && l.trim() ? l.trim() : null;
};

/** "Hallway — return grille", "Return grille 2", "Whole-house filter", "Filter record with no type". */
export function filterRowLabel(doc: EquipmentDocument, grilleNumber: number | undefined): string {
  const type = filterTypeOf(doc);
  const kind =
    type === 'airReturns'
      ? `Return grille${grilleNumber ? ` ${grilleNumber}` : ''}`
      : type === 'furnace'
        ? WHOLE_HOUSE_FILTER // FS-825: the console's one word for the furnace type.
        : 'Filter record with no type';
  const name = filterLocationOf(doc);
  return name ? `${name} — ${kind.charAt(0).toLowerCase()}${kind.slice(1)}` : kind;
}

/** The last segment of a document path: the id a slot is named by. */
const docId = (doc: EquipmentDocument): string =>
  doc.id ?? doc.path.split('/').filter(Boolean).pop() ?? '';

/**
 * Plan 120 — the tabs are named after the machines, the way the Overview
 * cards are. "HVAC" and "A/C" named the documents, which was survivable while
 * there were three of them and is not now that a coil is a fourth: an
 * operator has to know which record they are editing before they type into
 * it.
 */
const TABS: Array<{ key: EquipmentTargetKind; label: string }> = [
  { key: 'hvac', label: 'Indoor unit' },
  { key: 'ac', label: 'Outdoor unit' },
  { key: 'coil', label: 'Indoor coil (A-coil)' },
  { key: 'filter', label: 'Filter' },
];

/**
 * Plan 120 — a coil takes field edits only.
 *
 * Photos attach to the HVAC or A/C document and reach a coil only through the
 * server's per-photo routing, so there is nothing for an upload on this tab to
 * be attached TO. A drop zone that filed the photo somewhere else would be
 * worse than none at all.
 */
const PHOTO_TARGET_KINDS: EquipmentTargetKind[] = ['hvac', 'ac', 'filter'];
const acceptsPhotos = (kind: EquipmentTargetKind): kind is EquipmentPhotoTargetKind =>
  PHOTO_TARGET_KINDS.includes(kind);

const LOADING_TEXT = 'Loading the equipment record…';

/**
 * After a write, when the device detail is reloaded for a caller that does
 * not follow the projection itself (FS-952: every caller in the app does).
 */
const REFRESH_DELAYS_MS = [5000, 10000, 15000];

/**
 * How the drawer follows a label read: a minute of five-second polls. The read
 * is a queued job behind an upload, so the wait is real; a minute is long
 * enough for the ones that finish and short enough that a stuck one says so
 * rather than spinning at the operator.
 */
const PHOTO_POLL_MS = 5000;
const PHOTO_POLL_MAX = 12;

const NUMBER_HELP = 'Enter a number, for example 3.5.';
const DATE_HELP = 'Enter the install date as YYYY-MM-DD, for example 2026-09-12.';

/** A document path is not an id, so make one that is safe in `htmlFor`. */
const idPrefix = (path: string) => path.replace(/[^a-zA-Z0-9]+/g, '-');

interface ParsedField {
  value: unknown;
  error?: string;
}

/** One typed box turned into the value the server will store, or the reason it cannot be. */
function parseField(spec: EquipmentFieldSpec, raw: string): ParsedField {
  const trimmed = raw.trim();
  // An empty set is a value, not a blank: ticking nothing and saving is how a
  // declared absence is withdrawn, and `null` would not clear it.
  if (spec.kind === 'components') return { value: selectedComponents(raw) };
  if (trimmed === '') return { value: null };
  if (spec.kind === 'number') {
    const n = Number(trimmed);
    if (!Number.isFinite(n)) return { value: null, error: NUMBER_HELP };
    return { value: n };
  }
  if (spec.kind === 'boolean') return { value: trimmed === 'true' };
  if (spec.kind === 'date') {
    if (!isCalendarDate(trimmed)) return { value: null, error: DATE_HELP };
    return { value: trimmed };
  }
  return { value: trimmed };
}

/** True when the typed value is the value already on file, so it is not sent. */
function isUnchanged(current: unknown, next: unknown): boolean {
  if (Array.isArray(next)) {
    const before = (Array.isArray(current) ? current : []).map(String).sort();
    const after = next.map(String).sort();
    return before.length === after.length && before.every((v, i) => v === after[i]);
  }
  if (next === null) return current === null || current === undefined || current === '';
  if (typeof next === 'number') return Number(current) === next;
  if (typeof next === 'boolean') return current === next;
  return String(current ?? '') === String(next);
}

/**
 * The words for where a value came from, or the fact that nothing recorded it.
 * FS-787: a catalog value (`spec_lookup`) is named as the review dialog names
 * it, so the drawer and the dialog say the same thing.
 */
const provenance = (source: string | undefined): string =>
  source === 'spec_lookup' ? CATALOG_SOURCE_WORDS : source ? sourceLabel(source) : 'source not recorded';

/** FS-783 — a day the way an operator reads it: "Jan 2, 2027". */
const dayText = (iso: string | null | undefined): string => {
  if (!iso) return 'an unknown date';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
};

/**
 * FS-863 — who removed a photo, by sign-in email. An `operator:u5` the server
 * found no account for (`removed_by_email` null) reads "a deleted account
 * (#5)"; one it could not look up (the key absent) reads "an operator", never
 * a bare number. Any other value ("you", for a removal made in this session)
 * is shown as it is, and nothing at all as "an operator".
 */
const removerText = (entry: Pick<EquipmentRemovedPhoto, 'removed_by' | 'removed_by_email'>): string => {
  if (entry.removed_by_email) return entry.removed_by_email;
  const match = /^operator:u(\d+)$/.exec(entry.removed_by ?? '');
  if (match) return entry.removed_by_email === null ? `a deleted account (#${match[1]})` : 'an operator';
  return entry.removed_by || 'an operator';
};

/**
 * FS-783 — one record named the way the removal panel names it:
 * `HVAC system "Furnace"`, `Outdoor unit "Air Conditioner" (model NXA642)`.
 * A removal reaches every record that carries the photo, so the operator has
 * to be able to tell them apart before pressing Remove, not after.
 */
function recordWords(record: EquipmentRecord | null, path: string, fallbackName?: string | null): string {
  const kind = kindOfPath(path);
  const docs = record ? [record.hvac, ...record.ac_systems, ...(record.coils ?? []), ...record.filters] : [];
  const doc = docs.find((d) => d?.path === path) ?? null;
  const text = (v: unknown) => (typeof v === 'string' ? v.trim() : '');
  const name = text(doc?.fields.name) || text(fallbackName);
  const model = text(doc?.fields.modelNumber);
  if (kind === 'hvac') return name ? `HVAC system "${name}"` : 'HVAC system';
  if (kind === 'ac') return `${name ? `Outdoor unit "${name}"` : 'Outdoor unit'}${model ? ` (model ${model})` : ''}`;
  if (kind === 'coil') return `Indoor coil${model ? ` (model ${model})` : ''}`;
  const location = doc ? filterLocationOf(doc) : null;
  return location ? `Filter "${location}"` : 'Filter record';
}

const Banner: React.FC<{ tone: 'warn' | 'info'; children: React.ReactNode }> = ({
  tone,
  children,
}) => (
  <div
    className={`rounded-md border p-3 text-sm ${
      tone === 'warn' ? 'border-status-warning-border bg-status-warning text-status-warning-foreground' : 'border-border bg-muted text-muted-foreground'
    }`}
  >
    {children}
  </div>
);

/**
 * The before → after → source table the dry run produces. It is the server's
 * own computation, not the drawer's: the point of Preview is to see what the
 * write would do, worked out by the code that would do it.
 */
const DiffTable: React.FC<{
  kind: EquipmentTargetKind;
  changes: Record<string, EquipmentChange>;
  /** FS-825 — the filter type the card has selected, which names the filter's name box. */
  filterType?: unknown;
}> = ({ kind, changes, filterType }) => {
  const rows = Object.entries(changes);
  return (
    <div data-testid="equipment-diff" className="mt-3 rounded-md border border-status-info-border bg-blue-50 p-3">
      <p className="text-sm font-medium text-blue-900">
        Preview — nothing has been saved yet.
      </p>
      {rows.length === 0 ? (
        <p className="mt-2 text-sm text-blue-900">
          The server found no difference between what is on file and what was typed.
        </p>
      ) : (
        <table className="mt-2 min-w-full text-sm">
          <thead>
            <tr className="text-left text-blue-900">
              <th className="py-1 pr-4 font-medium">Field</th>
              <th className="py-1 pr-4 font-medium">On file</th>
              <th className="py-1 pr-4 font-medium">After saving</th>
              <th className="py-1 pr-4 font-medium">Source on file</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(([field, change]) => {
              const spec = specForFilterType(kind, field, filterType);
              return (
                <tr key={field} className="border-t border-status-info-border">
                  <td className="py-1 pr-4">{spec?.label ?? field}</td>
                  <td className="py-1 pr-4">{changeValue(spec, change.before)}</td>
                  <td className="py-1 pr-4 font-medium">{changeValue(spec, change.after)}</td>
                  <td className="py-1 pr-4 text-blue-900">{provenance(change.before_source ?? undefined)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
};

/** One editable field: what is on file, where it came from, and the pending change. */
const FieldRow: React.FC<{
  spec: EquipmentFieldSpec;
  prefix: string;
  current: unknown;
  source: string | undefined;
  options: string[];
  edited: string | undefined;
  error: string | undefined;
  disabled: boolean;
  /**
   * True when this field holds an equipment model number, so the value on
   * file is a link into the Equipment Catalog. Only ever the value on file —
   * the box above it is what the operator is typing, and following a link out
   * of a half-filled correction loses the correction.
   */
  catalogModel?: boolean;
  /** FS-784 — a photo review just wrote this field: ring it for a while. */
  fromPhoto?: boolean;
  /** FS-787 — the words over the ring: "From label photo" or "From the catalog". */
  fromLabel?: string;
  /**
   * FS-784 — a photo review wrote this field while a value typed here was
   * still unsaved. The typed value is kept; this says so.
   */
  photoChangedUnderEdit?: boolean;
  /**
   * FS-783 — the value on file was read from a photo that has since been
   * removed. Derived on the server from the record's removals, never stored
   * per field, so it goes away by itself once the value is re-entered.
   */
  evidenceRemoved?: EquipmentEvidenceRemoved | null;
  /** FS-783 — read from a photo, but the stamp never said which one. */
  uncertainEvidence?: boolean;
  /** FS-783 — the field this value was worked out with, still to be set or cleared too. */
  companionNeeded?: string | null;
  /** FS-783 — flagged, and the server will not leave it empty: it has to be retyped or confirmed. */
  cannotClear?: boolean;
  /**
   * Addendum 2 F12 — the Confirm control on a flagged value: null when the
   * field is not flagged (or the card is read-only), else whether it is on.
   */
  confirmState?: 'on' | 'off' | null;
  onConfirm?: () => void;
  onChange: (raw: string) => void;
}> = ({
  spec,
  prefix,
  current,
  source,
  options,
  edited,
  error,
  disabled,
  catalogModel,
  fromPhoto,
  fromLabel = 'From label photo',
  photoChangedUnderEdit,
  evidenceRemoved,
  uncertainEvidence,
  companionNeeded,
  cannotClear,
  confirmState = null,
  onConfirm,
  onChange,
}) => {
  const id = `${prefix}-${spec.name}`;
  const helpId = spec.help ? `${id}-help` : undefined;
  const raw = edited ?? editableValue(current);
  const parsed = edited === undefined ? null : parseField(spec, edited);
  const pending = parsed && !parsed.error && !isUnchanged(current, parsed.value) ? parsed : null;

  // A set of names is ticked, not typed: the vocabulary is two items long and
  // a text box over it would only invent refusals.
  const ticked = spec.kind === 'components' ? selectedComponents(raw) : [];

  const control =
    spec.kind === 'components' ? (
      <fieldset
        id={id}
        tabIndex={-1}
        aria-describedby={helpId}
        className="rounded-sm border px-2 py-1.5"
      >
        <legend className="sr-only">{spec.label}</legend>
        <div className="flex flex-wrap gap-x-4 gap-y-1">
          {options.map((option) => {
            const boxId = `${id}-${option}`;
            return (
              <label key={option} htmlFor={boxId} className="flex items-center gap-1.5 text-sm">
                <Checkbox
                  id={boxId}
                  checked={ticked.includes(option)}
                  disabled={disabled}
                  title={`${optionLabel(spec.options, option)}. ${spec.help ?? 'Record whether this part is on the system.'}`}
                  onCheckedChange={(checked) =>
                    onChange(
                      (checked
                        ? [...ticked, option]
                        : ticked.filter((name) => name !== option)
                      ).join(','),
                    )
                  }
                />
                {optionLabel(spec.options, option)}
              </label>
            );
          })}
        </div>
      </fieldset>
    ) : spec.kind === 'enum' || spec.kind === 'boolean' ? (
      <Select value={raw} onValueChange={(value) => onChange(value ?? '')} disabled={disabled}>
        <SelectTrigger id={id} aria-describedby={helpId} title={spec.help ?? `Set ${spec.label}`} className="w-full">
          <SelectValue>{raw === ''
            ? (spec.clearable === false ? '— not recorded —' : '— clear this value —')
            : (spec.kind === 'boolean' ? (raw === 'true' ? 'yes' : 'no') : optionLabel(spec.options, raw))}</SelectValue>
        </SelectTrigger>
        <SelectContent>
          {spec.clearable === false ? (
            // The server will not clear it, so blank is only ever what is on
            // file — "not recorded" — never a choice to send.
            <SelectItem value="" disabled>
              — not recorded —
            </SelectItem>
          ) : (
            <SelectItem value="">— clear this value —</SelectItem>
          )}
          {spec.kind === 'boolean' ? (
            <>
              <SelectItem value="true">yes</SelectItem>
              <SelectItem value="false">no</SelectItem>
            </>
          ) : (
            options.map((option) => (
              <SelectItem key={option} value={option}>
                {optionLabel(spec.options, option)}
              </SelectItem>
            ))
          )}
        </SelectContent>
      </Select>
    ) : (
      <Input
        id={id}
        type="text"
        value={raw}
        disabled={disabled}
        aria-describedby={helpId}
        title={spec.help ?? `Set ${spec.label}`}
        placeholder={spec.kind === 'date' ? 'YYYY-MM-DD' : undefined}
        onChange={(e) => onChange(e.target.value)}
      />
    );

  return (
    <div
      className={`flex flex-col ${fromPhoto ? 'rounded-sm ring-2 ring-status-success-border ring-offset-2' : ''}`}
      data-from-photo={fromPhoto ? 'true' : undefined}
    >
      {fromPhoto && (
        <span className="text-[10px] font-semibold uppercase text-green-700" data-testid="field-from-photo">
          {fromLabel}
        </span>
      )}
      {spec.kind === 'components' ? (
        // The boxes carry their own labels, and the group is named by the
        // fieldset's legend; a second `for=` here would point at nothing.
        <span className="text-xs uppercase tracking-wide text-muted-foreground">{spec.label}</span>
      ) : (
        <label htmlFor={id} className="text-xs uppercase tracking-wide text-muted-foreground">
          {spec.label}
        </label>
      )}
      <div className="mt-1">{control}</div>
      <span className="mt-1 text-xs text-muted-foreground">
        {'on file: '}
        {catalogModel && typeof current === 'string' && current.trim().length > 0 ? (
          // In a new tab: this drawer holds corrections that are not saved
          // yet, and navigating to the catalog would throw them away.
          <ModelLink model={current} newTab />
        ) : (
          displayValue(spec, current)
        )}
        {` · ${provenance(source)}${evidenceRemoved ? ' (photo removed)' : ''}`}
      </span>
      {evidenceRemoved && (
        <span
          data-testid="field-evidence-removed"
          className={`text-xs ${evidenceRemoved.reason_kind === 'duplicate' ? 'text-muted-foreground' : 'text-amber-800'}`}
          title={`The photo this value was read from was removed on ${dayText(evidenceRemoved.removed_at)}. The value is kept until you clear or replace it.`}
        >
          {/* A duplicate took nothing away that another photo does not still
              show, so it is muted; any other reason asks for a look. */}
          {evidenceRemoved.reason_kind === 'duplicate'
            ? 'Read from a photo removed as a duplicate'
            : `Evidence removed: ${evidenceRemoved.reason ?? 'no reason recorded'} (${dayText(evidenceRemoved.removed_at)})`}
        </span>
      )}
      {uncertainEvidence && !evidenceRemoved && (
        <span
          data-testid="field-evidence-uncertain"
          className="text-xs text-muted-foreground"
          title="This value was read from a photo, but which photo was not recorded, so it cannot be said whether the removed photo was its source."
        >
          Source photo not recorded; check this value
        </span>
      )}
      {cannotClear && (
        <span data-testid="field-cannot-clear" className="text-xs text-amber-800">
          This field can't be left empty: type the right value
        </span>
      )}
      {confirmState && (
        <span className="text-xs">
          <Button
            variant="secondary"
            size="sm"
            aria-pressed={confirmState === 'on'}
            disabled={disabled}
            onClick={onConfirm}
            title={
              confirmState === 'on'
                ? 'Take back the confirmation; the value stays marked as read from a removed photo'
                : 'Keep this value as it is and record it as checked by you; it stops being marked when you save'
            }
          >
            {confirmState === 'on' ? 'Confirmed: still right' : 'Confirm still right'}
          </Button>
          {confirmState === 'on' && (
            <span className="ml-1 text-green-800">will be recorded as your entry</span>
          )}
        </span>
      )}
      {companionNeeded && (
        <span data-testid="field-companion-needed" className="text-xs text-amber-800" role="alert">
          {`${companionNeeded} was worked out from the same photo; set, clear or confirm it too`}
        </span>
      )}
      {pending && (
        <span className="text-xs text-blue-700">
          {`→ ${changeValue(spec, pending.value)} · will be recorded as your entry`}
        </span>
      )}
      {photoChangedUnderEdit && edited !== undefined && (
        <span className="text-xs text-amber-800" data-testid="photo-changed-under-edit">
          The photo changed this field while you were editing; your typed value is kept. Check it before
          saving.
        </span>
      )}
      {error && (
        <span className="text-xs text-red-700" role="alert">
          {error}
        </span>
      )}
      {spec.help && (
        <span id={helpId} className="text-xs text-muted-foreground">
          {spec.help}
        </span>
      )}
    </div>
  );
};

/** The reason box, on every write. The server enforces it too; this says why here. */
const ReasonField: React.FC<{
  prefix: string;
  value: string;
  error: string | null;
  disabled: boolean;
  onChange: (value: string) => void;
  /** The box's hover help. Addendum 2 F20: every control says what it is for. */
  title?: string;
}> = ({
  prefix,
  value,
  error,
  disabled,
  onChange,
  title = 'Why this record is being corrected, at least 8 characters; kept with the edit in the audit record',
}) => {
  const id = `${prefix}-reason`;
  return (
    <div className="mt-4">
      <label htmlFor={id} className="text-xs uppercase tracking-wide text-muted-foreground">
        Reason for this change
      </label>
      <Input
        id={id}
        type="text"
        value={value}
        disabled={disabled}
        title={title}
        aria-describedby={`${id}-help${error ? ` ${id}-error` : ''}`}
        aria-invalid={Boolean(error)}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1"
      />
      <span id={`${id}-help`} className="mt-1 block text-xs text-muted-foreground">
        This is kept with the edit, so say what the homeowner's record got wrong and how
        it was checked.
      </span>
      {error && (
        <span id={`${id}-error`} className="mt-1 block text-xs text-red-700" role="alert">
          {error}
        </span>
      )}
    </div>
  );
};

/** How the drawer's follow-up of a label read stands, for one record. */
export type PhotoReadState = 'polling' | 'gave_up' | null;

/**
 * How far the file in hand has got. The block reports every one of these,
 * because a step that shows nothing reads as nothing having happened — which
 * is exactly what the native file input does after its value is cleared.
 */
type PhotoPhase = 'idle' | 'selected' | 'validating' | 'uploading' | 'stored' | 'failed';

const PHOTO_HELP: Record<EquipmentPhotoTargetKind, string> = {
  hvac: 'The label is read automatically; fields it fills are marked as read from a photo and never replace a value a person entered.',
  ac: 'The label is read automatically; fields it fills are marked as read from a photo and never replace a value a person entered.',
  filter: 'Stored with the record. Filter labels are not read automatically.',
};

/** FS-784 — the help line when the read goes into a review instead of the record. */
const PHOTO_REVIEW_HELP =
  'The label is read, then you choose which values to use. Nothing on the record changes until you apply.';

const PHOTO_STILL_READING =
  'The label is still being read. Close the drawer and open it again to see the result.';

/**
 * Why every write in the drawer is held while a photo is in hand.
 *
 * A label read is a write nobody in this drawer made: the server fills the
 * record from the photo while the operator is looking at it. A PATCH sent into
 * that window either races that write or is refused for a version that changed
 * under it, and both come back to the operator as a correction that did not
 * happen. So the controls wait, and say what they are waiting for.
 */
const PHOTO_WAIT_TOOLTIP = 'Wait for the photo to finish being read';

/** FS-1078 — what Read again does, for an operator who has not used it. */
const READ_AGAIN_TITLE =
  'Read this label again with the current reader. No copy of the photo is added: the photo already here is read. ' +
  'A review opens with the new reading beside the values on file, and nothing on the record changes until you press Apply.';
const READ_AGAIN_STARTING = 'Asking for the read…';
const READ_AGAIN_READING = 'This photo is being read now. Read again is available once that read has finished.';
const READ_AGAIN_APPLYING = 'An Apply from this photo is running. Read again is available once it has finished.';
const READ_AGAIN_HOMEOWNER =
  "The homeowner's app is still reading this photo. Read again is available once that read has finished.";

/**
 * FS-1078 — why Read again cannot start on this photo now, or null: a read
 * of it on this record has not finished, a homeowner read of it is pending,
 * or a review of it is reading or applying. The server refuses the same
 * cases (`photo_busy`); this says so before the click.
 */
function readAgainBlock(
  doc: EquipmentDocument,
  reviews: PhotoReviewSummary[],
  url: string,
): string | null {
  const ownReviews = reviews.filter((r) => r.photo_url === url && r.source_path === doc.path);
  if (ownReviews.some((r) => r.status === 'applying')) return READ_AGAIN_APPLYING;
  const unread = (doc.photo_analysis_requests ?? []).some((row) => row.url === url && !row.processed_at);
  if (unread || ownReviews.some((r) => r.status === 'reading')) return READ_AGAIN_READING;
  const entries = [...(doc.label_photos ?? []), ...(doc.other_photo_urls ?? [])];
  if (entries.some((p) => p.url === url && p.homeowner_read_pending === true)) return READ_AGAIN_HOMEOWNER;
  return null;
}

/**
 * FS-877 — a card's Save sends only the values typed or confirmed on it. A
 * photo, a photo removal and an applied review are written when they happen,
 * so with nothing typed there is nothing for Save to do, and the card says so.
 */
const NOTHING_TO_SAVE_TITLE = 'Nothing to save: type a new value above. Photos and applied review values save on their own.';
const NOTHING_TO_PREVIEW_TITLE = 'Nothing to preview: type a new value above.';
const NOT_SAVED_YET = 'Not saved yet.';

/** What the zone says while a file is over it, and when nothing is. */
const PHOTO_DROP_ACTIVE = 'Drop the label photo here';
const PHOTO_DROP_IDLE = 'Or drag a photo onto this box.';

/**
 * One photo goes with one reason and one upload, so a drop of several is not
 * ambiguous — it takes the first — but it does have to say so, or the operator
 * walks away believing four photos were filed.
 */
const PHOTO_ONE_AT_A_TIME = 'One photo at a time — the first file was used';

/** A drag that carried something other than a file: a link, a selection, a folder. */
const PHOTO_NO_FILE =
  'That drop had no file in it. Drag a photo file, or choose one with the button above.';

/**
 * HEIC is what an iPhone takes, and the server converts a copy of it for the
 * label read. Saying so is the honest version: the photo is not being turned
 * away, and the thing read is not the file that was filed. A filter label is
 * only stored, so its line stops at the acceptance.
 */
const PHOTO_HEIC_NOTE: Record<EquipmentPhotoTargetKind, string> = {
  hvac: 'iPhone photos (HEIC) are accepted; the label is read from a converted copy.',
  ac: 'iPhone photos (HEIC) are accepted; the label is read from a converted copy.',
  filter: 'iPhone photos (HEIC) are accepted.',
};

/** The file name out of a photo URL, for a tile that cannot show the photo. */
const photoFileName = (url: string): string => {
  const last = url.split(/[?#]/)[0].split('/').filter(Boolean).pop() ?? '';
  if (!last) return url;
  try {
    return decodeURIComponent(last);
  } catch {
    return last;
  }
};

/**
 * FS-782 / FS-784 — what an upload hands the drawer. The drawer follows each
 * upload by its own edit id, never by "the latest request on this record":
 * two photos in flight on one record must each finish against their own
 * request.
 */
export interface PhotoUploaded {
  path: string;
  editId: string;
  /** The review the read goes into, or null when it fills the record itself. */
  reviewId: string | null;
  analysis: EquipmentPhotoDisposition;
  /** The tab the photo was dropped on (the hint's tab on an empty one). */
  droppedOn: PhotoReviewTab | null;
  /** The reason the operator typed, when it was long enough to keep. */
  typedReason: string | null;
}

/** One photo on the record: the file that was filed, and what can be drawn of it. */
interface DrawerPhoto {
  /** The upload itself — the evidence, and what the operator actually sent. */
  url: string;
  /** What to draw and open: the preview or converted copy when there is one, else the upload. */
  shownUrl: string;
  /** The format the copy was made from, e.g. `heic`. Null when nothing was converted. */
  convertedFrom: string | null;
  /**
   * FS-783 — every record the photo is on (absolute paths), as the server
   * reports it. Null on an API before FS-783 or for a photo just uploaded,
   * which is on this record alone.
   */
  appearsOn?: string[] | null;
  /** FS-783 — the older single-photo field the photo sits in, when it is not in the list. */
  legacyField?: string | null;
  /**
   * FS-806 — the other records that carry this same photo, as the server
   * names them. Non-empty is what offers Detach (take it off this record
   * alone); null when none does or the server does not say.
   */
  alsoOn?: string[] | null;
  /** FS-806 — why Detach is refused here right now, or null. */
  detachBlocked?: PhotoKeepReason | null;
  /** FS-1079 — the stored clockwise turn the photo is drawn with; 0 draws it as it is. */
  rotation: number;
}

/** FS-806 — the Detach facts of one photo entry, read defensively. */
const detachFacts = (entry: { also_on?: unknown; detach_blocked?: unknown } | null | undefined) => {
  const alsoOn = Array.isArray(entry?.also_on)
    ? (entry!.also_on as unknown[]).filter((v): v is string => typeof v === 'string' && v.trim() !== '')
    : [];
  const blocked = entry?.detach_blocked;
  return {
    alsoOn: alsoOn.length > 0 ? alsoOn : null,
    detachBlocked: typeof blocked === 'string' && blocked ? (blocked as PhotoKeepReason) : null,
  };
};

/**
 * The photos on one record, each with whatever can actually be drawn of it.
 * `label_photos` is the same list as `label_photo_urls` with the converted
 * copies attached; a photo this drawer has only just uploaded is in neither
 * until the record is read again, so `added` is carried alongside them.
 */
function drawerPhotosOf(doc: EquipmentDocument, added: string[]): DrawerPhoto[] {
  const entries = doc.label_photos ?? [];
  const byUrl = new Map(entries.filter((entry) => entry?.url).map((entry) => [entry.url, entry]));
  const seen = new Set<string>();
  const ordered = [...(doc.label_photo_urls ?? []), ...entries.map((entry) => entry?.url), ...added];
  const out: DrawerPhoto[] = [];
  ordered.forEach((url) => {
    if (!url || seen.has(url)) return;
    seen.add(url);
    const entry = byUrl.get(url);
    const appearsOn = Array.isArray(entry?.appears_on) && entry!.appears_on.length > 0 ? entry!.appears_on : null;
    // FS-780: `preview_url` is the server's answer to "what can be drawn",
    // and the only one on a record a photo was routed to — the read ran on
    // the record it was filed against, so this one has no analysis of it.
    // When the key is there the answer is final, null included: null means
    // draw the upload, and `preview_from` is taken as sent, never borrowed
    // from an analysis that may describe a different copy.
    if (entry && 'preview_url' in entry) {
      const preview = typeof entry.preview_url === 'string' && entry.preview_url ? entry.preview_url : null;
      const from = typeof entry.preview_from === 'string' && entry.preview_from ? entry.preview_from : null;
      out.push({
        url,
        shownUrl: preview ?? url,
        convertedFrom: preview ? from : null,
        appearsOn,
        ...detachFacts(entry),
        rotation: rotationDegrees(entry.rotation?.degrees),
      });
      return;
    }
    // An API from before FS-780 (no `preview_url` key). `analyzed_url` is
    // the upload itself when nothing was converted, so the derivative is
    // what says there is a different file to show.
    const converted = entry?.derivative ? (entry.analyzed_url ?? null) : null;
    out.push({
      url,
      shownUrl: converted ?? url,
      convertedFrom: entry?.derivative?.from ?? null,
      appearsOn,
      ...detachFacts(entry),
      rotation: rotationDegrees(entry?.rotation?.degrees),
    });
  });
  return out;
}

/**
 * FS-783 — photos in a record's older single-photo fields that are not in its
 * list. The Photos tab always drew these; the drawer did not, which left a
 * wrong legacy photo with no way off the record.
 */
function otherPhotosOf(doc: EquipmentDocument, already: DrawerPhoto[]): DrawerPhoto[] {
  const seen = new Set(already.map((photo) => photo.url));
  const out: DrawerPhoto[] = [];
  (doc.other_photo_urls ?? []).forEach((entry) => {
    if (!entry?.url || seen.has(entry.url)) return;
    seen.add(entry.url);
    out.push({
      url: entry.url,
      shownUrl: entry.url,
      convertedFrom: null,
      appearsOn: Array.isArray(entry.appears_on) && entry.appears_on.length > 0 ? entry.appears_on : null,
      legacyField: entry.field,
      ...detachFacts(entry),
      rotation: rotationDegrees(entry.rotation?.degrees),
    });
  });
  return out;
}

/**
 * One photo already on the record.
 *
 * A HEIC is a photo every browser will accept and only Safari will draw. Once
 * the label has been read there is a JPEG of it to show instead, and that is
 * also what the tile opens — clicking a HEIC only ever downloaded a file the
 * operator then had to open by hand. Until that copy exists the name of the
 * file stands in for the picture rather than a broken image.
 */
const PhotoThumb: React.FC<{ photo: DrawerPhoto; index: number; degrees: number; rotate: React.ReactNode }> = ({
  photo,
  index,
  degrees,
  rotate,
}) => {
  const [failed, setFailed] = useState(false);
  const name = photoFileName(photo.url);
  // Nothing drew, so there is nothing to show large: the file opens in a new tab.
  if (failed) {
    return (
      <a
        href={photo.shownUrl}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={`Label photo ${index + 1}`}
        title={`${name} — this browser cannot preview it. Opens in a new tab.`}
      >
        <span
          data-testid="photo-placeholder"
          className="flex h-16 w-16 flex-col items-center justify-center gap-1 rounded-sm border bg-gray-50 p-1 text-center"
        >
          <ImageOff className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
          <span className="w-full truncate text-[10px] leading-tight text-gray-600">{name}</span>
        </span>
      </a>
    );
  }
  return (
    <LabelPhotoOpener
      src={photo.shownUrl}
      alt={`Label photo ${index + 1}`}
      degrees={degrees}
      onError={() => setFailed(true)}
      boxClassName="h-16 w-16 rounded-sm border"
      fit="cover"
      label={`Show label photo ${index + 1} large`}
      heading={`Label photo ${index + 1}`}
      fileTitle={fileLinkTitle(photo.shownUrl !== photo.url)}
      rotate={rotate}
    />
  );
};

// ── FS-783: removing a photo from its records ─────────────────────────────

/**
 * What the drawer hands every photo list so a photo can be removed, and a
 * removal undone, from whichever card shows it. The calls are made by the
 * tile itself, so a refusal is said right where the operator pressed; what
 * follows a success (the in-place reload, the summary) is the drawer's.
 */
export interface PhotoRemovalCtx {
  serial: string;
  /** The record as loaded, for naming every record a photo is on. */
  record: EquipmentRecord | null;
  /** Remove may be offered here: the server's mode is on and the card's record can be written. */
  canRemove: boolean;
  /** Something else in the drawer is changing the records; Remove and Undo wait for it. */
  hold: boolean;
  /** Photos removed in this drawer session and not undone, by URL. */
  removedUrls: ReadonlySet<string>;
  /** What this session removed, by removal id: the server's answer and when. */
  sessionRemovals: Readonly<Record<string, SessionRemoval>>;
  /** Removals this session undid, by removal id, so a stale record never shows them as removed. */
  undoneRemovals: ReadonlySet<string>;
  onBeforeWrite: () => void;
  onRemoved: (result: PhotoRemovalResult, input: { reason: string; reasonKind: PhotoRemovalReasonKind | null }) => void;
  onUndone: (result: PhotoUndoResult, entry: EquipmentRemovedPhoto, already?: PhotoLastUndo | null) => void;
  onWorking: (key: string, working: boolean) => void;
  /** Load the record again in place (never a remount), after a change made outside a removal or Undo. */
  reload: () => void;
  /**
   * FS-806 — Detach may be offered here: the server's placement mode is on
   * and the card's record can be written. Each photo still needs the
   * server's `also_on` to show it.
   */
  canDetach?: boolean;
  /** FS-806 — photos taken off one record this session, as `photoKey(path, url)`. */
  detachedKeys?: { has: (key: string) => boolean };
  onDetached?: (result: PhotoDetachResult, input: { from: string; url: string }) => void;
  /**
   * FS-806 — photos an Apply moved off a record this session, as
   * `photoKey(path, url)`: the upload block drops them from what it drew
   * after its own upload, so the reload never keeps one on the record it left.
   */
  movedOff?: ReadonlySet<string>;
  /** FS-807 — where a review stands now, for the upload block's duplicate line; null when unknown. */
  reviewReadOf?: (reviewId: string) => DuplicateRead | null;
  /** FS-1079 — Rotate may be offered here: the card's record can be written. */
  canRotate?: boolean;
}

/**
 * FS-1079 — the stored turn of a photo on whichever HVAC, A/C or coil record
 * of the system carries it (every carrier stores the same turn); 0 when none.
 */
function photoRotationOn(record: EquipmentRecord | null, url: string): number {
  const docs = [record?.hvac ?? null, ...(record?.ac_systems ?? []), ...(record?.coils ?? [])];
  for (const doc of docs) {
    const entry = doc?.label_photos?.find((photo) => photo?.url === url) ??
      doc?.other_photo_urls?.find((photo) => photo?.url === url);
    if (entry?.rotation) return rotationDegrees(entry.rotation.degrees);
  }
  return 0;
}

/** FS-1079 — Rotate waits while a Remove or Detach panel is open on this card. */
const ROTATE_PANEL_OPEN_TITLE = 'Finish or cancel the open Remove or Detach panel on this record first';

/** FS-806 — one photo on one record, as a set key. */
export const photoKey = (path: string, url: string): string => `${path}\n${url}`;

/** FS-806 — Detach's hover help when the server refuses it here, by reason. */
export const DETACH_BLOCKED_TITLES: Record<string, string> = {
  source_values_cite_photo:
    'Values or open questions on this record were read from this photo, so it cannot be taken off this record alone. Remove it instead if it is wrong.',
  photo_busy:
    'A label read or Apply of this photo is still running, so it stays on this record for now. Try again in a few minutes.',
  source_evidence_unresolved:
    "Some values on this record were read from this photo earlier and can't be traced to another copy, so it stays here.",
};

/** FS-807 — the upload matched a photo already on the record. */
export const DUPLICATE_READING = 'Same photo as one already on this record; reading its values again…';
export const DUPLICATE_REREAD = 'Same photo as one already on this record; its values were re-read.';
export const DUPLICATE_FILTER = 'Same photo as one already on this record; nothing was added.';
/** Addendum 2 F10 — a duplicate whose read did not succeed is never called re-read. */
export const DUPLICATE_READ_FAILED = "Same photo as one already on this record; its label couldn't be read again.";
export const DUPLICATE_READ_STOPPED = 'Same photo as one already on this record; the read stopped before it finished.';

/** How a label read ended: read, failed, or closed before it was read. */
export type DuplicateRead = 'ok' | 'failed' | 'stopped';

/**
 * Addendum 2 F10 — how a review's read stage ended, from its status view, or
 * null while it is still reading. The read stage decides when it says; a
 * view without one falls back on the status, where a failed APPLY (the read
 * had succeeded) is told apart from a failed read.
 */
export function reviewReadOutcome(view: PhotoReview): DuplicateRead | null {
  if (view.status === 'reading') return null;
  const read = (view.stages?.read as { status?: unknown } | null | undefined)?.status;
  if (read === 'ok') return 'ok';
  if (read === 'failed' || read === 'error') return 'failed';
  switch (view.status) {
    case 'failed':
      return view.last_apply?.result === 'failed' ? 'ok' : 'failed';
    case 'superseded':
      return 'stopped';
    default:
      return 'ok';
  }
}

/** The same for a request read into the record itself (no review), once processed. */
export function requestReadOutcome(row: { error?: string | null; outcome?: string | null; rejected?: string | null }): DuplicateRead {
  if (row.outcome === 'removed') return 'stopped';
  if (row.error || row.rejected || row.outcome === 'error') return 'failed';
  return 'ok';
}

const DUPLICATE_WORDS: Record<DuplicateRead, string> = {
  ok: DUPLICATE_REREAD,
  failed: DUPLICATE_READ_FAILED,
  stopped: DUPLICATE_READ_STOPPED,
};

/** How long the tile of an identical photo stays ringed after a duplicate upload. */
const DUPLICATE_RING_MS = 15000;

/** One removal this drawer session made. */
export interface SessionRemoval {
  result: PhotoRemovalResult;
  reason: string;
  reasonKind: PhotoRemovalReasonKind | null;
  /** The browser's time of the answer; only shown when the record has not been reloaded yet. */
  at: string;
}

/** The optional chips: each names the kind and offers a reason when the box is empty. */
const REMOVAL_REASONS: Array<{ kind: PhotoRemovalReasonKind; label: string; text: string; title: string }> = [
  {
    kind: 'duplicate',
    label: 'Duplicate',
    text: 'Duplicate photo',
    title: 'This photo is a copy of another photo of the same label',
  },
  {
    kind: 'wrong_photo',
    label: 'Wrong photo',
    text: 'Wrong photo for this record',
    title: 'This photo is of different equipment, or of another home',
  },
  {
    kind: 'unreadable',
    label: "Can't be read",
    text: "Label can't be read",
    title: 'Nothing on the label can be made out in this photo',
  },
];

/** Every record a photo is on, named, in the order the server listed them. */
const familyWords = (record: EquipmentRecord | null, family: string[]): string =>
  family.map((path) => recordWords(record, path)).join(', ');

/**
 * FS-806 — the records a photo also sits on, named. The server may send
 * paths (named here as every other record is) or names already in words
 * (used as sent).
 */
const alsoOnWords = (record: EquipmentRecord | null, alsoOn: string[]): string =>
  alsoOn.map((entry) => (entry.includes('/') ? recordWords(record, entry) : entry)).join(', ');

/**
 * The inline panel under a photo that is about to be removed. No confirm
 * dialog: the panel IS the second step — it names every record the photo
 * comes off, says what stays, and takes the reason the audit keeps.
 */
const RemovePhotoPanel: React.FC<{
  ctx: PhotoRemovalCtx;
  kind: EquipmentTargetKind;
  doc: EquipmentDocument;
  photo: DrawerPhoto;
  family: string[];
  prefix: string;
  onRemoved: (url: string) => void;
  onCancel: () => void;
}> = ({ ctx, kind, doc, photo, family, prefix, onRemoved, onCancel }) => {
  const [reason, setReason] = useState('');
  const [reasonKind, setReasonKind] = useState<PhotoRemovalReasonKind | null>(null);
  const [reasonError, setReasonError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  /**
   * One id per attempt. A request that got no answer may have been applied,
   * so pressing again sends the same id and the server answers with what it
   * did; a request the server refused is over, and the next one is new.
   */
  const editId = useRef(newEditId());
  const workKey = `${doc.path}#remove`;
  const disabled = busy || ctx.hold;

  const submit = async () => {
    setError(null);
    setReasonError(null);
    const typed = reason.trim();
    if (typed.length < MIN_REASON_LENGTH) {
      setReasonError(EQUIPMENT_EDIT_ERRORS.reason_required);
      return;
    }
    setBusy(true);
    ctx.onWorking(workKey, true);
    ctx.onBeforeWrite();
    try {
      const result = await removePhoto(ctx.serial, {
        edit_id: editId.current,
        target: { kind, path: doc.path },
        url: photo.url,
        reason: typed,
        ...(reasonKind ? { reason_kind: reasonKind } : {}),
      });
      onRemoved(photo.url);
      ctx.onRemoved(result, { reason: typed, reasonKind });
    } catch (err) {
      if (serverAnswered(err)) editId.current = newEditId();
      setError(photoRemovalErrorMessage(err));
    } finally {
      setBusy(false);
      ctx.onWorking(workKey, false);
    }
  };

  return (
    <div
      data-testid="remove-photo-panel"
      className="rounded-md border border-status-warning-border bg-amber-50 p-3 text-sm text-amber-900"
    >
      <p>{`Removes this photo from: ${familyWords(ctx.record, family)}.`}</p>
      <p className="mt-1 text-xs">
        Values read from it stay on the records, marked &apos;evidence removed&apos;. The file is kept for 90 days.
      </p>
      <ToggleGroup
        value={reasonKind ? [reasonKind] : []}
        onValueChange={(values) => {
          const selected = REMOVAL_REASONS.find((chip) => chip.kind === values[0]);
          if (!selected) return;
          setReasonKind(selected.kind);
          setReason((prev) => (prev.trim() === '' ? selected.text : prev));
        }}
        disabled={disabled}
        variant="outline"
        size="sm"
        className="mt-2 flex-wrap"
        aria-label="Why this photo is being removed"
      >
        {REMOVAL_REASONS.map((chip) => (
          <ToggleGroupItem
            key={chip.kind}
            value={chip.kind}
            title={chip.title}
            className="rounded-full text-xs"
          >
            {chip.label}
          </ToggleGroupItem>
        ))}
      </ToggleGroup>
      <ReasonField
        prefix={`${prefix}-remove`}
        value={reason}
        error={reasonError}
        disabled={disabled}
        onChange={setReason}
        title="Why this photo is coming off the records, at least 8 characters; kept in the audit record with the removal"
      />
      {error && (
        <p className="mt-2 rounded-md border border-status-error-border bg-red-50 p-2 text-sm text-red-800" role="alert">
          {error}
        </p>
      )}
      <div className="mt-3 flex gap-2">
        <Button
          variant="danger"
          disabled={disabled}
          onClick={() => void submit()}
          title={ctx.hold && !busy ? PHOTO_WAIT_TOOLTIP : 'Take the photo off these records now; you can undo for 90 days'}
        >
          {busy ? 'Removing…' : 'Remove photo'}
        </Button>
        <Button
          variant="secondary"
          disabled={busy}
          onClick={onCancel}
          title="Keep the photo"
        >
          Cancel
        </Button>
      </div>
    </div>
  );
};

/**
 * Addendum 2 F10 — the file's protection in words, object by object: the
 * original upload and its converted copy are said apart, and a file that was
 * already gone (`missing`) is never described as kept. `retry` is true when
 * pressing Retry protection could change something — never for a missing
 * file. Null when the server sent no per-object status (an older API).
 */
function storageWords(
  storage: PhotoStorageState | null | undefined,
  mode: 'protect' | 'release',
): { lines: string[]; retry: boolean; settled: boolean } | null {
  if (!storage || !Array.isArray(storage.objects) || storage.objects.length === 0) return null;
  const lines: string[] = [];
  let retry = false;
  storage.objects.forEach((object) => {
    const what =
      object.role === 'preview'
        ? 'The converted copy'
        : object.role === 'late_copy'
          ? 'The converted copy made after the removal'
          : 'The original file';
    const why = object.error ? ` (${object.error})` : '';
    let line: string | null = null;
    if (object.status === 'missing') {
      line = `${what} was already gone${mode === 'protect' ? ', so there was nothing to protect' : ''}.`;
    } else if (mode === 'protect' && (object.status === 'failed' || object.status === 'release_failed')) {
      line = `${what} couldn't be protected from deletion${why}. It has not been deleted.`;
      retry = true;
    } else if (mode === 'release' && (object.status === 'release_failed' || object.status === 'failed')) {
      line = `${what} is still protected from deletion${why}; that does no harm, and Retry protection releases it.`;
      retry = true;
    } else if (object.status === 'pending') {
      line = `${mode === 'protect' ? 'Protecting' : 'Releasing the protection on'} ${what.charAt(0).toLowerCase()}${what.slice(1)} has not finished.`;
      retry = true;
    }
    if (line && !lines.includes(line)) lines.push(line);
  });
  return { lines, retry, settled: lines.length === 0 };
}

/**
 * FS-806 — the inline panel under a photo that is about to be taken off this
 * record alone. No confirm dialog, as with Remove: the panel says where the
 * photo stays and takes the reason the audit keeps. There is no Undo (it is a
 * move, and the photo is still on the other record); a re-drop puts it back.
 */
const DetachPhotoPanel: React.FC<{
  ctx: PhotoRemovalCtx;
  kind: EquipmentTargetKind;
  doc: EquipmentDocument;
  photo: DrawerPhoto;
  stays: string;
  prefix: string;
  onDetached: (url: string) => void;
  onCancel: () => void;
}> = ({ ctx, kind, doc, photo, stays, prefix, onDetached, onCancel }) => {
  const [reason, setReason] = useState('');
  const [reasonError, setReasonError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  /** One id per attempt, reused only when a request got no answer (as Remove does). */
  const editId = useRef(newEditId());
  const workKey = `${doc.path}#detach`;
  const disabled = busy || ctx.hold;

  const submit = async () => {
    setError(null);
    setReasonError(null);
    const typed = reason.trim();
    if (typed.length < MIN_REASON_LENGTH) {
      setReasonError(EQUIPMENT_EDIT_ERRORS.reason_required);
      return;
    }
    setBusy(true);
    ctx.onWorking(workKey, true);
    ctx.onBeforeWrite();
    try {
      const result = await detachPhoto(ctx.serial, {
        edit_id: editId.current,
        url: photo.url,
        from_target: { kind, path: doc.path },
        reason: typed,
      });
      onDetached(photo.url);
      ctx.onDetached?.(result, { from: doc.path, url: photo.url });
    } catch (err) {
      if (serverAnswered(err)) editId.current = newEditId();
      // A refusal by the keep rule names the record the photo stayed on.
      const stayedOn = equipmentErrorDetails(err)?.stayed_on;
      const kept = ['source_values_cite_photo', 'photo_busy', 'source_evidence_unresolved'].includes(equipmentErrorCode(err) ?? '');
      setError(
        kept && typeof stayedOn === 'string' && stayedOn
          ? `The photo stayed on ${recordWords(ctx.record, stayedOn)}. ${photoRemovalErrorMessage(err)}`
          : photoRemovalErrorMessage(err),
      );
    } finally {
      setBusy(false);
      ctx.onWorking(workKey, false);
    }
  };

  return (
    <div data-testid="detach-photo-panel" className="rounded-md border border-status-info-border bg-blue-50 p-3 text-sm text-blue-900">
      {/* Addendum 2 F11, verbatim. */}
      <p>{`Takes the photo off this record. It stays on ${stays}. Saved values do not change.`}</p>
      <ReasonField
        prefix={`${prefix}-detach`}
        value={reason}
        error={reasonError}
        disabled={disabled}
        onChange={setReason}
        title="Kept with the change: say why this photo does not belong on this record (8 characters or more)"
      />
      {error && (
        <p className="mt-2 rounded-md border border-status-error-border bg-red-50 p-2 text-sm text-red-800" role="alert">
          {error}
        </p>
      )}
      <div className="mt-3 flex gap-2">
        <Button
          variant="primary"
          disabled={disabled}
          onClick={() => void submit()}
          title={ctx.hold && !busy ? PHOTO_WAIT_TOOLTIP : `Take the photo off this record now; it stays on ${stays}`}
        >
          {busy ? 'Taking it off…' : 'Take off this record'}
        </Button>
        <Button
          variant="secondary"
          disabled={busy}
          onClick={onCancel}
          title="Leave the photo on this record"
        >
          Cancel
        </Button>
      </div>
    </div>
  );
};

/**
 * A photo that was removed from this record: greyed, with who removed it and
 * why, and Undo while its window is open. Undo works whatever the server's
 * removal mode is, so turning removals off never strands one.
 */
const RemovedPhotoTile: React.FC<{
  ctx: PhotoRemovalCtx;
  entry: EquipmentRemovedPhoto;
  session: SessionRemoval | null;
}> = ({ ctx, entry, session }) => {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  const shown = entry.preview_url || entry.url;
  /**
   * Addendum 3 N8 — what the tile may promise about the file follows the
   * file's own status: an original that was already gone is never said to be
   * kept, and the link says whether the file it opens is still there.
   */
  const objects = (entry.storage?.objects ?? []) as PhotoStorageState['objects'];
  const originalGone = objects.some((o) => o.role === 'original' && o.status === 'missing');
  const shownIsPreview = Boolean(entry.preview_url) && entry.preview_url !== entry.url;
  const shownGone = shownIsPreview
    ? objects.some((o) => o.role === 'preview' && o.status === 'missing')
    : originalGone;
  const linkTitle = shownGone
    ? 'Open the removed photo in a new tab; this file was already gone when the photo was removed, so it may not open'
    : originalGone
      ? 'Open the converted copy of the removed photo in a new tab; the original file was already gone'
      : 'Open the removed photo in a new tab; the file is kept until its retention date';
  const workKey = `${entry.removal_id}#undo`;
  const records = session?.result.records.length ?? 0;
  const reason = entry.reason ?? 'no reason recorded';
  /**
   * Addendum 2 F17 — one id per Undo attempt. An Undo that got no answer may
   * have gone through, so pressing again sends the SAME id and the server
   * replays what it did; one the server answered is over, and the next
   * press is a new attempt.
   */
  const undoId = useRef(newEditId());
  /** The protection as the last Retry here reported it, over what the record said. */
  const [storageNow, setStorageNow] = useState<PhotoStorageState | null>(null);
  const [retryBusy, setRetryBusy] = useState(false);
  const [retryError, setRetryError] = useState<string | null>(null);
  const storage = storageWords(storageNow ?? entry.storage ?? null, 'protect');

  const undo = async () => {
    setError(null);
    setBusy(true);
    ctx.onWorking(workKey, true);
    ctx.onBeforeWrite();
    try {
      const result = await undoPhotoRemoval(ctx.serial, entry.removal_id, { edit_id: undoId.current, url: entry.url });
      undoId.current = newEditId();
      ctx.onUndone(result, entry);
    } catch (err) {
      if (equipmentErrorCode(err) === 'already_restored') {
        // The photo is already back — most likely this very Undo, whose answer
        // was lost. Show what that Undo did when the server sends it, and load
        // the record as it is now either way.
        const last = (equipmentErrorDetails(err)?.last_undo ?? null) as PhotoLastUndo | null;
        undoId.current = newEditId();
        if (last?.response) ctx.onUndone(last.response, entry, last);
        else ctx.reload();
        return;
      }
      if (serverAnswered(err)) undoId.current = newEditId();
      setError(`Couldn't undo the removal: ${photoRemovalErrorMessage(err)}`);
    } finally {
      setBusy(false);
      ctx.onWorking(workKey, false);
    }
  };

  /** §4.3 — set a protection that failed or never finished. */
  const retryProtection = async () => {
    setRetryError(null);
    setRetryBusy(true);
    try {
      setStorageNow(await retryPhotoProtection(ctx.serial, entry.removal_id, { url: entry.url }));
      ctx.reload();
    } catch (err) {
      setRetryError(`Couldn't retry the protection: ${photoRemovalErrorMessage(err)}`);
    } finally {
      setRetryBusy(false);
    }
  };

  return (
    <li data-testid="removed-photo" data-removal-id={entry.removal_id} className="flex w-full items-start gap-2 rounded-sm border border-border bg-gray-50 p-2">
      {failed ? (
        // Nothing drew, so there is nothing to show large: the file opens in a new tab.
        <a href={shown} target="_blank" rel="noopener noreferrer" title={linkTitle} className="shrink-0">
          <span className="flex h-12 w-12 items-center justify-center rounded-sm border bg-gray-100">
            <ImageOff className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
          </span>
        </a>
      ) : (
        // FS-1079: drawn with the turn it had when it was removed, faded here
        // and at full strength large, where the label is there to be read.
        <LabelPhotoOpener
          src={shown}
          alt="Removed photo"
          degrees={rotationDegrees(entry.rotation?.degrees)}
          onError={() => setFailed(true)}
          boxClassName="h-12 w-12 rounded-sm border"
          fit="cover"
          imageClassName="opacity-50 grayscale"
          label="Show the removed photo large"
          heading="Removed photo"
          fileTitle={`${linkTitle}. It opens as stored, not turned.`}
          className="shrink-0"
        />
      )}
      <div className="min-w-0 text-xs text-gray-600">
        {session ? (
          <p className="font-medium text-gray-700">
            {`Removed from ${records} ${records === 1 ? 'record' : 'records'}: ${reason}`}
          </p>
        ) : null}
        <p>
          {session
            ? `Removed ${dayText(entry.removed_at)} by ${removerText(entry)}`
            : `Removed ${dayText(entry.removed_at)} by ${removerText(entry)}: ${reason}`}
        </p>
        {entry.undo_available ? (
          <Button
            variant="secondary"
            size="sm"
            disabled={busy || ctx.hold}
            onClick={() => void undo()}
            title={
              ctx.hold && !busy
                ? PHOTO_WAIT_TOOLTIP
                : `Put the photo back on these records exactly as it was; available until ${dayText(entry.undo_until)}`
            }
            className="mt-1"
          >
            {busy ? 'Putting it back…' : 'Undo'}
          </Button>
        ) : (
          <p className="mt-1 text-muted-foreground" data-testid="removed-photo-expired">
            {originalGone
              ? 'Undo has expired; the original file was already gone when the photo was removed, so nothing is kept'
              : `Undo has expired; the file is kept until ${dayText(entry.retain_until)}`}
          </p>
        )}
        {/* Addendum 2 F9 — read from the record on every load, so a hold that
            failed is still here, with Retry, after the drawer is reopened. */}
        {storage && storage.lines.length > 0 && (
          <div data-testid="removed-photo-storage" className="mt-1 text-amber-900">
            {storage.lines.map((line) => (
              <p key={line}>{line}</p>
            ))}
            {storage.retry && (
              <Button
                variant="secondary"
                size="sm"
                disabled={retryBusy}
                onClick={() => void retryProtection()}
                title="Try again to protect the file from deletion; nothing on the records changes"
                className="mt-1"
              >
                {retryBusy ? 'Trying…' : 'Retry protection'}
              </Button>
            )}
          </div>
        )}
        {storageNow && storage?.settled && <p className="mt-1 text-green-800">The file is now protected from deletion.</p>}
        {retryError && (
          <p className="mt-1 text-red-700" role="alert">
            {retryError}
          </p>
        )}
        {typeof entry.reapplied === 'number' && entry.reapplied > 0 && (
          <p className="mt-1 text-amber-900" data-testid="removed-photo-reapplied">
            {`The homeowner's app put this photo back ${entry.reapplied === 1 ? 'once' : `${entry.reapplied} times`}; it was taken off again${entry.reapplied === 1 ? '' : ' each time'}.`}
          </p>
        )}
        {entry.resync?.failed && (
          <p className="mt-1 text-muted-foreground">The device summary will catch up overnight.</p>
        )}
        {error && (
          <p className="mt-1 text-red-700" role="alert">
            {error}
          </p>
        )}
      </div>
    </li>
  );
};

/**
 * Addendum 2 F9 — one notice the server keeps on a record about a removal:
 * a photo put back whose file protection could not be released (Retry), or
 * a removed photo the homeowner's app wrote back and the server took off
 * again. Both stay until dismissed, so closing the drawer never loses them.
 */
const RemovalNoticeRow: React.FC<{ ctx: PhotoRemovalCtx; notice: PhotoRemovalNotice }> = ({ ctx, notice }) => {
  const [busy, setBusy] = useState<'retry' | 'dismiss' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [storageNow, setStorageNow] = useState<PhotoStorageState | null>(null);
  const isRelease = notice.kind === 'release' || notice.kind === 'release_failed';
  const release = isRelease ? storageWords(storageNow ?? notice.storage ?? null, 'release') : null;

  const act = async (what: 'retry' | 'dismiss') => {
    setError(null);
    setBusy(what);
    try {
      if (!notice.removal_id || !notice.url) throw new Error('the notice names no removal');
      if (what === 'retry') {
        setStorageNow(await retryPhotoProtection(ctx.serial, notice.removal_id, { url: notice.url }));
      } else {
        await dismissPhotoRemovalNotice(ctx.serial, notice.removal_id, notice.id);
      }
      ctx.reload();
    } catch (err) {
      setError(`${what === 'retry' ? "Couldn't retry the protection" : "Couldn't dismiss the notice"}: ${photoRemovalErrorMessage(err)}`);
    } finally {
      setBusy(null);
    }
  };

  const text =
    !isRelease
      ? `The homeowner's app put a removed photo back on this record${
          typeof notice.count === 'number' && notice.count > 1 ? ` ${notice.count} times` : ''
        }; it was taken off again (${dayText(notice.at)}).`
      : release && release.lines.length > 0
        ? `A photo put back on ${dayText(notice.at)}: ${release.lines.join(' ')}`
        : release?.settled && storageNow
          ? `A photo put back on ${dayText(notice.at)}: its file is no longer protected from deletion.`
          : `A photo put back on ${dayText(notice.at)} is still protected from deletion; that does no harm, and Retry protection releases it.`;

  return (
    <li
      data-testid="photo-removal-notice"
      data-kind={isRelease ? 'release' : 'readded'}
      className="rounded-sm border border-status-warning-border bg-amber-50 p-2 text-xs text-amber-900"
    >
      <p>{text}</p>
      <div className="mt-1 flex gap-2">
        {isRelease && !(release?.settled && storageNow) && notice.removal_id && notice.url && (
          <Button
            variant="secondary"
            size="sm"
            disabled={busy !== null}
            onClick={() => void act('retry')}
            title="Try again to release the file's protection; nothing on the records changes"
          >
            {busy === 'retry' ? 'Trying…' : 'Retry protection'}
          </Button>
        )}
        <Button
          variant="secondary"
          size="sm"
          disabled={busy !== null}
          onClick={() => void act('dismiss')}
          title="Hide this notice for good; nothing on the records changes"
        >
          {busy === 'dismiss' ? 'Dismissing…' : 'Dismiss'}
        </Button>
      </div>
      {error && (
        <p className="mt-1 text-red-700" role="alert">
          {error}
        </p>
      )}
    </li>
  );
};

/**
 * The removed photos to show on one record: what the record says, plus what
 * this session removed and the record has not caught up with yet (a reload
 * that failed), minus what this session already undid.
 */
function removedPhotosFor(doc: EquipmentDocument, ctx: PhotoRemovalCtx | null): EquipmentRemovedPhoto[] {
  const listed = (doc.removed_photos ?? []).filter((entry) => !ctx?.undoneRemovals.has(entry.removal_id));
  if (!ctx) return listed;
  const known = new Set(listed.map((entry) => entry.removal_id));
  const fromSession: EquipmentRemovedPhoto[] = Object.values(ctx.sessionRemovals)
    .filter(
      (s) =>
        !known.has(s.result.removal_id) &&
        !ctx.undoneRemovals.has(s.result.removal_id) &&
        s.result.records.some((r) => r.path === doc.path),
    )
    .map((s) => ({
      removal_id: s.result.removal_id,
      url: s.result.url,
      preview_url: null,
      reason: s.reason,
      reason_kind: s.reasonKind,
      removed_at: s.at,
      removed_by: 'you',
      undo_until: s.result.undo_until,
      retain_until: s.result.retain_until,
      undo_available: true,
    }));
  return [...fromSession, ...listed];
}

/**
 * The photo tiles on one record: the live photos (with Remove when it is
 * offered), any in the older single-photo fields, and the removed ones. Used
 * by the upload block and, read-only, by cards that take no upload (a coil,
 * a filter slot, an older filter record of this Clip).
 */
const PhotoList: React.FC<{
  kind: EquipmentTargetKind;
  doc: EquipmentDocument;
  prefix: string;
  photos: DrawerPhoto[];
  removal: PhotoRemovalCtx | null;
  /** Called with a photo's URL once the server has removed it. */
  onRemovedUrl?: (url: string) => void;
  /** What to say when there is no live photo. Null says nothing. */
  emptyText?: string | null;
  /** FS-807 — the tile to ring: the photo an identical upload matched. */
  ringUrl?: string | null;
  /**
   * FS-1078 — Read again on each photo: why a photo cannot be read again
   * now (null when it can), and the action. Absent where nothing is read.
   */
  readAgain?: { blockedFor: (url: string) => string | null; start: (url: string) => void } | null;
}> = ({ kind, doc, prefix, photos, removal, onRemovedUrl, emptyText = 'No label photo on this record yet.', ringUrl = null, readAgain = null }) => {
  const [removing, setRemoving] = useState<string | null>(null);
  /** FS-806 — the photo whose Detach panel is open. One panel at a time, Remove or Detach. */
  const [detaching, setDetaching] = useState<string | null>(null);
  /**
   * FS-1079 — turns saved in this list, by photo, with the stored turn they
   * replaced: shown at once, until the reloaded record brings a new value.
   */
  const [turned, setTurned] = useState<Record<string, { degrees: number; over: number }>>({});
  const turnOf = (photo: DrawerPhoto) => {
    const saved = turned[photo.url];
    return saved && saved.over === photo.rotation ? saved.degrees : photo.rotation;
  };
  const gone = (photo: DrawerPhoto) =>
    Boolean(removal?.removedUrls.has(photo.url) || removal?.detachedKeys?.has(photoKey(doc.path, photo.url)));
  const live = photos.filter((photo) => !gone(photo));
  const tiles = [...live, ...otherPhotosOf(doc, photos).filter((photo) => !gone(photo))];
  const removed = removedPhotosFor(doc, removal);
  const canRemove = Boolean(removal?.canRemove);
  // The server never detaches from a filter (400 invalid_field) and sends a
  // filter no `also_on`; the console holds to that even if one arrived.
  const canDetach = Boolean(removal?.canDetach) && kind !== 'filter';
  // FS-1079: a filter photo is not a label photo and has no stored turn.
  const canRotate = Boolean(removal?.canRotate) && kind !== 'filter';
  const panelOpen = removing !== null || detaching !== null;

  return (
    <>
      {tiles.length > 0 ? (
        <ul className="mt-2 flex flex-wrap gap-2">
          {tiles.map((photo, index) => {
            const family = photo.appearsOn ?? [doc.path];
            const ringed = ringUrl !== null && photo.url === ringUrl;
            // The server lists the carriers in the order Detach picks from: the
            // first is the record the photo stays on.
            const stays = photo.alsoOn ? alsoOnWords(removal?.record ?? null, photo.alsoOn.slice(0, 1)) : '';
            // One Rotate, offered beside the tile and in the large view it opens.
            const rotate = canRotate && removal ? (
              <RotatePhotoButton
                serial={removal.serial}
                url={photo.url}
                target={{ kind, path: doc.path }}
                degrees={turnOf(photo)}
                index={index}
                blockedTitle={removal.hold ? PHOTO_WAIT_TOOLTIP : panelOpen ? ROTATE_PANEL_OPEN_TITLE : null}
                onWorking={(working) => removal.onWorking(`rotate:${doc.path}:${photo.url}`, working)}
                onRotated={(degrees) => {
                  setTurned((held) => ({ ...held, [photo.url]: { degrees, over: photo.rotation } }));
                  // The turn is stored on every record that carries the photo.
                  removal.reload();
                }}
              />
            ) : null;
            return (
              <React.Fragment key={photo.url}>
                {/* As wide as the widest of the thumbnail and its buttons, so the buttons stay inside the tile. */}
                <li
                  className={`w-min ${ringed ? 'rounded-sm ring-2 ring-status-info-border ring-offset-2' : ''}`}
                  data-testid="photo-tile"
                  data-photo-url={photo.url}
                  data-ringed={ringed ? 'true' : undefined}
                  title={ringed ? 'The photo just uploaded is the same as this one' : undefined}
                >
                  <PhotoThumb photo={photo} index={index} degrees={turnOf(photo)} rotate={rotate} />
                  {/* Whenever the tile draws a copy, the upload itself stays one click away. */}
                  {photo.shownUrl !== photo.url && (
                    <p className="mt-0.5 text-[10px] leading-tight text-muted-foreground">
                      <span>
                        {photo.convertedFrom ? `${photo.convertedFrom.toUpperCase()} · converted` : 'converted'}
                      </span>{' '}
                      <a
                        href={photo.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="underline"
                        title="Open the photo as it was uploaded"
                      >
                        original
                      </a>
                    </p>
                  )}
                  {photo.legacyField && (
                    <p
                      className="mt-0.5 text-[10px] leading-tight text-muted-foreground"
                      title={`This photo is kept in the record's older ${photo.legacyField} field, not in its photo list`}
                    >
                      older field
                    </p>
                  )}
                  {readAgain && (() => {
                    const blocked = readAgain.blockedFor(photo.url);
                    return (
                      <Button
                        variant="secondary"
                        size="sm"
                        disabled={blocked !== null || panelOpen}
                        onClick={() => readAgain.start(photo.url)}
                        aria-label={`Read label photo ${index + 1} again`}
                        data-testid="read-again-photo"
                        title={blocked ?? READ_AGAIN_TITLE}
                        className="mt-0.5 block"
                      >
                        Read again
                      </Button>
                    );
                  })()}
                  {rotate}
                  {canRemove && removal && (
                    <Button
                      variant="danger"
                      size="sm"
                      disabled={removal.hold || panelOpen}
                      onClick={() => setRemoving(photo.url)}
                      aria-label={`Remove label photo ${index + 1}`}
                      title={
                        removal.hold
                          ? PHOTO_WAIT_TOOLTIP
                          : `Remove this photo from ${familyWords(removal.record, family)}; values read from it stay, marked`
                      }
                      className="mt-0.5"
                    >
                      Remove
                    </Button>
                  )}
                  {/* FS-806: only where the server says another record carries
                      this photo too. Refused here (values read from it, or a
                      read running) it is still drawn, disabled, saying why. */}
                  {canDetach && removal && photo.alsoOn && (
                    <Button
                      variant="link"
                      size="sm"
                      disabled={removal.hold || panelOpen || photo.detachBlocked != null}
                      onClick={() => setDetaching(photo.url)}
                      aria-label={`Take label photo ${index + 1} off this record (it stays on ${stays})`}
                      data-testid="detach-photo"
                      title={
                        photo.detachBlocked
                          ? DETACH_BLOCKED_TITLES[photo.detachBlocked] ?? `This photo cannot be taken off this record alone right now (${photo.detachBlocked}).`
                          : removal.hold
                            ? PHOTO_WAIT_TOOLTIP
                            : `Take off this record (it stays on ${stays})`
                      }
                      className="mt-0.5 block"
                    >
                      Detach
                    </Button>
                  )}
                </li>
                {removing === photo.url && removal && (
                  <li className="basis-full">
                    <RemovePhotoPanel
                      ctx={removal}
                      kind={kind}
                      doc={doc}
                      photo={photo}
                      family={family}
                      prefix={`${prefix}-${index}`}
                      onRemoved={(url) => {
                        setRemoving(null);
                        onRemovedUrl?.(url);
                      }}
                      onCancel={() => setRemoving(null)}
                    />
                  </li>
                )}
                {detaching === photo.url && removal && (
                  <li className="basis-full">
                    <DetachPhotoPanel
                      ctx={removal}
                      kind={kind}
                      doc={doc}
                      photo={photo}
                      stays={stays}
                      prefix={`${prefix}-${index}`}
                      onDetached={(url) => {
                        setDetaching(null);
                        onRemovedUrl?.(url);
                      }}
                      onCancel={() => setDetaching(null)}
                    />
                  </li>
                )}
              </React.Fragment>
            );
          })}
        </ul>
      ) : (
        emptyText && <p className="mt-1 text-xs text-muted-foreground">{emptyText}</p>
      )}
      {(doc.photo_removal_notices ?? []).length > 0 && removal && (
        <ul className="mt-2 space-y-1" aria-label="Notices about removed photos">
          {(doc.photo_removal_notices ?? []).map((notice) => (
            <RemovalNoticeRow key={notice.id} ctx={removal} notice={notice} />
          ))}
        </ul>
      )}
      {removed.length > 0 && removal && (
        <ul className="mt-2 space-y-1" aria-label="Removed photos">
          {removed.map((entry) => (
            <RemovedPhotoTile
              key={entry.removal_id}
              ctx={removal}
              entry={entry}
              session={removal.sessionRemovals[entry.removal_id] ?? null}
            />
          ))}
        </ul>
      )}
    </>
  );
};

/** A field the read wrote, named the way the card names it. */
const readFieldLabel = (kind: EquipmentTargetKind, name: string): string => {
  const spec = specFor(kind, name);
  if (spec) return spec.label;
  // A field the console has no box for — the read fills several of those.
  const spaced = name.replace(/([a-z0-9])([A-Z])/g, '$1 $2').toLowerCase();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
};

/**
 * The label photos on one record, and the way to add another.
 *
 * A photo is evidence for a correction, so it carries the same reason a save
 * does and is refusable for the same reasons. On equipment it is also an
 * *input*: the server reads the label off it and fills fields from what it
 * finds, which changes the record under the operator while they are looking at
 * it. So the block follows that read to its end and says how it came out,
 * rather than leaving a photo on screen and a record that quietly changed.
 *
 * Nothing here touches the form above it. An upload never clears a half-typed
 * correction, and the reloads the read causes re-render the values on file
 * while leaving every box the operator typed in alone.
 */
const PhotoBlock: React.FC<{
  serial: string;
  kind: EquipmentPhotoTargetKind;
  doc: EquipmentDocument;
  prefix: string;
  reason: string;
  disabled: boolean;
  /** Set while this drawer is following the read this block started. */
  readState: PhotoReadState;
  onUploaded: (uploaded: PhotoUploaded) => void;
  /** Raised while a photo is on its way up, so the drawer can hold its writes. */
  onWorking: (working: boolean) => void;
  /** FS-784 — the server reads operator photos into a review. */
  reviewMode?: boolean;
  /** FS-781 — sent with the upload from an empty Outdoor or Coil tab. */
  componentHint?: PhotoComponentHint;
  /** False on an empty tab, whose photo is kept on another record. */
  showList?: boolean;
  /** FS-783 — Remove and Undo on the photos, when the drawer offers them. */
  removal?: PhotoRemovalCtx | null;
}> = ({
  serial,
  kind,
  doc,
  prefix,
  reason,
  disabled,
  readState,
  onUploaded,
  onWorking,
  reviewMode = false,
  componentHint,
  showList = true,
  removal,
}) => {
  const id = `${prefix}-photo`;
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [stored, setStored] = useState(false);
  const [added, setAdded] = useState<string[]>([]);
  /** True while a drag is over the zone, so the operator can see where it lands. */
  const [dragging, setDragging] = useState(false);
  /** Something worth saying about the selection that is not a refusal. */
  const [notice, setNotice] = useState<string | null>(null);
  /**
   * The file in hand. The input's own value is cleared the moment it is read,
   * so the browser goes back to saying "No file chosen" over a file that is
   * already on its way up — this is what the operator reads instead.
   */
  const [selected, setSelected] = useState<{ name: string; size: number } | null>(null);
  /** How far the file has got. Every one of these is on screen while it holds. */
  const [phase, setPhase] = useState<PhotoPhase>('idle');
  /** The checks passed, so a failure after this one is the send, not the file. */
  const [checked, setChecked] = useState(false);
  /**
   * The values on file at the moment the photo went up. What the read changed
   * is the difference between this and the record that comes back — the
   * server reports that the read finished, not what it touched.
   */
  const [snapshot, setSnapshot] = useState<Record<string, unknown> | null>(null);
  /** The edit id of this block's last upload: the request it follows. */
  const [myEditId, setMyEditId] = useState<string | null>(null);
  /** The review the last upload went into, when it went into one. */
  const [myReview, setMyReview] = useState<string | null>(null);
  /**
   * FS-807 — the photo already on this record that the last upload matched
   * (nothing new was stored), or null. Its tile is ringed for a while.
   */
  const [duplicateOf, setDuplicateOf] = useState<string | null>(null);
  const [ringUrl, setRingUrl] = useState<string | null>(null);
  const ringTimer = useRef<number | undefined>(undefined);
  useEffect(() => () => window.clearTimeout(ringTimer.current), []);
  /**
   * FS-807 / addendum 2 F10 — how the duplicate's read ended, from the
   * drawer's record of its review (kept after the review itself is gone).
   * Null while it is still reading.
   */
  const duplicateReview = duplicateOf && myReview && removal?.reviewReadOf ? removal.reviewReadOf(myReview) : null;

  /**
   * FS-806 — a photo an Apply moved off this record leaves what this block
   * drew after its own upload too, so the in-place reload does not keep it.
   */
  const movedOff = removal?.movedOff;
  useEffect(() => {
    if (!movedOff || movedOff.size === 0) return;
    setAdded((prev) => {
      const next = prev.filter((url) => !movedOff.has(photoKey(doc.path, url)));
      return next.length === prev.length ? prev : next;
    });
  }, [movedOff, doc.path]);

  /** The photos on the record, and the ones this drawer has just uploaded to it. */
  const photos = useMemo<DrawerPhoto[]>(
    () => drawerPhotosOf(doc, added),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [doc.label_photo_urls, doc.label_photos, added],
  );

  const filled = useMemo(() => {
    if (!snapshot) return 0;
    return fieldsFor(kind).filter(
      (spec) => !isUnchanged(snapshot[spec.name] ?? null, doc.fields[spec.name] ?? null),
    ).length;
  }, [snapshot, doc.fields, kind]);

  const upload = async (file: File) => {
    setError(null);
    setStored(false);
    setSnapshot(null);
    setChecked(false);
    setDuplicateOf(null);
    setPhase('validating');
    const fileError = photoFileError(file);
    if (fileError) {
      setError(fileError);
      setPhase('failed');
      return;
    }
    setChecked(true);
    setBusy(true);
    setPhase('uploading');
    onWorking(true);
    // Taken before the request, so a read that lands between the POST and the
    // first poll is still measured against the values the operator saw.
    const before = { ...doc.fields };
    const editId = newEditId();
    try {
      const result = await uploadEquipmentPhoto(serial, {
        target: photoTarget(kind, doc.path),
        file,
        reason: photoReason(reason),
        editId,
        ...(componentHint ? { componentHint } : {}),
      });
      const reviewId = typeof result.review_id === 'string' && result.review_id ? result.review_id : null;
      const ownEditId = result.edit_id || editId;
      setStored(true);
      setSnapshot(before);
      setMyEditId(ownEditId);
      setMyReview(reviewId);
      // FS-807: an upload the server matched to a photo already here stored
      // nothing new, so there is no new tile: the existing one is ringed.
      const dup = typeof result.duplicate_of === 'string' && result.duplicate_of ? result.duplicate_of : null;
      setDuplicateOf(dup);
      if (dup) {
        setRingUrl(dup);
        window.clearTimeout(ringTimer.current);
        ringTimer.current = window.setTimeout(() => setRingUrl(null), DUPLICATE_RING_MS);
      } else {
        setAdded((prev) => [...prev, result.photo_url]);
      }
      setPhase('stored');
      // Nothing else is coming for a filter label, so the selection is done
      // with. An equipment label is still being read, and the file it is being
      // read off stays named until that lands. A label read into a review is
      // followed on the tab's banner, not here.
      if (result.analysis === 'stored_only' || reviewId) setSelected(null);
      const typed = reason.trim();
      onUploaded({
        path: doc.path,
        editId: ownEditId,
        reviewId,
        analysis: result.analysis,
        droppedOn:
          componentHint === 'outdoor_condenser'
            ? 'ac'
            : componentHint === 'indoor_coil'
              ? 'coil'
              : kind === 'filter'
                ? null
                : kind,
        typedReason: typed.length >= MIN_REASON_LENGTH ? typed : null,
      });
    } catch (err) {
      setError(photoUploadErrorMessage(err, file));
      setPhase('failed');
    } finally {
      setBusy(false);
      // Batched with whatever the try block set, so the drawer never sees a
      // moment where the upload is over and the read has not been picked up.
      onWorking(false);
    }
  };

  /** FS-1078 — the photo whose Read again is being asked for, and why the last one was refused. */
  const [rereading, setRereading] = useState<string | null>(null);
  const [rereadError, setRereadError] = useState<string | null>(null);

  /**
   * FS-1078 — Read again: the server reads the stored photo into a review on
   * it, and the drawer follows that review exactly as it follows an upload's
   * (it opens by itself when the read lands).
   */
  const readAgain = async (url: string) => {
    setRereadError(null);
    setRereading(url);
    onWorking(true);
    const editId = newEditId();
    try {
      const result = await rereadEquipmentPhoto(serial, {
        target: photoTarget(kind, doc.path),
        url,
        reason: photoReason(reason, PHOTO_REREAD_DEFAULT_REASON),
        editId,
      });
      const typed = reason.trim();
      onUploaded({
        path: doc.path,
        editId: result.edit_id || editId,
        reviewId: typeof result.review_id === 'string' && result.review_id ? result.review_id : editId,
        analysis: result.analysis,
        droppedOn: kind === 'filter' ? null : kind,
        typedReason: typed.length >= MIN_REASON_LENGTH ? typed : null,
      });
    } catch (err) {
      setRereadError(photoRereadErrorMessage(err));
    } finally {
      setRereading(null);
      onWorking(false);
    }
  };
  const reviewsOnFile = removal?.record?.photo_reviews ?? [];
  const readAgainCtl =
    reviewMode && kind !== 'filter'
      ? {
        blockedFor: (url: string): string | null => {
          if (rereading !== null) return READ_AGAIN_STARTING;
          // This photo's own reason first; the card's general hold after it.
          return readAgainBlock(doc, reviewsOnFile, url) ?? (disabled || busy ? PHOTO_WAIT_TOOLTIP : null);
        },
        start: (url: string) => void readAgain(url),
      }
      : null;

  const accepting = !disabled && !busy;

  /**
   * One selection, whichever way it arrived. The chooser and the drop run the
   * same line so a dropped file cannot skip a check the chooser applies.
   */
  const take = (files: File[]) => {
    if (files.length === 0) {
      setNotice(null);
      setSelected(null);
      setPhase('failed');
      setError(PHOTO_NO_FILE);
      return;
    }
    const file = files[0];
    setNotice(files.length > 1 ? PHOTO_ONE_AT_A_TIME : null);
    setSelected({ name: file.name, size: file.size });
    setPhase('selected');
    void upload(file);
  };

  /** Put the block back to nothing in hand, after a file it would not take. */
  const clearSelection = () => {
    setSelected(null);
    setError(null);
    setNotice(null);
    setChecked(false);
    setPhase('idle');
  };

  /** A drag the browser says is carrying files. An undescribed one is treated as one. */
  const carriesFiles = (transfer: DataTransfer | null): boolean => {
    const types = transfer?.types;
    if (!types) return true;
    return Array.from(types).includes('Files');
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    // Without this the drop never happens: the browser takes the file itself
    // and navigates away from the drawer, losing the correction in it.
    e.preventDefault();
    if (!accepting || !carriesFiles(e.dataTransfer)) return;
    if (e.dataTransfer) e.dataTransfer.dropEffect = 'copy';
    setDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    // Crossing onto a child of the zone fires dragleave on the zone as well;
    // that is still inside it, so the highlight stays.
    const to = e.relatedTarget as Node | null;
    if (to && e.currentTarget.contains(to)) return;
    setDragging(false);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setDragging(false);
    if (!accepting) return;
    take(e.dataTransfer?.files ? Array.from(e.dataTransfer.files) : []);
  };

  /**
   * FS-782 — the request this block's own upload made, by its edit id. An API
   * that lists the requests is answered from that list only, so a second
   * photo on the same record never finishes this one; an older API sends only
   * the newest request, which is all there is to go on.
   */
  const analysis: (EquipmentPhotoAnalysis & Partial<EquipmentPhotoAnalysisRequest>) | null =
    Array.isArray(doc.photo_analysis_requests)
      ? (doc.photo_analysis_requests.find((row) => myEditId !== null && row.edit_id === myEditId) ?? null)
      : (doc.photo_analysis ?? null);
  // Only ever this drawer's own upload: a record can arrive carrying the
  // result of a read somebody ran last week, and that is not news.
  const mine = stored && snapshot !== null;
  const finished = mine && Boolean(analysis?.processed_at);

  // The read landing is the end of this file's journey, so the block stops
  // naming it and leaves the result standing.
  useEffect(() => {
    if (finished) setSelected(null);
  }, [finished]);

  /** What the photo will be filed under, and whether the operator wrote it. */
  const reasonForPhoto = photoReason(reason);
  const usingDefaultReason = reasonForPhoto === PHOTO_DEFAULT_REASON;

  /**
   * What the read wrote, in the server's words where it sends them.
   *
   * The drawer's own count is the difference it can see, which is only the
   * fields it offers a box for — a read that filled four of them reported one.
   * `fields_updated` is the Function's list of what it actually promoted, so
   * it is used whenever it is there, names included.
   */
  const promoted = analysis?.fields_updated;
  /**
   * FS-784 §8.6 — a request that carries its outcome is named by it, with the
   * code in the `title`. One without (an API from before outcomes were kept)
   * keeps the wording below.
   */
  const named = outcomeWords({
    outcome: analysis?.outcome,
    reason: analysis?.reason_code,
    error: analysis?.error,
    rejected: analysis?.rejected,
    fieldsUpdated: Array.isArray(promoted) ? promoted : null,
    labelFor: (name) => readFieldLabel(kind, name),
  });
  const readSummary = (() => {
    if (Array.isArray(promoted)) {
      if (promoted.length === 0) return 'Label read — no fields changed';
      const names = promoted.map((name) => readFieldLabel(kind, name)).join(', ');
      return `Label read — ${promoted.length} ${
        promoted.length === 1 ? 'field' : 'fields'
      } updated: ${names}`;
    }
    return `Label read — ${filled} ${filled === 1 ? 'field' : 'fields'} updated`;
  })();

  /** Every step this file has reached, each one left on screen once it holds. */
  const steps: React.ReactNode[] = [];
  if (phase === 'validating') steps.push(<p key="checking">Checking the file…</p>);
  if (checked) steps.push(<p key="checked">File checked</p>);
  if (phase === 'uploading') steps.push(<p key="uploading">Uploading the photo…</p>);
  if (stored && !duplicateOf)
    steps.push(
      <p key="stored" className="font-medium">
        Photo stored
      </p>,
      // FS-877: the photo is already on the record; Save is for typed values.
      <p key="stored-note" className="text-muted-foreground">
        No need to press Save.
      </p>,
    );
  // FS-807 §8.5: an identical photo is said as such, in place of "stored".
  if (stored && duplicateOf) {
    const ended: DuplicateRead | null = myReview
      ? duplicateReview
      : finished && analysis
        ? requestReadOutcome(analysis)
        : null;
    steps.push(
      <p
        key="duplicate"
        className={`font-medium ${ended && ended !== 'ok' ? 'text-red-700' : ''}`}
        data-testid="photo-duplicate"
        title={`Same as ${photoFileName(duplicateOf)}`}
      >
        {kind === 'filter' ? DUPLICATE_FILTER : ended ? DUPLICATE_WORDS[ended] : DUPLICATE_READING}
      </p>,
    );
  }
  if (readState === 'polling') steps.push(<p key="reading">Reading the label…</p>);
  if (readState === 'gave_up') steps.push(<p key="gaveup">{PHOTO_STILL_READING}</p>);
  if (mine && myReview && !finished && !duplicateOf)
    steps.push(
      <p key="review">The label is being read; the values it finds are offered for review on this tab.</p>,
    );
  if (finished)
    steps.push(
      named ? (
        <p
          key="read"
          className={named.code === 'error' || named.code === 'rejected' ? 'text-red-700' : undefined}
          title={`Code: ${named.code}`}
        >
          {named.text}
        </p>
      ) : analysis?.error ? (
        <p key="read" className="text-red-700">
          {analysis.error}
        </p>
      ) : (
        <p key="read">{readSummary}</p>
      ),
    );
  // FS-807: a legacy request row the server marked as a duplicate says so,
  // whether or not this block's own upload was told (an older answer).
  if (finished && !duplicateOf && analysis?.duplicate_of)
    steps.push(
      <p key="duplicate-row" data-testid="photo-duplicate">
        {DUPLICATE_WORDS[requestReadOutcome(analysis)]}
      </p>,
    );

  return (
    <div data-testid="equipment-photos" className="mt-4">
      {showList && <p className="text-xs uppercase tracking-wide text-muted-foreground">Photos</p>}

      {showList && (
        <PhotoList
          kind={kind}
          doc={doc}
          prefix={prefix}
          photos={photos}
          removal={removal ?? null}
          // FS-783 §8.3: a photo this drawer uploaded is carried in `added`
          // until the record lists it; once removed it must not come back
          // from there.
          onRemovedUrl={(url) => setAdded((prev) => prev.filter((u) => u !== url))}
          ringUrl={ringUrl}
          readAgain={readAgainCtl}
        />
      )}
      {rereadError && (
        <p className="mt-1 text-xs text-red-700" role="alert" data-testid="read-again-error">
          {rereadError}
        </p>
      )}

      <div
        data-testid="photo-drop-zone"
        title="Drop a label photo here. It is stored as soon as it uploads; no need to press Save."
        onDragEnter={handleDragOver}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        className={`mt-2 rounded-sm border border-dashed p-2 transition-colors ${
          dragging ? 'border-status-info-border bg-blue-50' : 'border-border'
        }`}
      >
        <label htmlFor={id} className="text-xs uppercase tracking-wide text-muted-foreground">
          Add label photo
        </label>
        <Input
          id={id}
          type="file"
          accept={PHOTO_ACCEPT}
          disabled={disabled || busy}
          title="Choose a photo of the equipment label. It is stored as soon as it uploads; no need to press Save."
          aria-describedby={`${id}-help`}
          onChange={(e) => {
            const files = e.target.files ? Array.from(e.target.files) : [];
            // Cleared so the same file can be chosen again after a refusal.
            // That is also why the block names the file itself: the control
            // goes back to saying nothing is chosen.
            e.target.value = '';
            // No files means the chooser was cancelled, which is not an error.
            if (files.length > 0) take(files);
          }}
          className="mt-1"
        />
        <p id={`${id}-help`} className="mt-1 text-xs text-muted-foreground">
          The photo is stored as soon as it uploads; Save is not needed.
        </p>
        {selected && (
          <p className="mt-1 flex items-center gap-2 text-xs">
            <span className="font-medium text-gray-700">
              {`Selected: ${selected.name} (${photoSizeLabel(selected.size)})`}
            </span>
            {phase === 'failed' && (
              <Button
                variant="link"
                onClick={clearSelection}
                aria-label="Clear the selected photo"
                title="Forget the photo chosen above. Its upload failed, so nothing was stored."
              >
                Clear
              </Button>
            )}
          </p>
        )}
        <p className={`mt-1 text-xs ${dragging ? 'font-medium text-blue-700' : 'text-muted-foreground'}`}>
          {dragging ? PHOTO_DROP_ACTIVE : PHOTO_DROP_IDLE}
        </p>
        <p className="mt-1 text-xs text-muted-foreground">{PHOTO_HEIC_NOTE[kind]}</p>
        <p className="mt-1 text-xs text-muted-foreground">
          {reviewMode && kind !== 'filter' ? PHOTO_REVIEW_HELP : PHOTO_HELP[kind]}
        </p>
        <p className="mt-1 text-xs text-muted-foreground">
          {usingDefaultReason
            ? `Reason: ${PHOTO_DEFAULT_REASON} — edit below to change`
            : 'Reason: the reason typed below is kept with the photo.'}
        </p>
      </div>

      {error && (
        <p className="mt-2 text-xs text-red-700" role="alert">
          {error}
        </p>
      )}

      {notice && <p className="mt-2 text-xs text-gray-700">{notice}</p>}

      {steps.length > 0 && (
        <div role="status" className="mt-2 space-y-1 text-xs text-gray-700">
          {steps}
        </div>
      )}
    </div>
  );
};

/**
 * One document an operator can correct. Each card carries its own pending
 * change, reason and preview, because each is a separate write against a
 * separate document version.
 */
const EquipmentDocCard: React.FC<{
  serial: string;
  kind: EquipmentTargetKind;
  doc: EquipmentDocument;
  title: string;
  optionsFor: (spec: EquipmentFieldSpec) => string[];
  onSaved: () => void;
  readState: PhotoReadState;
  onPhotoUploaded: (uploaded: PhotoUploaded) => void;
  /** A photo anywhere in the drawer is going up or being read off. */
  photoWorking: boolean;
  /** FS-784 — the server reads operator photos into a review. */
  reviewMode?: boolean;
  /**
   * FS-784 — the fields a photo review just wrote on this record. A new
   * `nonce` is a new set; null clears the rings.
   */
  highlight?: { fields: string[]; nonce: number; label?: string } | null;
  /** FS-784 — a review is being applied: no upload starts on this card (Codex F12). */
  uploadHold?: boolean;
  onPhotoWorking: (working: boolean) => void;
  /** FS-593 — the fields this card offers, when not the kind's whole list. */
  specs?: EquipmentFieldSpec[];
  /** FS-593 — called just before a real (not preview) write is sent. */
  onBeforeWrite?: () => void;
  /** FS-593 — sent as `filterId`: this card edits a slot that is not the Clip's own. */
  filterId?: string;
  /** Whether the card takes a label photo. Default: whatever the kind takes. */
  photos?: boolean;
  /** A line under the title saying what this record is. */
  note?: React.ReactNode;
  /**
   * FS-876 — a catalog review of this record's model that an operator
   * dismissed, offered again: one quiet line under the title.
   */
  reviewNotice?: React.ReactNode;
  /**
   * FS-593 — the record cannot be edited from here, and this says where it
   * can. Every box is disabled and there is nothing to save.
   */
  locked?: { hint: React.ReactNode; title: string };
  /** FS-783 — Remove and Undo on this record's photos. */
  removal?: PhotoRemovalCtx | null;
  /**
   * FS-783 — show the record's photos without an upload (a coil, a filter slot,
   * an older filter record of this Clip), so they can be seen and removed.
   */
  viewPhotos?: boolean;
}> = ({
  serial,
  kind,
  doc,
  title,
  optionsFor,
  onSaved,
  readState,
  onPhotoUploaded,
  photoWorking,
  onPhotoWorking,
  specs: specsOverride,
  filterId,
  photos,
  note,
  reviewNotice,
  locked,
  onBeforeWrite,
  reviewMode,
  highlight,
  uploadHold = false,
  removal,
  viewPhotos,
}) => {
  const [edits, setEdits] = useState<Record<string, string>>({});
  /**
   * FS-783 addendum 2 F12 — flagged values the operator says are still right.
   * Sent as `confirm`: the server keeps each value and re-stamps it as the
   * operator's entry, which is what ends its "evidence removed" mark. Typing
   * a new value into a field takes it off this list.
   */
  const [confirmed, setConfirmed] = useState<Set<string>>(new Set());
  const previewRef = useRef<HTMLButtonElement | null>(null);
  /**
   * FS-877 — Keep and Clear hand the operator to Preview, but Preview is
   * disabled until something is staged, so the focus waits for the render
   * that stages it.
   */
  const [focusPreview, setFocusPreview] = useState(0);
  useEffect(() => {
    if (focusPreview > 0) previewRef.current?.focus();
  }, [focusPreview]);
  /**
   * Codex F10 — the record as it stood when the first value was typed. A save
   * is checked against THAT version, so a value that changed on file while
   * the operator was typing (a photo Apply, a label read, anyone) is never
   * overwritten blind: the save waits until the operator has looked and said
   * to keep their typed values against the record as it is now.
   */
  const [baseline, setBaseline] = useState<{ time: string; fields: Record<string, unknown> } | null>(null);
  /** FS-784 — fields ringed as just written from a photo, until edited or timed out. */
  const [ringed, setRinged] = useState<Set<string>>(new Set());
  /** FS-784 — fields a photo wrote while the operator had an unsaved value in them. */
  const [changedUnderEdit, setChangedUnderEdit] = useState<Set<string>>(new Set());
  const editsRef = useRef(edits);
  editsRef.current = edits;
  const highlightNonce = highlight?.nonce ?? null;
  useEffect(() => {
    if (!highlight) {
      setRinged(new Set());
      return;
    }
    setRinged(new Set(highlight.fields));
    setChangedUnderEdit(
      (prev) =>
        new Set([...prev, ...highlight.fields.filter((name) => editsRef.current[name] !== undefined)]),
    );
    // Keyed on the nonce: the same set arriving again is not a new write.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [highlightNonce]);
  const [reason, setReason] = useState('');
  const [reasonError, setReasonError] = useState<string | null>(null);
  const [preview, setPreview] = useState<Record<string, EquipmentChange> | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // FS-825 — a filter's name box is worded for the filter type selected in
  // this card: the one being typed if any, else the one on file.
  const filterTypeNow =
    kind === 'filter' ? (edits.filterType !== undefined ? edits.filterType : doc.fields.filterType) : undefined;
  const specs = useMemo(() => {
    const base = specsOverride ?? fieldsFor(kind);
    return kind === 'filter' ? withFilterLocationWording(base, filterTypeNow) : base;
  }, [specsOverride, kind, filterTypeNow]);
  const prefix = idPrefix(doc.path);
  const showPhotos = (photos ?? true) && acceptsPhotos(kind);

  const { fields, fieldErrors } = useMemo(() => {
    const out: Record<string, unknown> = {};
    const errors: Record<string, string> = {};
    specs.forEach((spec) => {
      const raw = edits[spec.name];
      if (raw === undefined) return;
      const parsed = parseField(spec, raw);
      if (parsed.error) {
        errors[spec.name] = parsed.error;
        return;
      }
      if (isUnchanged(doc.fields[spec.name], parsed.value)) return;
      out[spec.name] = parsed.value;
    });
    return { fields: out, fieldErrors: errors };
  }, [edits, doc.fields, specs]);

  /** The record moved on file since the first value was typed here. */
  const moved =
    baseline !== null && (Object.keys(edits).length > 0 || confirmed.size > 0) && baseline.time !== doc.update_time;
  /** Of the fields typed into, the ones whose value on file changed meanwhile. */
  const movedFields = moved
    ? Object.keys(edits).filter((name) => !isUnchanged(baseline!.fields[name] ?? null, doc.fields[name] ?? null))
    : [];
  const MOVED_TITLE =
    'The record changed on file while you were editing. Check it, then press Keep my typed values before saving.';

  /**
   * FS-783 §6 — the values on this record read from a removed photo. The
   * server derives the flag from the record's removals; the console only
   * shows it, and clearing or replacing a value is an ordinary edit.
   */
  const flagged = doc.field_evidence_removed ?? {};
  const flaggedNames = Object.keys(flagged);
  const uncertain = new Set(doc.uncertain_evidence_fields ?? []);
  const specByName = new Map(specs.map((spec) => [spec.name, spec]));
  const canClear = (spec: EquipmentFieldSpec | undefined): spec is EquipmentFieldSpec =>
    Boolean(spec) && spec!.clearable !== false && spec!.kind !== 'components';
  /** The flagged fields Clear stages: every clearable one with a box here, and its flagged companions. */
  const toClear = (() => {
    const names = new Set<string>();
    flaggedNames.forEach((name) => {
      if (!canClear(specByName.get(name))) return;
      names.add(name);
      (COMPANION_FIELDS[name] ?? []).forEach((partner) => {
        if (flagged[partner]?.removal_id === flagged[name].removal_id && canClear(specByName.get(partner))) {
          names.add(partner);
        }
      });
    });
    return Array.from(names);
  })();
  /** The confirmations that go out: flagged fields still marked, not also being changed. */
  const confirmList = Array.from(confirmed).filter((name) => flagged[name] && fields[name] === undefined);
  /**
   * FS-877 — something on this card for Save to send. A value that does not
   * parse counts too: Save then says what is wrong with it, rather than
   * claiming nothing was typed.
   */
  const staged = Object.keys(fields).length > 0 || confirmList.length > 0 || Object.keys(fieldErrors).length > 0;
  /**
   * A flagged value being changed or confirmed whose partner was worked out
   * from the same photo, by field: the partner's label. The server checks the
   * EFFECTIVE update (addendum 2 F12): a partner counts as resolved only when
   * its value changes, is cleared, or is confirmed — sending it unchanged
   * does not. Every flagged partner the record holds is asked for (F11: a
   * coil's capacity source too); a partner the record does not have is never
   * flagged, so it is never asked for.
   */
  const resolved = (name: string) => fields[name] !== undefined || confirmList.includes(name);
  const companionsNeeded: Record<string, string> = {};
  Object.keys(flagged).forEach((name) => {
    if (!resolved(name)) return;
    (COMPANION_FIELDS[name] ?? []).forEach((partner) => {
      if (flagged[partner]?.removal_id !== flagged[name].removal_id) return;
      if (!resolved(partner)) companionsNeeded[name] = specByName.get(partner)?.label ?? readFieldLabel(kind, partner);
    });
  });
  const companionBlocked = Object.keys(companionsNeeded).length > 0;
  const COMPANION_TITLE = `Set, clear or confirm ${Object.values(companionsNeeded).join(' and ')} too: it was worked out from the same removed photo`;

  /** Mark a flagged value as checked and still right, or take that back. */
  const toggleConfirm = (name: string) => {
    setBaseline((prev) => prev ?? { time: doc.update_time, fields: { ...doc.fields } });
    setConfirmed((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
    // Confirming means keeping what is on file, so a value typed over it goes.
    setEdits((prev) => {
      if (prev[name] === undefined) return prev;
      const next = { ...prev };
      delete next[name];
      return next;
    });
  };

  /**
   * The flagged fields Keep stages as confirmed: each one this card has a box
   * for (a restricted filter slot offers only its type and name, and the
   * server refuses a confirm of anything else) and that has a value on file
   * (a confirm of an empty field is refused too).
   */
  const toKeep = flaggedNames.filter((name) => {
    if (!specByName.has(name)) return false;
    const value = doc.fields[name];
    return value !== null && value !== undefined && value !== '';
  });

  /** The reason Keep offers when the box is empty; the operator may change it before saving. */
  const KEEP_REASON = 'Values confirmed after the photo was removed';

  /**
   * One click for what pressing Confirm still right on every flagged field
   * does: each is kept as it is and re-stamped as the operator's entry on
   * save. Nothing is sent here; the operator reviews and saves as usual.
   */
  const keepFlagged = () => {
    setBaseline((prev) => prev ?? { time: doc.update_time, fields: { ...doc.fields } });
    setConfirmed((prev) => new Set([...Array.from(prev), ...toKeep]));
    // Keeping means keeping what is on file, so a value typed over one goes.
    setEdits((prev) => {
      const next = { ...prev };
      toKeep.forEach((name) => {
        delete next[name];
      });
      return next;
    });
    setReason((prev) => (prev.trim() === '' ? KEEP_REASON : prev));
    setFocusPreview((n) => n + 1);
  };

  /** Stage every clearable flagged value as cleared, and hand the operator to Preview. */
  const clearFlagged = () => {
    setBaseline((prev) => prev ?? { time: doc.update_time, fields: { ...doc.fields } });
    setEdits((prev) => {
      const next = { ...prev };
      toClear.forEach((name) => {
        next[name] = '';
      });
      return next;
    });
    setConfirmed((prev) => new Set(Array.from(prev).filter((name) => !toClear.includes(name))));
    setFocusPreview((n) => n + 1);
  };

  const run = async (dryRun: boolean) => {
    setError(null);
    setReasonError(null);
    setPreview(null);
    if (moved) return;
    if (reason.trim().length < MIN_REASON_LENGTH) {
      setReasonError(EQUIPMENT_EDIT_ERRORS.reason_required);
      return;
    }
    if (Object.keys(fieldErrors).length > 0) return;
    if (Object.keys(fields).length === 0 && confirmList.length === 0) {
      setError(EQUIPMENT_EDIT_ERRORS.no_changes);
      return;
    }
    if (companionBlocked) return;
    setBusy(true);
    try {
      if (!dryRun) onBeforeWrite?.();
      const result = await patchEquipment(serial, {
        target: { kind, path: doc.path },
        expected_update_time: baseline?.time ?? doc.update_time,
        fields,
        ...(confirmList.length > 0 ? { confirm: confirmList } : {}),
        reason: reason.trim(),
        ...(filterId ? { filterId } : {}),
        ...(dryRun ? { dry_run: true } : {}),
      });
      if (dryRun) {
        setPreview(result.changes ?? {});
      } else {
        onSaved();
      }
    } catch (err) {
      // Addendum 3 N5: a confirmed field must have a value on file. The
      // server names the ones that do not, and so does this sentence.
      const details = equipmentErrorDetails(err);
      const refusedConfirm =
        equipmentErrorCode(err) === 'invalid_field' && details?.field === 'confirm' && Array.isArray(details?.fields)
          ? (details.fields as unknown[]).map(String)
          : null;
      setError(
        refusedConfirm && refusedConfirm.length > 0
          ? `${refusedConfirm.map((name) => specByName.get(name)?.label ?? readFieldLabel(kind, name)).join(', ')} ${
            refusedConfirm.length === 1 ? 'has' : 'have'
          } nothing on file, so there is nothing to confirm. Type a value instead. Nothing was saved.`
          : equipmentErrorMessage(err),
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="equipment-card" data-card-path={doc.path} className="rounded-md border p-4">
      <div className="flex items-baseline justify-between gap-2">
        <h4 className="font-medium">{title}</h4>
        {doc.is_resolver_pick && (
          <span
            className="text-[10px] font-semibold uppercase border border-status-info-border text-status-info-foreground bg-status-info rounded-sm px-1"
            title="This is the record the resolver reads this device's facts from."
          >
            resolver pick
          </span>
        )}
      </div>
      {note && <p className="mt-1 text-xs text-muted-foreground">{note}</p>}
      {reviewNotice}
      {locked && (
        <p className="mt-1 text-sm text-amber-800" title={locked.title} data-testid="filter-slot-locked">
          {locked.hint}
        </p>
      )}

      {flaggedNames.length > 0 && (
        <div
          data-testid="evidence-removed-banner"
          className="mt-3 flex items-center justify-between gap-3 rounded-md border border-status-warning-border bg-amber-50 p-3 text-sm text-amber-900"
        >
          <p>
            {flaggedNames.length === 1
              ? '1 value on this record was read from a removed photo.'
              : `${flaggedNames.length} values on this record were read from a removed photo.`}
          </p>
          <div className="flex shrink-0 gap-2">
            {!locked && toClear.length > 0 && (
              <Button
                variant="secondary"
                disabled={busy}
                onClick={clearFlagged}
                title="Stage these fields as cleared, then review and save with a reason"
                className="shrink-0"
              >
                Clear these values
              </Button>
            )}
            {!locked && toKeep.length > 0 && (
              <Button
                variant="secondary"
                disabled={busy}
                onClick={keepFlagged}
                title="Keep these values as operator-confirmed; they will no longer be marked as read from a removed photo"
                className="shrink-0"
              >
                Keep these values
              </Button>
            )}
          </div>
        </div>
      )}

      <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-4">
        {specs.map((spec) => (
          <FieldRow
            key={spec.name}
            spec={spec}
            prefix={prefix}
            current={doc.fields[spec.name]}
            source={doc.field_sources[spec.name]?.source}
            options={optionsFor(spec)}
            edited={edits[spec.name]}
            error={fieldErrors[spec.name]}
            disabled={busy || Boolean(locked)}
            // A filter's model number is not equipment: it belongs to the
            // filter catalog, which is a different list.
            catalogModel={kind !== 'filter' && spec.name === 'modelNumber'}
            fromPhoto={ringed.has(spec.name)}
            fromLabel={highlight?.label}
            photoChangedUnderEdit={changedUnderEdit.has(spec.name)}
            evidenceRemoved={flagged[spec.name] ?? null}
            uncertainEvidence={uncertain.has(spec.name)}
            companionNeeded={companionsNeeded[spec.name] ?? null}
            // Addendum 2 F19: said whenever the field is flagged, not only
            // after Clear — a record flagged only on required fields has no
            // Clear button to press.
            cannotClear={!locked && Boolean(flagged[spec.name]) && !canClear(spec)}
            confirmState={
              locked || !flagged[spec.name] ? null : confirmed.has(spec.name) && edits[spec.name] === undefined ? 'on' : 'off'
            }
            onConfirm={() => toggleConfirm(spec.name)}
            onChange={(raw) => {
              setBaseline((prev) => prev ?? { time: doc.update_time, fields: { ...doc.fields } });
              setEdits((prev) => ({ ...prev, [spec.name]: raw }));
              setConfirmed((prev) => {
                if (!prev.has(spec.name)) return prev;
                const next = new Set(prev);
                next.delete(spec.name);
                return next;
              });
              // An edit is the operator taking the field over: the ring has
              // done its job.
              setRinged((prev) => {
                if (!prev.has(spec.name)) return prev;
                const next = new Set(prev);
                next.delete(spec.name);
                return next;
              });
            }}
          />
        ))}
      </div>

      {/* Plan 120: a coil takes field edits only — there is no photo action
          on it, so the block is absent rather than disabled. */}
      {showPhotos && acceptsPhotos(kind) && !locked && (
        <PhotoBlock
          serial={serial}
          kind={kind}
          doc={doc}
          prefix={prefix}
          reason={reason}
          disabled={busy || uploadHold}
          readState={readState}
          onUploaded={onPhotoUploaded}
          onWorking={onPhotoWorking}
          reviewMode={reviewMode}
          removal={removal}
        />
      )}

      {/* FS-783: a card that takes no upload still shows its photos, so a
          wrong one can be seen and removed. Nothing is drawn when there is
          nothing to show. */}
      {!(showPhotos && acceptsPhotos(kind) && !locked) && (viewPhotos ?? kind === 'coil') && (() => {
        const photos = drawerPhotosOf(doc, []);
        const hasAny =
          photos.length > 0 ||
          (doc.other_photo_urls ?? []).length > 0 ||
          (doc.photo_removal_notices ?? []).length > 0 ||
          removedPhotosFor(doc, removal ?? null).length > 0;
        if (!hasAny) return null;
        return (
          <div data-testid="equipment-photos-view" className="mt-4">
            <p className="text-xs uppercase tracking-wide text-muted-foreground">Photos</p>
            <PhotoList kind={kind} doc={doc} prefix={prefix} photos={photos} removal={removal ?? null} emptyText={null} />
          </div>
        );
      })()}

      {!locked && (
        <>
          <ReasonField
            prefix={prefix}
            value={reason}
            error={reasonError}
            disabled={busy}
            onChange={setReason}
          />

          {error && (
            <p className="mt-3 rounded-md border border-status-error-border bg-red-50 p-3 text-sm text-red-800" role="alert">
              {error}
            </p>
          )}

          {preview && <DiffTable kind={kind} changes={preview} filterType={filterTypeNow} />}

          {moved && !photoWorking && (
            <div
              className="mt-3 rounded-md border border-status-warning-border bg-amber-50 p-3 text-sm text-amber-900"
              data-testid="card-record-moved"
            >
              <p>
                {movedFields.length > 0
                  ? `The record changed on file while you were editing: ${movedFields
                    .map((name) => specForFilterType(kind, name, filterTypeNow)?.label ?? name)
                    .join(', ')}. Nothing was saved. Check the value on file before keeping yours.`
                  : 'The record changed on file while you were editing, in fields you did not type into. Nothing was saved.'}
              </p>
              <Button
                variant="secondary"
                onClick={() => setBaseline({ time: doc.update_time, fields: { ...doc.fields } })}
                title="Save your typed values against the record as it is now; they replace what is on file for those fields"
                className="mt-2"
              >
                Keep my typed values
              </Button>
            </div>
          )}

          <div className="mt-3 flex flex-wrap items-center gap-2">
            <Button
              variant="secondary"
              ref={previewRef}
              disabled={busy || photoWorking || moved || companionBlocked || !staged}
              onClick={() => run(true)}
              title={
                photoWorking
                  ? PHOTO_WAIT_TOOLTIP
                  : moved
                    ? MOVED_TITLE
                    : companionBlocked
                      ? COMPANION_TITLE
                      : !staged
                        ? NOTHING_TO_PREVIEW_TITLE
                        : 'Ask the server what this change would do, without writing anything.'
              }
            >
              Preview
            </Button>
            <Button
              variant="primary"
              disabled={busy || photoWorking || moved || companionBlocked || !staged}
              aria-describedby={!staged ? `${prefix}-save-disabled-reason` : undefined}
              onClick={() => run(false)}
              title={
                photoWorking
                  ? PHOTO_WAIT_TOOLTIP
                  : moved
                    ? MOVED_TITLE
                    : companionBlocked
                      ? COMPANION_TITLE
                      : !staged
                        ? NOTHING_TO_SAVE_TITLE
                        : 'Save these changes to the homeowner\'s record'
              }
            >
              {busy ? 'Working…' : 'Save changes'}
            </Button>
            {!staged && (
              <span id={`${prefix}-save-disabled-reason`} className="text-xs text-muted-foreground">
                {NOTHING_TO_SAVE_TITLE}
              </span>
            )}
          </div>
          {!busy && staged && (
            <p data-testid="save-state" className="mt-1 text-xs text-muted-foreground">
              {NOT_SAVED_YET}
            </p>
          )}
        </>
      )}
    </div>
  );
};

/**
 * Record a filter change on the homeowner's behalf — the same thing the app
 * does when they change a filter, so the device's baselines are reset and the
 * new period starts from the install date.
 *
 * The version this form sends back is the **device** document's, not the
 * filter's: the write creates a filter, repoints the device at it and clears
 * the device's baselines in one transaction, so what it must not race is a
 * homeowner changing their filter in the app at the same moment. Without that
 * version there is nothing to check the write against, so the form says so and
 * sends nothing.
 */
const FilterChangeForm: React.FC<{
  serial: string;
  currentFilter: EquipmentDocument | undefined;
  brands: string[];
  deviceUpdateTime: string | null;
  onSaved: () => void;
  /** A photo is going up or being read: this form writes the same records. */
  photoWorking: boolean;
  /** FS-593 — called just before a real (not preview) write is sent. */
  onBeforeWrite?: () => void;
}> = ({ serial, currentFilter, brands, deviceUpdateTime, onSaved, photoWorking, onBeforeWrite }) => {
  const prefix = 'filter-change';
  const brandsListId = `${prefix}-brands`;
  const [manufacturer, setManufacturer] = useState('');
  const [rating, setRating] = useState('');
  const [length, setLength] = useState('');
  const [width, setWidth] = useState('');
  const [thickness, setThickness] = useState('');
  const [modelNumber, setModelNumber] = useState('');
  const [installDate, setInstallDate] = useState('');
  const [isNewFilter, setIsNewFilter] = useState(true);
  const [reason, setReason] = useState('');
  const [reasonError, setReasonError] = useState<string | null>(null);
  const [dateError, setDateError] = useState<string | null>(null);
  const [sizeError, setSizeError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [preview, setPreview] = useState<Record<string, EquipmentChange> | null>(null);
  const [busy, setBusy] = useState(false);
  const copyDisabledReason = !currentFilter
    ? 'This Clip has no current filter record to copy.'
    : busy ? 'Wait for the filter change request to finish before copying.' : null;

  const copyPreviousFilter = () => {
    if (!currentFilter) return;
    const { fields } = currentFilter;
    setManufacturer(editableValue(fields.manufacturer));
    setRating(editableValue(fields.rating));
    setLength(editableValue(fields.length));
    setWidth(editableValue(fields.width));
    setThickness(editableValue(fields.thickness));
    setModelNumber(editableValue(fields.modelNumber));
  };

  const numberField = (label: string, value: string, set: (v: string) => void) => {
    const id = `${prefix}-${label.replace(/[^a-z]+/gi, '-').toLowerCase()}`;
    return (
      <div className="flex flex-col">
        <label htmlFor={id} className="text-xs uppercase tracking-wide text-muted-foreground">
          {label}
        </label>
        <Input
          id={id}
          type="text"
          inputMode="decimal"
          value={value}
          disabled={busy}
          title={`Enter the installed filter's ${label.toLowerCase()} in inches.`}
          aria-describedby={`${id}-help`}
          onChange={(e) => set(e.target.value)}
          className="mt-1"
        />
        <span id={`${id}-help`} className="mt-1 text-xs text-muted-foreground">
          Enter the installed filter&apos;s {label.toLowerCase()}.
        </span>
      </div>
    );
  };

  const run = async (dryRun: boolean) => {
    setError(null);
    setReasonError(null);
    setDateError(null);
    setSizeError(null);
    setPreview(null);

    // Nothing typed into this form can supply the missing version, so this is
    // checked before the fields are and stops the submit on its own.
    if (!deviceUpdateTime) {
      setError(EQUIPMENT_EDIT_ERRORS.missing_device_version);
      return;
    }

    const sizes = [Number(length), Number(width), Number(thickness)];
    const sizesValid =
      length.trim() !== '' &&
      width.trim() !== '' &&
      thickness.trim() !== '' &&
      sizes.every((n) => Number.isFinite(n) && n > 0);
    if (!sizesValid) {
      setSizeError('Give the length, width and thickness in inches, for example 20, 25 and 1.');
    }
    if (!isCalendarDate(installDate.trim())) {
      setDateError(DATE_HELP);
    }
    if (reason.trim().length < MIN_REASON_LENGTH) {
      setReasonError(EQUIPMENT_EDIT_ERRORS.reason_required);
    }
    if (manufacturer.trim() === '' || rating.trim() === '') {
      setError('Give the brand and the rating printed on the filter before recording it.');
    }
    if (
      !sizesValid ||
      !isCalendarDate(installDate.trim()) ||
      reason.trim().length < MIN_REASON_LENGTH ||
      manufacturer.trim() === '' ||
      rating.trim() === ''
    ) {
      return;
    }

    setBusy(true);
    try {
      if (!dryRun) onBeforeWrite?.();
      const result = await createFilterRecord(serial, {
        filter: {
          manufacturer: manufacturer.trim(),
          rating: rating.trim(),
          length: sizes[0],
          width: sizes[1],
          thickness: sizes[2],
          ...(modelNumber.trim() ? { modelNumber: modelNumber.trim() } : {}),
          installDate: installDate.trim(),
          isNewFilter,
        },
        expected_update_time: deviceUpdateTime,
        reason: reason.trim(),
        ...(dryRun ? { dry_run: true } : {}),
      });
      if (dryRun) {
        setPreview(result.changes ?? {});
      } else {
        onSaved();
      }
    } catch (err) {
      setError(equipmentErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="filter-change-form" role="region" aria-labelledby={`${prefix}-heading`} className="rounded-md border p-4">
      <h4 id={`${prefix}-heading`} className="font-medium">Record filter change</h4>
      <p className="mt-1 text-xs text-muted-foreground">
        This records a new filter on the homeowner's system exactly as the app would, so
        the device's baselines reset and filter life is measured from the install date.
      </p>

      <ActionRow
        className="mt-3"
        reason={copyDisabledReason}
        reasonId={`${prefix}-copy-reason`}
      >
        <Button
          variant="secondary"
          disabled={busy || !currentFilter}
          aria-describedby={copyDisabledReason ? `${prefix}-copy-reason` : undefined}
          onClick={copyPreviousFilter}
          title="Fill the brand, rating, size and model number from this Clip's current filter. The install date is still entered by you."
        >
          Copy Previous Filter
        </Button>
      </ActionRow>

      <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="flex flex-col">
          <label htmlFor={`${prefix}-brand`} className="text-xs uppercase tracking-wide text-muted-foreground">
            Brand
          </label>
          <Input
            id={`${prefix}-brand`}
            type="text"
            list={brandsListId}
            value={manufacturer}
            disabled={busy}
            title="Enter the brand printed on the installed filter; catalog brands are suggested."
            aria-describedby={`${prefix}-brand-help`}
            onChange={(e) => setManufacturer(e.target.value)}
            className="mt-1"
          />
          <datalist id={brandsListId}>
            {brands.map((brand) => (
              <option key={brand} value={brand} />
            ))}
          </datalist>
          <span id={`${prefix}-brand-help`} className="mt-1 text-xs text-muted-foreground">
            Brands already in the filter catalog are suggested; a brand that is not on the
            list can still be typed in.
          </span>
        </div>

        <div className="flex flex-col">
          <label htmlFor={`${prefix}-rating`} className="text-xs uppercase tracking-wide text-muted-foreground">
            Rating
          </label>
          <Input
            id={`${prefix}-rating`}
            type="text"
            value={rating}
            disabled={busy}
            title="Enter the MERV, MPR or FPR rating printed on the installed filter."
            aria-describedby={`${prefix}-rating-help`}
            onChange={(e) => setRating(e.target.value)}
            className="mt-1"
          />
          <span id={`${prefix}-rating-help`} className="mt-1 text-xs text-muted-foreground">
            The MERV, MPR or FPR figure printed on the filter.
          </span>
        </div>

        {numberField('Length (in.)', length, setLength)}
        {numberField('Width (in.)', width, setWidth)}
        {numberField('Thickness (in.)', thickness, setThickness)}

        <div className="flex flex-col">
          <label htmlFor={`${prefix}-model`} className="text-xs uppercase tracking-wide text-muted-foreground">
            Model number
          </label>
          <Input
            id={`${prefix}-model`}
            type="text"
            value={modelNumber}
            disabled={busy}
            title="Enter the installed filter's model number if it is printed on the filter."
            aria-describedby={`${prefix}-model-help`}
            onChange={(e) => setModelNumber(e.target.value)}
            className="mt-1"
          />
          <span id={`${prefix}-model-help`} className="mt-1 text-xs text-muted-foreground">Optional.</span>
        </div>

        <div className="flex flex-col">
          <label htmlFor={`${prefix}-install-date`} className="text-xs uppercase tracking-wide text-muted-foreground">
            Install date
          </label>
          <Input
            id={`${prefix}-install-date`}
            type="text"
            value={installDate}
            placeholder="YYYY-MM-DD"
            disabled={busy}
            title="Enter the date this filter was installed as YYYY-MM-DD."
            aria-describedby={`${prefix}-install-date-help${dateError ? ` ${prefix}-install-date-error` : ''}`}
            aria-invalid={Boolean(dateError)}
            onChange={(e) => setInstallDate(e.target.value)}
            className="mt-1"
          />
          <span id={`${prefix}-install-date-help`} className="mt-1 text-xs text-muted-foreground">
            Use YYYY-MM-DD for the date the filter went in.
          </span>
          {dateError && (
            <span id={`${prefix}-install-date-error`} className="mt-1 text-xs text-red-700" role="alert">
              {dateError}
            </span>
          )}
        </div>

        <div className="flex flex-col">
          <span className="text-xs uppercase tracking-wide text-muted-foreground">Installed new</span>
          <label className="mt-1 flex items-center gap-2 text-sm">
            <Checkbox
              checked={isNewFilter}
              disabled={busy}
              title="Mark whether the installed filter went in clean; this affects the baseline for filter life."
              aria-describedby={`${prefix}-installed-new-help`}
              onCheckedChange={setIsNewFilter}
            />
            The filter went in clean
          </label>
          <span id={`${prefix}-installed-new-help`} className="mt-1 text-xs text-muted-foreground">
            This choice affects the new filter period&apos;s baseline.
          </span>
        </div>
      </div>

      {sizeError && (
        <p className="mt-3 text-sm text-red-700" role="alert">
          {sizeError}
        </p>
      )}

      <ReasonField
        prefix={prefix}
        value={reason}
        error={reasonError}
        disabled={busy}
        onChange={setReason}
      />

      {error && (
        <p className="mt-3 rounded-md border border-status-error-border bg-red-50 p-3 text-sm text-red-800" role="alert">
          {error}
        </p>
      )}

      {preview && <DiffTable kind="filter" changes={preview} />}

      <div className="mt-3 flex gap-2">
        <Button
          variant="secondary"
          disabled={busy || photoWorking}
          onClick={() => run(true)}
          title={
            photoWorking
              ? PHOTO_WAIT_TOOLTIP
              : 'Ask the server what this would record, without writing anything.'
          }
        >
          Preview
        </Button>
        <Button
          variant="primary"
          disabled={busy || photoWorking}
          onClick={() => run(false)}
          title={
            photoWorking
              ? PHOTO_WAIT_TOOLTIP
              : 'Record that this filter was replaced: starts a new filter period with the details above. A reason is needed.'
          }
        >
          {busy ? 'Working…' : 'Record filter change'}
        </Button>
      </div>
    </div>
  );
};

/**
 * FS-593 — every filter record on the system, one card each, in the order the
 * projection lists them. The Clip's own filter takes every field (type and
 * name first); a slot with no Clip, or an older record naming this Clip, takes
 * its type and name; another Clip's slot is shown with both boxes disabled
 * and says where it can be edited. The server enforces the same rules.
 */
const FilterSlotCards: React.FC<{
  serial: string;
  filters: EquipmentDocument[];
  reloadNonce: number;
  clipLinks?: Record<string, number>;
  optionsFor: (spec: EquipmentFieldSpec) => string[];
  onSaved: () => void;
  readStateFor: (path: string) => PhotoReadState;
  onPhotoUploaded: (uploaded: PhotoUploaded) => void;
  photoWorking: boolean;
  onPhotoWorking: (path: string, working: boolean) => void;
  onBeforeWrite: () => void;
  /** FS-784 — a review is being applied: no upload starts. */
  uploadHold?: boolean;
  /** FS-783 — Remove and Undo; never offered on a slot another Clip holds. */
  removal?: PhotoRemovalCtx | null;
}> = ({
  serial,
  filters,
  reloadNonce,
  clipLinks,
  optionsFor,
  onSaved,
  readStateFor,
  onPhotoUploaded,
  photoWorking,
  onPhotoWorking,
  onBeforeWrite,
  uploadHold = false,
  removal = null,
}) => {
  let grille = 0;
  // The slot fields are read off the row's own keys (trimmed by the server)
  // into `fields`, so the value on file and the change test see the same text.
  const numbered = filters.map((raw) => ({
    doc: {
      ...raw,
      fields: { ...raw.fields, filterType: filterTypeOf(raw), location: filterLocationOf(raw) },
    },
    grilleNumber: filterTypeOf(raw) === 'airReturns' ? (grille += 1) : undefined,
  }));
  const hasOwn = filters.some((doc) => filterRowAccess(doc, serial) === 'own');
  return (
    <>
      {!hasOwn && (
        <p className="text-sm text-muted-foreground">{EQUIPMENT_EDIT_ERRORS.no_current_filter}</p>
      )}
      {numbered.map(({ doc, grilleNumber }) => {
        const access = filterRowAccess(doc, serial);
        const label = filterRowLabel(doc, grilleNumber);
        const common = {
          serial,
          kind: 'filter' as const,
          doc,
          optionsFor,
          onSaved,
          readState: readStateFor(doc.path),
          onPhotoUploaded,
          photoWorking,
          onPhotoWorking: (working: boolean) => onPhotoWorking(doc.path, working),
          onBeforeWrite,
          uploadHold,
          // FS-783: the server takes a removal on this Clip's filters and on
          // a slot with no Clip (contract §4.1 step 6); another Clip's slot is
          // that Clip's to change, so it is not offered there.
          removal: removal ? { ...removal, canRemove: removal.canRemove && access !== 'other' } : null,
        };
        const key = `${doc.path}#${reloadNonce}`;
        if (access === 'own') {
          return (
            <EquipmentDocCard
              key={key}
              {...common}
              title={`Current filter (${label})`}
              specs={[...FILTER_SLOT_FIELDS, ...FILTER_FIELDS]}
            />
          );
        }
        if (access === 'slot' || access === 'older_own') {
          return (
            <EquipmentDocCard
              key={key}
              {...common}
              title={label}
              specs={FILTER_SLOT_FIELDS}
              filterId={docId(doc)}
              photos={false}
              viewPhotos
              note={
                access === 'slot'
                  ? 'No Clip is recorded in this slot. Its type and name can be set here; its size and rating belong to whoever changes that filter.'
                  : 'Older record for this Clip. Only its type and name can be changed here.'
              }
            />
          );
        }
        const other = (doc.device_name ?? '').trim().toLowerCase();
        const otherId = clipLinks?.[other];
        const hintTitle = `Clip ${other} is recorded in this slot. A Clip's filter is edited only from that Clip's own page.`;
        return (
          <EquipmentDocCard
            key={key}
            {...common}
            title={`${label} (Clip ${other})`}
            specs={FILTER_SLOT_FIELDS}
            photos={false}
            locked={{
              title: hintTitle,
              hint:
                otherId != null ? (
                  <>
                    Edit it from{' '}
                    <Link
                      to={`/devices/${otherId}/overview`}
                      className="text-blue-700 underline"
                      title={`Open the overview for device #${otherId}, where this filter can be edited.`}
                    >
                      Clip {other}&apos;s page
                    </Link>
                  </>
                ) : (
                  `Edit it from Clip ${other}'s page`
                ),
            }}
          />
        );
      })}
    </>
  );
};

/**
 * FS-782 — a label read this drawer is following by polling the record: one
 * per upload, keyed by the upload's edit id. Only reads that fill the record
 * on their own are followed this way; a read into a review is followed by its
 * review (below).
 */
interface LegacyWatch {
  path: string;
  state: 'polling' | 'gave_up';
}

/**
 * The request a watch is waiting on, once it has finished. An API that lists
 * the requests is answered only by the row with this edit id; an older one
 * sends only the newest, which is all there is to go on.
 */
function finishedRequest(
  doc: EquipmentDocument | null | undefined,
  editId: string,
): (EquipmentPhotoAnalysis & Partial<EquipmentPhotoAnalysisRequest>) | null {
  if (!doc) return null;
  if (Array.isArray(doc.photo_analysis_requests)) {
    const row = doc.photo_analysis_requests.find((r) => r.edit_id === editId);
    return row?.processed_at ? row : null;
  }
  return doc.photo_analysis?.processed_at ? doc.photo_analysis : null;
}

/** FS-784 — one photo review the drawer knows about and may be following. */
interface ReviewEntry {
  reviewId: string;
  droppedOn: PhotoReviewTab;
  status: PhotoReviewStatus;
  /** FS-787 — a photo review, or catalog values for a model the record carries. */
  kind: PhotoReviewKind;
  /** FS-787 — the model a catalog review names, and what the catalog says it is. */
  catalogModel: string | null;
  catalogType: string | null;
  /** The review's version, which a Dismiss from the banner sends. */
  version: number | null;
  /** How the last Apply ended, so a failed Apply is not mistaken for a failed preparation. */
  lastApplyResult: string | null;
  /** A Dismiss from the banner that the server refused, in words. */
  dismissError: string | null;
  /** FS-787 — the Clip a catalog review can be applied from (its registration's serial). */
  appliesFromSerial: string | null;
  /** FS-787 R4 — whether that Clip is still registered on this system; null when not said. */
  bindingCurrent: boolean | null;
  /** FS-787 R4 — a "Review here" the server refused, in words. */
  rebindError: string | null;
  /** FS-787 R4 — a "Review here" in flight. */
  rebinding: boolean;
  /** Uploaded in this drawer session: the dialog opens by itself when the read lands. */
  mine: boolean;
  typedReason: string | null;
  holdReason: string | null;
  /** The server says the Apply in flight has run past its retry window. */
  stuck: boolean;
  /**
   * Applied, and the device summary refresh has not reported yet: followed a
   * little longer so a failed refresh is still said (Codex F7). No banner.
   */
  awaitingResync: boolean;
  /**
   * Codex N2 — the record's list stopped naming this review: one look at its
   * own status decides whether it really closed, instead of dropping it.
   */
  checking: boolean;
  /** Bumped by Check again (and by a new Apply), which restarts the poll window. */
  pollEpoch: number;
  poll: ReviewPollState;
}

/** An Apply this drawer is following: until its own status ends it, nothing else may. */
/** Statuses no open review can have: a summary carrying one is never shown. */
const CLOSED_REVIEW_STATUSES: ReadonlySet<string> = new Set(['superseded', 'declined']);

const isTracked = (entry: ReviewEntry): boolean => entry.awaitingResync || entry.status === 'applying';

/** A review the drawer has only just heard of. */
const newEntry = (reviewId: string, droppedOn: PhotoReviewTab): ReviewEntry => ({
  reviewId,
  droppedOn,
  status: 'reading',
  kind: 'photo',
  catalogModel: null,
  catalogType: null,
  version: null,
  lastApplyResult: null,
  dismissError: null,
  appliesFromSerial: null,
  bindingCurrent: null,
  rebindError: null,
  rebinding: false,
  mine: false,
  typedReason: null,
  holdReason: null,
  stuck: false,
  awaitingResync: false,
  checking: false,
  pollEpoch: 0,
  poll: IDLE_POLL,
});

/** What one Apply saved, and how its reload and its device summary refresh went. */
interface AppliedNote {
  kind: 'applied';
  reviewId: string;
  /** FS-787 — the ring's words: "From the catalog" for a catalog review. */
  ringLabel?: string;
  targetPath: string;
  fields: string[];
  created: boolean;
  resync: 'pending' | 'ok' | 'failed' | 'unreported';
  reload: 'loading' | 'ok' | 'failed';
  reloadError: string | null;
  /** "the outdoor unit (Air Conditioner)", from the reloaded record. */
  label: string | null;
  /** FS-806 — where the photo ended up, from `decision.placement`; null when not said. */
  placement?: PhotoReviewPlacementDecision | null;
  /** FS-806 — the tab the photo was dropped on, in words ("Indoor unit"). */
  sourceTab?: string;
}

/** A tracked Apply that ended without writing, or whose end could not be confirmed. */
interface EndedNote {
  kind: 'ended';
  reviewId: string;
  text: string;
  code: string | null;
}

/** FS-876 — what a held review's fix did, and how its reload went. */
interface FixedNote {
  kind: 'fixed';
  reviewId: string;
  result: ReviewFixResult;
  reload: 'loading' | 'ok' | 'failed';
  reloadError: string | null;
  /** "the outdoor unit (Lennox)", from the reloaded record. */
  wrongLabel: string | null;
  rightLabel: string | null;
  /**
   * FS-950 — the catalog review the fix asked for on the record the model
   * ended up on: `none` when no job was written, `waiting` while the record
   * is read again for it, then `raised`, `not_raised` (none after the wait)
   * or `unchecked` (no read of the record worked during the wait).
   */
  catalog?: 'none' | 'waiting' | 'raised' | 'not_raised' | 'unchecked';
}

/** FS-950 — how long, and how often, the drawer looks for the catalog review a fix asked for. */
export const FIX_CATALOG_POLL_MS = 3000;
export const FIX_CATALOG_WAIT_MS = 30000;

/**
 * Whether the record lists the catalog review the fix asked for: one for this
 * model on this record. A failure notice, or a review for another model on the
 * same record, is not it.
 */
const catalogReviewOn = (record: EquipmentRecord, path: string, modelId: number | null): boolean =>
  (record.photo_reviews ?? []).some((r) => r.kind === 'catalog' && r.status !== 'failed' && r.source_path === path &&
    (modelId === null || String(r.catalog?.model_id ?? '') === String(modelId)));

/** One line per review in the drawer's saved area (Codex N3: never one shared line). */
type Note = AppliedNote | EndedNote | FixedNote;

/** The saved line after a fix, in words: what moved or was cleared, and where. */
export function fixedNoteText(note: FixedNote): string {
  const r = note.result;
  const count = (n: number) => `${n} ${n === 1 ? 'value' : 'values'}`;
  const wrong = note.wrongLabel ?? `the ${KIND_WORDS[r.wrong.kind]}`;
  const right = note.rightLabel ?? (r.right.created ? `the new ${KIND_WORDS[r.right.kind]} record` : `the ${KIND_WORDS[r.right.kind]}`);
  const main = r.fix === 'moved'
    ? `Moved ${count(r.moved.length)} to ${right}${r.cleared.length > 0 ? ` and cleared ${r.cleared.length} more from ${wrong}` : ''}.`
    : `Cleared ${count(r.cleared.length)} from ${wrong}.`;
  const reload =
    note.reload === 'failed'
      ? ` Couldn't reload the record: ${note.reloadError ?? 'no response'}.`
      : note.reload === 'loading'
        ? ' Reloading the record…'
        : '';
  const photo =
    r.photo?.action === 'moved'
      ? ' The label photo moved with them.'
      : r.photo?.action === 'not_removed'
        ? ` The label photo was not removed: ${
          own(EQUIPMENT_EDIT_ERRORS, r.photo.error?.code) ?? r.photo.error?.message ?? 'no reason was given'
        }`
        : '';
  const sync = r.sync && r.sync.enqueued === false ? ' The device summary will catch up overnight.' : '';
  const catalogRecord = r.catalog_proposal
    ? note.rightLabel ?? `the ${KIND_WORDS[r.catalog_proposal.record.kind]}`
    : null;
  const catalog = !catalogRecord || note.reload === 'loading'
    ? ''
    : note.catalog === 'waiting'
      ? ` Asking the catalog what ${catalogRecord} is missing…`
      : note.catalog === 'raised'
        ? ` The catalog's values are up for review on ${catalogRecord}.`
        : note.catalog === 'not_raised'
          ? ` No catalog review came up for ${catalogRecord}: the catalog may have nothing it is missing.`
          : note.catalog === 'unchecked'
            ? ` Couldn't check for a catalog review on ${catalogRecord}; open the drawer again to see it.`
            : '';
  return main + reload + photo + sync + catalog;
}

/** The record the values went to, named from the record as reloaded. */
function appliedTargetLabel(record: EquipmentRecord, path: string, created: boolean): string {
  const kind = kindOfPath(path);
  const words = kind === 'ac' ? 'outdoor unit' : kind === 'coil' ? 'indoor coil' : 'indoor unit';
  if (created) return `the new ${words} record`;
  const doc = [record.hvac, ...record.ac_systems, ...(record.coils ?? [])].find((d) => d?.path === path);
  const name = String(doc?.fields.name ?? (kind === 'coil' ? doc?.fields.modelNumber : '') ?? '').trim();
  return `the ${words}${name ? ` (${name})` : ''}`;
}

/** The saved line after an Apply, in words. */
function appliedNoteText(note: AppliedNote): string {
  const n = note.fields.length;
  const values = `${n} ${n === 1 ? 'value' : 'values'}`;
  const main =
    note.reload === 'failed'
      ? `${n === 0 ? 'The photo was attached' : `Saved ${values}`}, but couldn't reload the record: ${note.reloadError ?? 'no response'}.`
      : note.reload === 'loading'
        ? `${n === 0 ? 'The photo was attached' : `Saved ${values}`}. Reloading the record…`
        : n === 0
          ? `Attached the photo to ${note.label}.`
          : `Saved ${values} to ${note.label}.`;
  const resync =
    note.resync === 'failed'
      ? ' Saved. The device summary will catch up overnight.'
      : note.resync === 'unreported'
        ? ' The device summary refresh did not report back; it will catch up overnight.'
        : '';
  const placed = placementNoteText(note);
  return main + (placed ? ` ${placed}` : '') + resync;
}

/**
 * FS-806 §8.4 — where the photo went, in words, from the Apply's
 * `decision.placement`. Empty when the Apply said nothing about it (an older
 * server, a catalog review, the photo's own record).
 */
export function placementNoteText(note: AppliedNote): string {
  const p = note.placement;
  if (!p) return '';
  const source = note.sourceTab ?? 'the record it was dropped on';
  const kind = kindOfPath(note.targetPath);
  const target =
    note.label ?? (note.created ? 'the new record' : `the ${kind === 'ac' ? 'outdoor unit' : kind === 'coil' ? 'indoor coil' : 'indoor unit'}`);
  const lines: string[] = [];
  if (p.duplicate_of) {
    lines.push(
      `Same photo as one already on ${target}; its values were re-read.${p.moved ? ` The copy on ${source} was taken off.` : ''}`,
    );
  } else if (p.moved) {
    lines.push(`Photo moved from ${source} to ${target}.`);
  } else if (p.kept_on_source && !p.keep_reason) {
    lines.push(`Photo kept on ${source} and added to ${target}.`);
  }
  // Addendum 2 F7: a keep the server required is said as required, even
  // when the box (checked and disabled) also asked for it.
  if (p.keep_reason === 'source_values_cite_photo') {
    lines.push(`Photo kept on ${source} because values there were read from it.`);
  } else if (p.keep_reason === 'photo_busy') {
    lines.push(`Photo kept on ${source} because another read of it was still running there.`);
  } else if (p.keep_reason === 'source_evidence_unresolved') {
    lines.push(`Photo kept on ${source} because some values there were read from it earlier and can't be traced to another copy.`);
  } else if (p.keep_reason) {
    lines.push(`Photo kept on ${source} because the server required it (${p.keep_reason}).`);
  }
  const n = p.siblings_superseded?.length ?? 0;
  if (n > 0) lines.push(`${n} other ${n === 1 ? 'review' : 'reviews'} of this photo ${n === 1 ? 'was' : 'were'} closed.`);
  return lines.join(' ');
}

/**
 * FS-783 — what one removal or Undo did, and how its reload and the file's
 * protection went. One per action, keyed by `<kind>:<removal id>`.
 */
interface RemovalNote {
  key: string;
  /** FS-806 adds `detached`: a photo taken off one record alone. */
  kind: 'removed' | 'restored' | 'detached';
  removalId: string;
  url: string;
  removed?: PhotoRemovalResult;
  restored?: PhotoUndoResult;
  detached?: { result: PhotoDetachResult; from: string };
  /** The file's protection as last reported: the removal's `file_hold`, or a retry's answer. */
  hold: string;
  /** Addendum 2 F10 — the same, per object, when the server sent it. */
  storage: PhotoStorageState | null;
  /** Addendum 2 F17 — set when the photo had already been put back: the Undo that did it. */
  already: PhotoLastUndo | null;
  reload: 'loading' | 'ok' | 'failed';
  reloadError: string | null;
  retryBusy: boolean;
  retryError: string | null;
}

/** The read stopped by a removal, in one sentence wherever it is reported. */
const READ_STOPPED_TEXT =
  'The label read was stopped when the photo was removed; upload the photo again to read it';

/** An Undo piece left alone, in plain words, or null for one that needs no mention. */
function skippedPieceWords(
  piece: string,
  code: string,
  recordName: string,
  kind: EquipmentTargetKind,
): string | null {
  if (code === 'already_present') return null; // already back; nothing that matters was skipped
  if (code === 'record_gone') return `${recordName} no longer exists`;
  // Addendum 2 F18: every line names the piece it is about, so two stale
  // questions on one record are two lines, each naming its field.
  const conflict = /^fieldConflicts\.(.+)$/.exec(piece);
  const what = conflict
    ? `the question to the homeowner about ${readFieldLabel(kind, conflict[1])}`
    : (
      {
        labelPhotoUrl: "the record's main photo",
        photoUrl: "the record's older photo field",
        labelPhotoUrls: 'the photo list',
        photoUrls: "the filter's photo gallery",
        labelPhotoPreviews: 'the converted copy of the photo',
        labelPhotoAnalyses: 'the label read of the photo',
      } as Record<string, string>
    )[piece] ?? `the ${piece} field`;
  if (code === 'singular_changed') return `${recordName}: ${what} was changed since; it was left as it is`;
  if (code === 'conflict_stale') return `${recordName}: ${what} was not reopened because the value changed`;
  return `${recordName}: ${what} was not put back (${code})`;
}

/** What, if anything, the drawer is polling a review for. */
const pollModeOf = (entry: ReviewEntry): ReviewPollMode | null => {
  if (entry.awaitingResync) return 'resync';
  if (entry.status === 'applying') return entry.stuck ? null : 'applying';
  if (entry.checking) return 'check';
  if (entry.status === 'reading') return 'reading';
  return null;
};

/**
 * Follows one review through its status view. It renders nothing: every poll
 * lives at drawer level — reading, applying and the resync after it — so a
 * review keeps being followed whichever tab is open and whether or not its
 * dialog is, and the write hold is derived from what it reports.
 */
const ReviewPoller: React.FC<{
  serial: string;
  entry: ReviewEntry;
  onStatus: (review: PhotoReview) => void;
  onPollState: (reviewId: string, state: ReviewPollState) => void;
}> = ({ serial, entry, onStatus, onPollState }) => {
  const state = useReviewStatusPoll({
    serial,
    reviewId: entry.reviewId,
    mode: pollModeOf(entry),
    epoch: entry.pollEpoch,
    onStatus,
  });
  useEffect(() => {
    onPollState(entry.reviewId, state);
  }, [entry.reviewId, state, onPollState]);
  return null;
};

/** The line on a tab for one open review: reading, or waiting for the operator. */
const ReviewBanner: React.FC<{
  /** This drawer's Clip, to tell a catalog review bound to another Clip. */
  serial: string;
  entry: ReviewEntry;
  onOpen: (reviewId: string) => void;
  onCheckAgain: (reviewId: string) => void;
  /** FS-787 — close a held or failed catalog review from the banner. */
  onDismiss: (reviewId: string) => void;
  /** FS-787 R4 — take over a catalog review whose Clip left the system. */
  onRebind: (reviewId: string) => void;
}> = ({ serial, entry, onOpen, onCheckAgain, onDismiss, onRebind }) => {
  const { gaveUp } = entry.poll;
  const fetchError = entry.poll.fetchError ?? entry.poll.refused?.message ?? null;
  if (entry.kind === 'catalog') {
    return (
      <CatalogReviewBanner serial={serial} entry={entry} onOpen={onOpen} onDismiss={onDismiss} onRebind={onRebind} />
    );
  }
  if (entry.status === 'reading') {
    return (
      <div data-testid="photo-review-banner" className="rounded-md border border-status-info-border bg-status-info p-3 text-sm text-status-info-foreground" role="status">
        <p>{gaveUp ? 'Still reading. The review will wait on this tab.' : 'Reading a label photo…'}</p>
        {fetchError && (
          <p className="mt-1 text-red-800">{`Couldn't check the photo review: ${fetchError}. Retrying…`}</p>
        )}
        {gaveUp && (
          <Button
            variant="secondary"
            onClick={() => onCheckAgain(entry.reviewId)}
            title="Ask the server whether the label has been read yet"
            className="mt-2"
          >
            Check again
          </Button>
        )}
      </div>
    );
  }
  // Named from the review list's `hold` (or the status view's); a review
  // with no recorded reason is not given a guessed one.
  const held = entry.status === 'held' && entry.holdReason ? heldWords(entry.holdReason) : null;
  return (
    <div
      data-testid="photo-review-banner"
      className="flex items-center justify-between gap-3 rounded-md border border-status-warning-border bg-status-warning p-3 text-sm text-status-warning-foreground"
    >
      <div>
        <p className="font-medium">Label photo waiting for review</p>
        {held && (
          <p className="text-xs" title={`Code: ${held.code}`}>
            {held.text}
          </p>
        )}
        {entry.status === 'applying' && (
          <p className="text-xs">
            {entry.stuck
              ? 'Applying has not finished after 10 minutes; open it to try again.'
              : gaveUp
                ? 'Applying is taking much longer than usual; open it to check again.'
                : 'Applying the chosen values…'}
          </p>
        )}
        {entry.status === 'applying' && entry.poll.refused ? (
          <p className="text-xs text-red-800" title={entry.poll.refused.code ? `Code: ${entry.poll.refused.code}` : undefined}>
            {`Couldn't confirm yet how applying the values ended: ${entry.poll.refused.message} Still checking.`}
            <Button
              variant="secondary"
              size="sm"
              onClick={() => onCheckAgain(entry.reviewId)}
              title="Ask the server again now how applying these values ended"
              className="ml-2"
            >
              Retry
            </Button>
          </p>
        ) : (
          entry.status === 'applying' &&
          fetchError && (
            <p className="text-xs text-red-800">{`Couldn't check the photo review: ${fetchError}. Retrying…`}</p>
          )
        )}
        {entry.status === 'failed' && <p className="text-xs">Something went wrong; open it to see what.</p>}
      </div>
      <Button
        variant="primary"
        onClick={() => onOpen(entry.reviewId)}
        title="Open the values read from this photo"
        className="shrink-0"
      >
        Review
      </Button>
    </div>
  );
};

/**
 * FS-787 — the line on a tab for catalog values: waiting for review (open the
 * dialog), or held / not prepared (Dismiss, right here: there is nothing to
 * apply). Never opened by itself; an operator opens it.
 */
const CatalogReviewBanner: React.FC<{
  serial: string;
  entry: ReviewEntry;
  onOpen: (reviewId: string) => void;
  onDismiss: (reviewId: string) => void;
  onRebind: (reviewId: string) => void;
}> = ({ serial, entry, onOpen, onDismiss, onRebind }) => {
  const { gaveUp, fetchError } = entry.poll;
  const model = entry.catalogModel ?? 'this model';
  // A catalog review is bound to one Clip's registration, and only that
  // Clip's drawer can apply or dismiss it (the server refuses the others).
  // Several Clips can share a system, so another Clip's drawer says where it
  // is waiting instead, with nothing to press but the way there.
  const elsewhere = entry.appliesFromSerial;
  if (elsewhere && elsewhere.toUpperCase() !== serial.toUpperCase()) {
    return (
      <div
        data-testid="catalog-review-banner"
        data-status={entry.status}
        data-elsewhere="true"
        className="rounded-md border border-border bg-muted p-3 text-sm text-muted-foreground"
        role="status"
      >
        <p>
          Catalog values for this system are waiting for review on Clip{' '}
          <Link
            to={`/devices?q=${encodeURIComponent(elsewhere)}`}
            className="font-mono text-blue-700 underline"
            title={`Find Clip ${elsewhere}; the catalog values for model ${model} are reviewed from its equipment editor`}
          >
            {elsewhere}
          </Link>
        </p>
        {/* FS-787 R4: that Clip left the system, so nobody could finish the
            review; this Clip can take it over. */}
        {entry.bindingCurrent === false && (
          <>
            <p className="mt-1">The Clip this review was raised for is no longer on this system.</p>
            <Button
              variant="secondary"
              onClick={() => onRebind(entry.reviewId)}
              disabled={entry.rebinding}
              title="Move this review to this Clip so it can be applied here"
              className="mt-2"
            >
              {entry.rebinding ? 'Moving…' : 'Review here'}
            </Button>
            {entry.rebindError && (
              <p className="mt-1 text-xs text-red-800" role="alert">
                {entry.rebindError}
              </p>
            )}
          </>
        )}
      </div>
    );
  }
  const applyFailed = entry.status === 'failed' && entry.lastApplyResult === 'failed';
  const closed =
    entry.status === 'held' || (entry.status === 'failed' && !applyFailed)
      ? catalogHeldWords({
        status: entry.status,
        holdReason: entry.holdReason,
        model: entry.catalogModel,
        equipmentType: entry.catalogType,
        droppedOn: entry.droppedOn,
      })
      : null;
  if (closed) {
    // FS-876: a model on the wrong record can be fixed from its dialog
    // (cleared here, or moved to the record it belongs on).
    const fixable = entry.status === 'held' && entry.holdReason === 'catalog_component_mismatch';
    return (
      <div
        data-testid="catalog-review-banner"
        data-status={entry.status}
        className="flex items-center justify-between gap-3 rounded-md border border-status-warning-border bg-status-warning p-3 text-sm text-status-warning-foreground"
      >
        <div>
          <p className="font-medium" title={`Code: ${closed.code}`}>
            {closed.text}
          </p>
          {entry.dismissError && (
            <p className="text-xs text-red-800" role="alert">
              {entry.dismissError}
            </p>
          )}
        </div>
        <div className="flex shrink-0 gap-2">
          {fixable && (
            <Button
              variant="primary"
              onClick={() => onOpen(entry.reviewId)}
              title={`See which values of model ${model} can be cleared from this record or moved to the record they belong on`}
            >
              Review
            </Button>
          )}
          <Button
            variant="secondary"
            onClick={() => onDismiss(entry.reviewId)}
            title="Close this notice for good; no values on the record change"
          >
            Dismiss
          </Button>
        </div>
      </div>
    );
  }
  return (
    <div
      data-testid="catalog-review-banner"
      data-status={entry.status}
      className="flex items-center justify-between gap-3 rounded-md border border-status-warning-border bg-status-warning p-3 text-sm text-status-warning-foreground"
    >
      <div>
        <p className="font-medium">Catalog values waiting for review</p>
        {entry.status === 'applying' && (
          <p className="text-xs">
            {entry.stuck
              ? 'Applying has not finished after 10 minutes; open it to try again.'
              : gaveUp
                ? 'Applying is taking much longer than usual; open it to check again.'
                : 'Applying the chosen values…'}
          </p>
        )}
        {entry.status === 'applying' && fetchError && (
          <p className="text-xs text-red-800">{`Couldn't check the review: ${fetchError}. Retrying…`}</p>
        )}
        {applyFailed && <p className="text-xs">Applying did not finish; open it to try again.</p>}
      </div>
      <Button
        variant="primary"
        onClick={() => onOpen(entry.reviewId)}
        title={`Open the values the equipment catalog has for model ${model}`}
        className="shrink-0"
      >
        Review
      </Button>
    </div>
  );
};

/**
 * FS-876 — a dismissed catalog review of this record's model, while the
 * record still carries it: one quiet line on the card, and "Review again",
 * which opens the review again and its dialog. Every review dismissed before
 * the fix existed is one of these.
 */
const DismissedReviewLine: React.FC<{
  entry: DismissedReviewSummary;
  state: { busy: boolean; error: string | null } | undefined;
  onReviewAgain: (entry: DismissedReviewSummary) => void;
}> = ({ entry, state, onReviewAgain }) => {
  const said = catalogHeldWords({
    status: 'held',
    holdReason: entry.hold?.reason,
    model: entry.catalog?.model_display,
    equipmentType: entry.catalog?.equipment_type,
    droppedOn: entry.dropped_on,
  });
  return (
    <p data-testid="dismissed-review" className="mt-1 text-xs text-muted-foreground" title={`Code: ${said?.code ?? 'held'}`}>
      {`${said?.text ?? 'A catalog review of this record'}. It was dismissed.`}{' '}
      <Button
        variant="link"
        size="sm"
        onClick={() => onReviewAgain(entry)}
        disabled={state?.busy === true}
        title="Open this review again to clear these values from this record or move them to the record they belong on; nothing changes until you choose"
      >
        {state?.busy ? 'Opening…' : 'Review again'}
      </Button>
      {state?.error && (
        <span className="block text-red-700" role="alert">
          {state.error}
        </span>
      )}
    </p>
  );
};

/**
 * FS-781 — a drop zone on an empty Outdoor unit or Indoor coil tab.
 *
 * The photo has no record of its own to go on yet, so it is kept on the HVAC
 * system record, carrying a hint of what the operator says it is. The hint is
 * recorded and compared with the read; it never decides the component, and
 * nothing is created until the operator applies the review.
 */
const EmptyTabPhotoZone: React.FC<{
  serial: string;
  hvac: EquipmentDocument;
  hint: PhotoComponentHint;
  /** Held while a review is being applied (Codex F12). */
  disabled: boolean;
  onUploaded: (uploaded: PhotoUploaded) => void;
  onWorking: (working: boolean) => void;
  /** FS-807 — for the duplicate line's review status; the list itself is not shown here. */
  removal?: PhotoRemovalCtx | null;
}> = ({ serial, hvac, hint, disabled, onUploaded, onWorking, removal }) => {
  const [reason, setReason] = useState('');
  const words = hint === 'outdoor_condenser' ? 'outdoor unit' : 'indoor coil';
  const prefix = `${idPrefix(hvac.path)}-${hint}`;
  return (
    <div data-testid={`empty-tab-photo-zone-${hint}`} className="rounded-md border p-4">
      <p className="text-sm text-gray-600">
        {`This system has no ${words} record. Add a photo of its label to start one.`}
      </p>
      <p className="mt-1 text-xs text-muted-foreground">
        {`The photo is kept on the HVAC system record. You choose whether to create the ${words} record after the label is read.`}
      </p>
      <PhotoBlock
        serial={serial}
        kind="hvac"
        doc={hvac}
        prefix={prefix}
        reason={reason}
        disabled={disabled}
        readState={null}
        onUploaded={onUploaded}
        onWorking={onWorking}
        reviewMode
        componentHint={hint}
        showList={false}
        removal={removal ?? null}
      />
      <ReasonField prefix={prefix} value={reason} error={null} disabled={disabled} onChange={setReason} />
    </div>
  );
};

/**
 * FS-783 — what one removal or Undo did, one line per thing that happened,
 * built only from the server's answer: values kept and marked, reviews
 * closed, a read stopped, questions withdrawn, a file that could not be
 * protected, a summary that will catch up later, or an Undo piece left alone.
 */
const RemovalSummary: React.FC<{
  note: RemovalNote;
  record: EquipmentRecord | null;
  onGoToField: (path: string, field: string) => void;
  onRetryProtection: (note: RemovalNote) => void;
  onReload: (key: string) => void;
}> = ({ note, record, onGoToField, onRetryProtection, onReload }) => {
  const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;
  const lines: React.ReactNode[] = [];
  const retryButton = (title: string) => (
    <Button
      variant="secondary"
      size="sm"
      disabled={note.retryBusy}
      onClick={() => onRetryProtection(note)}
      title={title}
      className="ml-2"
    >
      {note.retryBusy ? 'Trying…' : 'Retry protection'}
    </Button>
  );

  if (note.kind === 'removed' && note.removed) {
    const r = note.removed;
    const names = r.records.map((rec) => recordWords(record, rec.path, rec.name));
    lines.push(
      <p key="main" className="font-medium text-green-900">
        {`Photo removed from ${plural(r.records.length, 'record', 'records')}: ${names.join(', ')}. You can undo this until ${dayText(r.undo_until)}.`}
      </p>,
    );
    if (r.evidence_removed.length > 0) {
      const first = r.evidence_removed[0];
      lines.push(
        <p key="evidence">
          {`${plural(r.evidence_removed.length, 'value', 'values')} read from it ${r.evidence_removed.length === 1 ? 'is' : 'are'} kept and marked.`}
          <Button
            variant="link"
            onClick={() => onGoToField(first.path, first.field)}
            title="Go to the first value read from the removed photo"
            className="ml-2"
          >
            Show me
          </Button>
        </p>,
      );
    }
    if (r.uncertain.length > 0) {
      lines.push(
        <p key="uncertain">
          {`${plural(r.uncertain.length, 'value', 'values')} may have come from this photo, but the source was not recorded; check ${r.uncertain.length === 1 ? 'it' : 'them'}.`}
        </p>,
      );
    }
    if (r.reviews_superseded.length > 0) {
      lines.push(
        <p key="reviews">{`${plural(r.reviews_superseded.length, 'review', 'reviews')} waiting for this photo ${r.reviews_superseded.length === 1 ? 'was' : 'were'} closed.`}</p>,
      );
    }
    if (r.requests_closed.length > 0) lines.push(<p key="read">The label read was stopped.</p>);
    if (r.conflicts_withdrawn.length > 0) {
      lines.push(
        <p key="questions">{`${plural(r.conflicts_withdrawn.length, 'question', 'questions')} to the homeowner ${r.conflicts_withdrawn.length === 1 ? 'was' : 'were'} withdrawn.`}</p>,
      );
    }
    const protect = storageWords(note.storage, 'protect');
    if (protect) {
      // Addendum 2 F10: object by object, a missing file said as gone.
      protect.lines.forEach((line) => {
        lines.push(
          <p key={`hold-${line}`} className="text-amber-900" data-testid="removal-storage-line">
            {line}
          </p>,
        );
      });
      if (protect.retry) {
        lines.push(
          <p key="hold-retry">
            {retryButton('Try again to protect the file from deletion; nothing on the records changes')}
          </p>,
        );
      } else if (protect.settled && r.file_hold !== 'set' && note.hold === 'set') {
        lines.push(<p key="hold">The file is now protected from deletion.</p>);
      }
    } else if (note.hold === 'set' && r.file_hold !== 'set') {
      lines.push(<p key="hold">The file is now protected from deletion.</p>);
    } else if (note.hold !== 'set' && note.hold !== 'not_applicable') {
      lines.push(
        <p key="hold" className="text-amber-900" title={`Code: ${note.hold}`}>
          {`The file couldn't be protected from deletion (${note.hold}). It has not been deleted.`}
          {retryButton('Try again to protect the file from deletion; nothing on the records changes')}
        </p>,
      );
    }
    if (r.resync?.failed) lines.push(<p key="resync">Saved. The device summary will catch up overnight.</p>);
  }

  if (note.kind === 'restored' && note.restored) {
    const r = note.restored;
    const back = r.records.filter((rec) => !rec.skipped.some((s) => s.code === 'record_gone'));
    if (note.already) {
      lines.push(
        <p key="already" data-testid="undo-already">
          {`This removal had already been undone${note.already.at ? ` on ${dayText(note.already.at)}` : ''}; this is what that Undo did.`}
        </p>,
      );
    }
    lines.push(
      <p key="main" className="font-medium text-green-900">
        {`Photo put back on ${plural(back.length, 'record', 'records')}.`}
      </p>,
    );
    // Addendum 2 F18: one line per skipped piece, named; only the same piece
    // reported twice is said once.
    const said = new Map<string, string>();
    r.records.forEach((rec) => {
      rec.skipped.forEach((s) => {
        const words = skippedPieceWords(s.piece, s.code, recordWords(record, rec.path), kindOfPath(rec.path));
        if (words) said.set(`${rec.path}|${s.piece}|${s.code}`, words);
      });
    });
    if (r.reviews_restored.length > 0) {
      said.set(
        'reviews-restored',
        `${plural(r.reviews_restored.length, 'review', 'reviews')} waiting for this photo ${r.reviews_restored.length === 1 ? 'was' : 'were'} reopened`,
      );
    }
    r.reviews_not_restored.forEach((rv) => {
      said.set(
        `read|${rv.review_id}`,
        rv.code === 'read_was_stopped'
          ? `The photo review ${rv.review_id} was not reopened: ${READ_STOPPED_TEXT.charAt(0).toLowerCase()}${READ_STOPPED_TEXT.slice(1)}`
          : `The photo review ${rv.review_id} was not reopened (${rv.code})`,
      );
    });
    // A review's id is its request's edit id, so a read already named by its
    // review is the same piece and is not said again.
    r.reads_not_restored.forEach((id) => {
      if (said.has(`read|${id}`)) return;
      said.set(`read|${id}`, `The label read ${id} was stopped when the photo was removed; upload the photo again to read it`);
    });
    Array.from(said.entries()).forEach(([key, words]) => {
      lines.push(
        <p key={key} data-testid="undo-skipped">
          {`${words}.`}
        </p>,
      );
    });
    const release = storageWords(note.storage, 'release');
    if (release) {
      release.lines.forEach((line) => {
        lines.push(
          <p key={`hold-${line}`} className="text-amber-900" data-testid="removal-storage-line">
            {line}
          </p>,
        );
      });
      if (release.retry) {
        lines.push(
          <p key="hold-retry">
            {retryButton("Try again to release the file's protection; nothing on the records changes")}
          </p>,
        );
      } else if (release.settled && r.file_hold_released === false) {
        lines.push(<p key="hold">The file's protection is now released.</p>);
      }
    } else if (note.hold === 'release_failed' || note.hold === 'failed' || note.hold === 'partial') {
      lines.push(
        <p key="hold" className="text-amber-900" title={`Code: ${note.hold}`}>
          The file is still protected from deletion; that does no harm, and Retry protection releases it.
          {retryButton("Try again to release the file's protection; nothing on the records changes")}
        </p>,
      );
    }
    if (r.resync?.failed) lines.push(<p key="resync">Saved. The device summary will catch up overnight.</p>);
    // Addendum 3 N3: an Undo whose answer was stored before its release and
    // refresh finished. The photo is back; those two have not reported.
    const unfinished =
      r.resync === undefined || r.file_hold_released === undefined || note.already?.status === 'committed';
    if (unfinished) {
      lines.push(
        <p key="finishing" data-testid="undo-finishing" className="text-gray-700">
          Finishing… the file&apos;s protection release and the device summary refresh had not reported yet.
          <Button
            variant="secondary"
            size="sm"
            onClick={() => onReload(note.key)}
            title="Load the record again to see how the release and the refresh ended; anything typed on the cards is kept"
            className="ml-2"
          >
            Reload
          </Button>
        </p>,
      );
    }
  }

  if (note.kind === 'detached' && note.detached) {
    const { result, from } = note.detached;
    const to = result.to ? recordWords(record, result.to) : null;
    lines.push(
      <p key="main" className="font-medium text-green-900">
        {`Photo taken off ${recordWords(record, result.from || from)}${to ? `; it stays on ${to}` : ''}.`}
      </p>,
    );
    const closed = result.reviews_superseded?.length ?? 0;
    if (closed > 0) {
      lines.push(
        <p key="reviews">{`${plural(closed, 'other review', 'other reviews')} of this photo ${closed === 1 ? 'was' : 'were'} closed.`}</p>,
      );
    }
    if (result.resync?.failed) lines.push(<p key="resync">Saved. The device summary will catch up overnight.</p>);
  }

  if (note.retryError) {
    lines.push(
      <p key="retry-error" className="text-red-800" role="alert">
        {`Couldn't retry the protection: ${note.retryError}`}
      </p>,
    );
  }
  if (note.reload === 'failed') {
    lines.push(
      <p key="reload" className="text-amber-900">
        {`Couldn't reload the record: ${note.reloadError ?? 'no response'}.`}
        <Button
          variant="secondary"
          size="sm"
          onClick={() => onReload(note.key)}
          title="Load the record again to see it as it is now; anything typed on the cards is kept"
          className="ml-2"
        >
          Reload
        </Button>
      </p>,
    );
  }

  return (
    <div data-testid="photo-removal-note" data-kind={note.kind} className="space-y-0.5 text-gray-800">
      {lines}
    </div>
  );
};

/** Hover help for the tab buttons, which had none. */
const TAB_TITLES: Record<EquipmentTargetKind, string> = {
  hvac: 'Edit the indoor unit record: the furnace or air handler',
  ac: 'Edit the outdoor unit records: the A/C or heat pump',
  coil: 'Edit the indoor coil (A-coil) records',
  filter: 'Edit the filter records, or record a filter change',
};

/** How long the fields a photo review wrote stay ringed. */
const PHOTO_HIGHLIGHT_MS = 15000;

const EditEquipmentDrawer: React.FC<EditEquipmentDrawerProps> = ({
  device,
  onClose,
  onSaved,
  initialTab,
  focusField,
  onWritten,
  projectionStamp,
  clipLinks,
}) => {
  const serial = device.name;
  /** FS-593 — the caller's projection stamp, read just before the last write was sent. */
  const writeBaseline = useRef<string | null>(null);
  const handleBeforeWrite = useCallback(() => {
    writeBaseline.current = projectionStamp ? projectionStamp() : null;
  }, [projectionStamp]);
  const [record, setRecord] = useState<EquipmentRecord | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [brands, setBrands] = useState<string[]>([]);
  const [tab, setTab] = useState<EquipmentTargetKind>(initialTab ?? 'hvac');
  /**
   * FS-784 — the tabs opened so far. A tab stays mounted (hidden) once it has
   * been opened, so a half-typed correction on one card survives a switch to
   * another tab and the reload an Apply causes.
   */
  const [visited, setVisited] = useState<Set<EquipmentTargetKind>>(
    () => new Set([initialTab ?? 'hvac']),
  );
  const [saved, setSaved] = useState(false);
  const [reloadNonce, setReloadNonce] = useState(0);
  /** FS-782 — every legacy label read being followed, by its upload's edit id. */
  const [watches, setWatches] = useState<Record<string, LegacyWatch>>({});
  const watchesRef = useRef(watches);
  watchesRef.current = watches;
  const pollAttempts = useRef<Record<string, number>>({});
  /** Which records have a photo on its way up right now, by document path. */
  const [photoUploading, setPhotoUploading] = useState<Record<string, boolean>>({});
  /** FS-784 — the open photo reviews on this system, by review id. */
  const [reviews, setReviews] = useState<Record<string, ReviewEntry>>({});
  const reviewsRef = useRef(reviews);
  reviewsRef.current = reviews;
  const [openReview, setOpenReview] = useState<string | null>(null);
  /** Reviews whose dialog has already opened by itself, so it never does twice. */
  const autoOpened = useRef<Set<string>>(new Set());
  const openReviewRef = useRef(openReview);
  openReviewRef.current = openReview;
  /** The newest status view per review, handed to its dialog. */
  const [latestViews, setLatestViews] = useState<Record<string, PhotoReview>>({});
  /** Reviews whose `applied` has been acted on, so it happens once. */
  const appliedHandled = useRef<Set<string>>(new Set());
  /** FS-784 — the fields just written by a photo, ringed on their card. */
  const [highlight, setHighlight] = useState<
    { path: string; fields: string[]; nonce: number; label?: string } | null
  >(null);
  /** The card to bring into view once it has rendered. */
  const [scrollTarget, setScrollTarget] = useState<string | null>(null);
  /** What the last Apply saved, and how its reload and resync went. */
  const [notes, setNotes] = useState<Note[]>([]);
  /** FS-783 — what each removal and Undo in this session did. */
  const [removalNotes, setRemovalNotes] = useState<RemovalNote[]>([]);
  /** FS-783 — the removals this session made, by removal id. */
  const [sessionRemovals, setSessionRemovals] = useState<Record<string, SessionRemoval>>({});
  /** FS-783 — the removals this session undid, by removal id. */
  const [undoneRemovals, setUndoneRemovals] = useState<Set<string>>(new Set());
  /**
   * Addendum 2 F10 — how each review's read ended, kept after the review is
   * dropped (a review closed straight from reading leaves no other trace).
   */
  const [reviewReads, setReviewReads] = useState<Record<string, DuplicateRead>>({});
  /** FS-806 — photos taken off one record alone this session, as `photoKey(path, url)`. */
  const [detachedKeys, setDetachedKeys] = useState<Map<string, number>>(new Map());
  /**
   * FS-806 addendum 2 F8 — a Detach's hiding is only until the server is
   * heard from. Each Detach gets a number; a record read that STARTED after
   * it and came back is the server's word on that record, so its key goes
   * (a later Apply may have put the photo back). A read that started before
   * it, or failed, leaves the photo hidden.
   */
  const detachSeq = useRef(0);
  /** FS-806 — photos an Apply moved off the record they were dropped on, as `photoKey(path, url)`. */
  const [movedOff, setMovedOff] = useState<Set<string>>(new Set());
  /** FS-783 — a field to put the cursor in once its tab has rendered. */
  const [pendingFocus, setPendingFocus] = useState<{ path: string; field: string } | null>(null);
  const timers = useRef<number[]>([]);
  /** FS-950 — the fixes whose catalog review is being looked for, so a retried reload starts no second wait. */
  const catalogFollowed = useRef<Set<string>>(new Set());
  const panelRef = useRef<HTMLDivElement | null>(null);
  /** A requested field is put in front of the operator once, not on every reload. */
  const focused = useRef(false);

  const showTab = useCallback((next: EquipmentTargetKind) => {
    setTab(next);
    setVisited((prev) => (prev.has(next) ? prev : new Set(prev).add(next)));
  }, []);

  /**
   * Every record read in the drawer: a confirmed answer retires the Detach
   * hiding it supersedes (F8). The record is put on screen in the same step,
   * so a photo never shows from the old record between the two.
   */
  const fetchRecord = useCallback(async (): Promise<EquipmentRecord> => {
    const startedAt = detachSeq.current;
    const fresh = await getEquipment(serial);
    setRecord(fresh);
    setDetachedKeys((prev) => {
      if (prev.size === 0) return prev;
      const next = new Map(Array.from(prev).filter(([, seq]) => seq > startedAt));
      return next.size === prev.size ? prev : next;
    });
    return fresh;
  }, [serial]);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      await fetchRecord();
    } catch (err) {
      setRecord(null);
      setLoadError(equipmentErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [fetchRecord]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    let cancelled = false;
    getFilterBrands()
      .then((list) => {
        if (!cancelled) setBrands(list);
      })
      .catch(() => {
        // Suggestions are a convenience over a free-text box; the box works
        // without them, so a failure here is not worth an error on the form.
      });
    return () => {
      cancelled = true;
    };
  }, []);

  /** False once the drawer has closed: a wait whose read was in flight then stops there. */
  const open = useRef(true);
  useEffect(
    () => () => {
      open.current = false;
      timers.current.forEach((t) => window.clearTimeout(t));
    },
    [],
  );

  /**
   * Plan 123 — the operator was sent here for one named field, so put it in
   * front of them. Once only: a reload after a save must not yank the page
   * back to a box they have finished with.
   */
  useEffect(() => {
    if (!focusField || loading || !record || focused.current) return;
    const box = panelRef.current?.querySelector<HTMLElement>(`[id$="-${focusField}"]`);
    if (!box) return;
    focused.current = true;
    box.scrollIntoView?.({ block: 'center' });
    box.focus?.();
  }, [focusField, loading, record]);

  /**
   * FS-784 §8.6 — every record load says which reviews are open. Each is
   * merged in, keeping what this session knows that the list does not (that
   * it uploaded the photo, and the reason it typed).
   */
  useEffect(() => {
    const list = record?.photo_reviews;
    // An API from before FS-784 sends no list; that says nothing either way.
    if (!Array.isArray(list)) return;
    // FS-787 — a closed document is never a review to show, whatever the list
    // says. The Functions keep a closed catalog failure notice
    // (`…_failed`, superseded) on every system as a revision watermark; the
    // API lists only open reviews, and this holds even if one slipped in.
    const shown = list.filter((summary) => !CLOSED_REVIEW_STATUSES.has(summary.status));
    setReviews((prev) => {
      const listed = new Set(shown.map((summary) => summary.review_id));
      const next: Record<string, ReviewEntry> = {};
      // A review the list does not name is not dropped on that say-so (Codex
      // N2): a tracked Apply keeps being followed by its own poll, this
      // session's upload whose read is starting stays, and anything else gets
      // one look at its own status, which decides.
      Object.values(prev).forEach((entry) => {
        if (listed.has(entry.reviewId)) return;
        if (isTracked(entry) || (entry.mine && entry.status === 'reading')) {
          next[entry.reviewId] = entry;
          return;
        }
        next[entry.reviewId] = entry.checking
          ? entry
          : { ...entry, checking: true, pollEpoch: entry.pollEpoch + 1, poll: IDLE_POLL };
      });
      shown.forEach((summary) => {
        const had = prev[summary.review_id];
        // A tracked Apply keeps its own state: the list may have been read
        // before the Apply started, and must never roll it back (Codex N2).
        if (had && isTracked(had)) {
          next[summary.review_id] = had;
          return;
        }
        next[summary.review_id] = {
          ...(had ?? newEntry(summary.review_id, summary.dropped_on)),
          droppedOn: summary.dropped_on,
          status: summary.status,
          holdReason: summary.hold?.reason ?? had?.holdReason ?? null,
          stuck: summary.stuck === true,
          checking: false,
          kind: reviewKindOf(summary),
          catalogModel: summary.catalog?.model_display ?? had?.catalogModel ?? null,
          catalogType: summary.catalog?.equipment_type ?? had?.catalogType ?? null,
          version: typeof summary.version === 'number' ? summary.version : had?.version ?? null,
          lastApplyResult: summary.last_apply?.result ?? null,
          appliesFromSerial: summary.applies_from_serial ?? had?.appliesFromSerial ?? null,
          bindingCurrent: typeof summary.binding?.current === 'boolean' ? summary.binding.current : null,
        };
      });
      return next;
    });
  }, [record]);

  /** Bring a card into view once it is on screen (a new child appears only after the reload). */
  useEffect(() => {
    if (!scrollTarget) return;
    const card = Array.from(
      panelRef.current?.querySelectorAll<HTMLElement>('[data-card-path]') ?? [],
    ).find((el) => el.getAttribute('data-card-path') === scrollTarget);
    if (!card) return;
    card.scrollIntoView?.({ block: 'start' });
    setScrollTarget(null);
  }, [scrollTarget, record, tab]);

  /** Ring the fields a photo wrote on one card, and let the ring go after a while. */
  const showWritten = useCallback(
    (path: string, fields: string[], label?: string) => {
      const nonce = Date.now() + Math.random();
      showTab(kindOfPath(path));
      setHighlight({ path, fields, nonce, label });
      setScrollTarget(path);
      timers.current.push(
        window.setTimeout(
          () => setHighlight((h) => (h && h.nonce === nonce ? null : h)),
          PHOTO_HIGHLIGHT_MS,
        ),
      );
    },
    [showTab],
  );

  /**
   * A write lands in Firestore first and reaches the console's projection
   * through the sync queue, so the device detail behind this page is briefly
   * the old one. FS-952: a caller that follows the projection is handed the
   * stamp from before the write and reloads once that stamp has moved,
   * usually a second or two later. Only a caller that cannot follow it gets
   * the timed reloads.
   */
  const afterWrite = useCallback(() => {
    if (onWritten) {
      onWritten(writeBaseline.current);
      return;
    }
    REFRESH_DELAYS_MS.forEach((delay) => {
      timers.current.push(window.setTimeout(() => onSaved?.(), delay));
    });
  }, [onSaved, onWritten]);

  const handleSaved = useCallback(() => {
    afterWrite();
    setSaved(true);
    setReloadNonce((n) => n + 1);
    load();
    // The write is already in Firestore, which is what the enrichment readers
    // (the Photos tab among them) read — so they are told now, as they are
    // for a stored photo. A recorded filter change is the case FS-539 needs:
    // it moves the device onto a new filter record, and the old one's photos
    // move to their own section.
    refreshDeviceEnrichment();
  }, [afterWrite, load]);

  /** FS-783 — change one removal note. */
  const patchRemovalNote = useCallback((key: string, change: (note: RemovalNote) => RemovalNote) => {
    setRemovalNotes((prev) => prev.map((n) => (n.key === key ? change(n) : n)));
  }, []);

  /**
   * FS-783 §8.3 — load the record again after a removal or Undo, in place:
   * never `load()` and never a new `reloadNonce`, either of which would
   * remount the cards and throw away what is typed on the others.
   */
  const reloadAfterRemoval = useCallback(
    async (key: string) => {
      patchRemovalNote(key, (n) => ({ ...n, reload: 'loading', reloadError: null }));
      try {
        await fetchRecord();
        patchRemovalNote(key, (n) => ({ ...n, reload: 'ok' }));
      } catch (err) {
        patchRemovalNote(key, (n) => ({ ...n, reload: 'failed', reloadError: pollErrorText(err) }));
      }
    },
    [patchRemovalNote, serial],
  );

  /** FS-783 — a reload in place that nothing else reports on: a notice dismissed, a protection retried. */
  const [plainReloadError, setPlainReloadError] = useState<string | null>(null);
  const reloadInPlace = useCallback(async () => {
    setPlainReloadError(null);
    try {
      await fetchRecord();
    } catch (err) {
      setPlainReloadError(pollErrorText(err));
    }
  }, [serial]);

  /** A write from a photo tile landed: tell the readers beside the drawer, as a save does. */
  const afterPhotoWrite = useCallback(() => {
    afterWrite();
    refreshDeviceEnrichment();
  }, [afterWrite]);

  const handlePhotoRemoved = useCallback(
    (result: PhotoRemovalResult, input: { reason: string; reasonKind: PhotoRemovalReasonKind | null }) => {
      const key = `removed:${result.removal_id}`;
      setSessionRemovals((prev) => ({
        ...prev,
        [result.removal_id]: { result, reason: input.reason, reasonKind: input.reasonKind, at: new Date().toISOString() },
      }));
      setUndoneRemovals((prev) => {
        if (!prev.has(result.removal_id)) return prev;
        const next = new Set(prev);
        next.delete(result.removal_id);
        return next;
      });
      setRemovalNotes((prev) => [
        ...prev.filter((n) => n.key !== key),
        {
          key,
          kind: 'removed',
          removalId: result.removal_id,
          url: result.url,
          removed: result,
          hold: result.file_hold,
          storage: result.file_objects ? { hold: null, objects: result.file_objects, at: null } : null,
          already: null,
          reload: 'loading',
          reloadError: null,
          retryBusy: false,
          retryError: null,
        },
      ]);
      afterPhotoWrite();
      void reloadAfterRemoval(key);
    },
    [afterPhotoWrite, reloadAfterRemoval],
  );

  const handlePhotoRestored = useCallback(
    (result: PhotoUndoResult, entry: EquipmentRemovedPhoto, already?: PhotoLastUndo | null) => {
      const key = `restored:${result.removal_id}`;
      setUndoneRemovals((prev) => new Set(prev).add(result.removal_id));
      setRemovalNotes((prev) => [
        // The removal's own note is finished with: the photo is back.
        ...prev.filter((n) => n.key !== key && n.key !== `removed:${result.removal_id}`),
        {
          key,
          kind: 'restored',
          removalId: result.removal_id,
          url: entry.url,
          restored: result,
          // N3: absent means not reported yet, which is not a failure.
          hold:
            result.file_hold_released === undefined ? 'pending' : result.file_hold_released ? 'released' : 'release_failed',
          storage: result.file_objects ? { hold: null, objects: result.file_objects, at: null } : null,
          already: already ?? null,
          reload: 'loading',
          reloadError: null,
          retryBusy: false,
          retryError: null,
        },
      ]);
      afterPhotoWrite();
      void reloadAfterRemoval(key);
    },
    [afterPhotoWrite, reloadAfterRemoval],
  );

  /**
   * FS-806 — a photo was taken off one record alone: hide it there at once,
   * say where it stays, and load the record again in place.
   */
  const handlePhotoDetached = useCallback(
    (result: PhotoDetachResult, input: { from: string; url: string }) => {
      const id = newEditId();
      const key = `detached:${id}`;
      detachSeq.current += 1;
      const seq = detachSeq.current;
      setDetachedKeys((prev) => new Map(prev).set(photoKey(input.from, input.url), seq));
      setRemovalNotes((prev) => [
        ...prev,
        {
          key,
          kind: 'detached',
          removalId: id,
          url: input.url,
          detached: { result, from: input.from },
          hold: 'not_applicable',
          storage: null,
          already: null,
          reload: 'loading',
          reloadError: null,
          retryBusy: false,
          retryError: null,
        },
      ]);
      afterPhotoWrite();
      void reloadAfterRemoval(key);
    },
    [afterPhotoWrite, reloadAfterRemoval],
  );

  /** §4.3 — set a protection that failed, or release one Undo could not. */
  const handleRetryProtection = useCallback(
    async (note: RemovalNote) => {
      patchRemovalNote(note.key, (n) => ({ ...n, retryBusy: true, retryError: null }));
      try {
        const storage = await retryPhotoProtection(serial, note.removalId, { url: note.url });
        patchRemovalNote(note.key, (n) => ({ ...n, retryBusy: false, hold: storage.hold ?? n.hold, storage }));
      } catch (err) {
        patchRemovalNote(note.key, (n) => ({ ...n, retryBusy: false, retryError: photoRemovalErrorMessage(err) }));
      }
    },
    [patchRemovalNote, serial],
  );

  /** FS-783 — take the operator to a flagged value: its tab, then its box. */
  const goToField = useCallback(
    (path: string, field: string) => {
      showTab(kindOfPath(path));
      setPendingFocus({ path, field });
    },
    [showTab],
  );

  useEffect(() => {
    if (!pendingFocus) return;
    const box = panelRef.current?.querySelector<HTMLElement>(
      `[id="${idPrefix(pendingFocus.path)}-${pendingFocus.field}"]`,
    );
    if (box) {
      box.scrollIntoView?.({ block: 'center' });
      box.focus?.();
    } else {
      // A field with no box on its card: the card itself is the nearest thing.
      setScrollTarget(pendingFocus.path);
    }
    setPendingFocus(null);
  }, [pendingFocus, record, tab]);

  /**
   * A label read is a queued job, so the record the drawer is showing is the
   * one from before it ran. Reload it until each read this drawer started
   * reports itself finished — each by its own edit id (FS-782) — then leave
   * it alone. One GET per tick serves every read in flight.
   *
   * Deliberately not `load()`: that puts the drawer back into its loading
   * state, which unmounts the cards and throws away whatever the operator has
   * typed. This replaces the record under the same cards instead, so the
   * values on file catch up and the half-finished correction above them does
   * not move.
   */
  const anyPolling = Object.values(watches).some((w) => w.state === 'polling');
  useEffect(() => {
    if (!anyPolling) return undefined;
    let cancelled = false;
    const id = window.setInterval(async () => {
      let fresh: EquipmentRecord | null = null;
      try {
        fresh = await fetchRecord();
      } catch {
        // A poll that fails is not the upload failing — the photo is stored
        // and the read is still running. Try again on the next tick.
      }
      if (cancelled) return;
      const docs = fresh ? [fresh.hvac, ...fresh.ac_systems, ...fresh.filters] : [];
      const done: string[] = [];
      const gaveUp: string[] = [];
      let routed: { path: string; fields: string[] } | null = null;
      Object.entries(watchesRef.current).forEach(([editId, watch]) => {
        if (watch.state !== 'polling') return;
        pollAttempts.current[editId] = (pollAttempts.current[editId] ?? 0) + 1;
        const request = finishedRequest(
          docs.find((doc) => doc?.path === watch.path),
          editId,
        );
        if (request) {
          done.push(editId);
          // The read put its facts on another record (a coil or an A/C
          // child): that is where the operator needs to look.
          const to = request.routed_to;
          if (to && to !== watch.path) routed = { path: to, fields: request.fields_updated ?? [] };
          return;
        }
        if (pollAttempts.current[editId] >= PHOTO_POLL_MAX) gaveUp.push(editId);
      });
      if (done.length === 0 && gaveUp.length === 0) return;
      setWatches((prev) => {
        const next = { ...prev };
        done.forEach((editId) => delete next[editId]);
        gaveUp.forEach((editId) => {
          if (next[editId]) next[editId] = { ...next[editId], state: 'gave_up' };
        });
        return next;
      });
      if (done.length > 0) {
        // The read wrote fields onto the same records the rest of the page
        // is showing, so they read again too.
        refreshDeviceEnrichment();
      }
      if (routed) showWritten((routed as { path: string }).path, (routed as { fields: string[] }).fields);
    }, PHOTO_POLL_MS);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, [anyPolling, serial, showWritten]);

  /**
   * A stored photo is already a change to the app's records — the Photos tab
   * and the photo counts beside this drawer read those, not this drawer's own
   * GET, so they are told rather than left showing what loaded with the page.
   * A read into a review is followed by its review; a read that fills the
   * record itself is followed by its edit id.
   */
  const handlePhotoUploaded = useCallback((uploaded: PhotoUploaded) => {
    refreshDeviceEnrichment();
    if (uploaded.reviewId) {
      const reviewId = uploaded.reviewId;
      setReviews((prev) => ({
        ...prev,
        [reviewId]: {
          ...newEntry(reviewId, uploaded.droppedOn ?? 'hvac'),
          mine: true,
          typedReason: uploaded.typedReason,
        },
      }));
      return;
    }
    if (uploaded.analysis !== 'requested') return;
    setWatches((prev) => {
      const next: Record<string, LegacyWatch> = {};
      // A read this drawer gave up on is superseded by a new one on the same
      // record; reads still running elsewhere are untouched.
      Object.entries(prev).forEach(([editId, watch]) => {
        if (watch.path === uploaded.path && watch.state === 'gave_up') return;
        next[editId] = watch;
      });
      next[uploaded.editId] = { path: uploaded.path, state: 'polling' };
      return next;
    });
  }, []);

  const handlePhotoWorking = useCallback((path: string, working: boolean) => {
    setPhotoUploading((prev) => ({ ...prev, [path]: working }));
  }, []);

  const dropReview = useCallback((reviewId: string) => {
    setReviews((prev) => {
      if (!prev[reviewId]) return prev;
      const next = { ...prev };
      delete next[reviewId];
      return next;
    });
    // FS-787 R5: a catalog review keeps its id across versions, so a later
    // version must not be handed this one's last status view.
    setLatestViews((prev) => {
      if (!prev[reviewId]) return prev;
      const next = { ...prev };
      delete next[reviewId];
      return next;
    });
  }, []);

  /** Add a note, or replace this review's note. */
  const putNote = useCallback((note: Note) => {
    setNotes((prev) => {
      const at = prev.findIndex((n) => n.reviewId === note.reviewId);
      if (at === -1) return [...prev, note];
      const next = [...prev];
      next[at] = note;
      return next;
    });
  }, []);

  /** Change this review's applied note, if it has one. */
  const patchApplied = useCallback((reviewId: string, change: (note: AppliedNote) => AppliedNote) => {
    setNotes((prev) =>
      prev.map((n) => (n.reviewId === reviewId && n.kind === 'applied' ? change(n) : n)),
    );
  }, []);

  /**
   * FS-787 — Dismiss on a held or failed catalog banner: the review's own
   * decline route, which writes nothing to the record. A refusal is said on
   * the banner; the banner stays until the server has closed the review.
   */
  /**
   * FS-787 R4 — "Review here": move a catalog review whose Clip left the
   * system to this Clip, then load the record again in place (never `load()`,
   * which would unmount the cards and their unsaved edits), so the banner
   * becomes this Clip's own and its Review can apply it. No confirm dialog;
   * a refusal is said on the banner.
   */
  const handleRebind = useCallback(
    async (reviewId: string) => {
      const patch = (change: Partial<ReviewEntry>) =>
        setReviews((prev) => (prev[reviewId] ? { ...prev, [reviewId]: { ...prev[reviewId], ...change } } : prev));
      patch({ rebinding: true, rebindError: null });
      try {
        await rebindCatalogReview(serial, reviewId);
      } catch (err) {
        patch({ rebinding: false, rebindError: `Couldn't move it to this Clip: ${equipmentErrorMessage(err)}` });
        return;
      }
      try {
        await fetchRecord();
        patch({ rebinding: false });
      } catch (err) {
        patch({ rebinding: false, rebindError: `Moved to this Clip, but couldn't reload the record: ${pollErrorText(err)}` });
      }
    },
    [serial],
  );

  const handleDismiss = useCallback(
    async (reviewId: string) => {
      const entry = reviewsRef.current[reviewId];
      if (!entry || entry.version === null) return;
      setReviews((prev) => (prev[reviewId] ? { ...prev, [reviewId]: { ...prev[reviewId], dismissError: null } } : prev));
      try {
        await declinePhotoReview(serial, reviewId, { edit_id: newEditId(), version: entry.version });
        dropReview(reviewId);
      } catch (err) {
        const said = `Couldn't dismiss it: ${equipmentErrorMessage(err)}`;
        setReviews((prev) => (prev[reviewId] ? { ...prev, [reviewId]: { ...prev[reviewId], dismissError: said } } : prev));
      }
    },
    [serial, dropReview],
  );

  /** FS-876 — "Review again" in flight or refused, per dismissed review. */
  const [reopening, setReopening] = useState<Record<string, { busy: boolean; error: string | null }>>({});

  /**
   * FS-876 — "Review again" on a dismissed held review: open it again on the
   * server, load the record in place (its banner then shows on the tab), and
   * open its dialog. A refusal is said on the card's line.
   */
  const handleReviewAgain = useCallback(
    async (entry: DismissedReviewSummary) => {
      const id = entry.review_id;
      const settle = (state: { busy: boolean; error: string | null } | null) =>
        setReopening((prev) => {
          const next = { ...prev };
          if (state) next[id] = state;
          else delete next[id];
          return next;
        });
      settle({ busy: true, error: null });
      try {
        await reopenCatalogReview(serial, id, entry.version);
      } catch (err) {
        settle({ busy: false, error: `Couldn't open it again: ${equipmentErrorMessage(err)}` });
        return;
      }
      try {
        await fetchRecord();
        settle(null);
        showTab(kindOfPath(entry.source_path));
        setOpenReview(id);
      } catch (err) {
        settle({ busy: false, error: `Opened again, but couldn't reload the record: ${pollErrorText(err)}` });
      }
    },
    [serial, fetchRecord, showTab],
  );

  /** Change this review's fix note, if it has one. */
  const patchFixed = useCallback((reviewId: string, change: (note: FixedNote) => FixedNote) => {
    setNotes((prev) => prev.map((n) => (n.reviewId === reviewId && n.kind === 'fixed' ? change(n) : n)));
  }, []);

  /**
   * FS-876 — load the record again after a fix, in place, and ring what
   * changed: the moved values on the record they went to, or the cleared
   * ones where they were.
   */
  /**
   * FS-950 — the fix wrote a catalog job; the Functions raise the review a
   * second or two later. Read the record again every 3 s until it lists an
   * open catalog review on that record, for 30 s at most, so it shows up on
   * its card without the drawer being opened again. In place, as the fix's
   * own reload; a failed read just waits for the next one.
   */
  const followFixCatalog = useCallback(
    (reviewId: string, path: string, modelId: number | null, record: EquipmentRecord) => {
      if (catalogReviewOn(record, path, modelId)) {
        patchFixed(reviewId, (n) => ({ ...n, catalog: 'raised' }));
        return;
      }
      const deadline = Date.now() + FIX_CATALOG_WAIT_MS;
      let read = false;
      const tick = () => {
        timers.current.push(window.setTimeout(async () => {
          let fresh: EquipmentRecord | null = null;
          try {
            fresh = await fetchRecord();
            read = true;
          } catch {
            fresh = null;
          }
          // Closed while the read was out: nothing more to say or read.
          if (!open.current) return;
          if (fresh && catalogReviewOn(fresh, path, modelId)) {
            patchFixed(reviewId, (n) => ({ ...n, catalog: 'raised' }));
          } else if (Date.now() >= deadline) {
            patchFixed(reviewId, (n) => ({ ...n, catalog: read ? 'not_raised' : 'unchecked' }));
          } else {
            tick();
          }
        }, FIX_CATALOG_POLL_MS));
      };
      tick();
    },
    [fetchRecord, patchFixed],
  );

  const reloadAfterFix = useCallback(
    async (note: FixedNote) => {
      patchFixed(note.reviewId, (n) => ({ ...n, reload: 'loading', reloadError: null }));
      try {
        const fresh = await fetchRecord();
        const r = note.result;
        patchFixed(note.reviewId, (n) => ({
          ...n,
          reload: 'ok',
          wrongLabel: appliedTargetLabel(fresh, r.wrong.path, false),
          rightLabel: appliedTargetLabel(fresh, r.right.path, r.right.created),
        }));
        if (r.fix === 'moved') showWritten(r.right.path, r.moved, 'Moved here');
        else showWritten(r.wrong.path, r.cleared, 'Cleared');
        // Once per fix: a retried reload does not start a second wait.
        if (r.catalog_proposal && note.catalog === 'waiting' && !catalogFollowed.current.has(note.reviewId)) {
          catalogFollowed.current.add(note.reviewId);
          followFixCatalog(note.reviewId, r.catalog_proposal.record.path, r.catalog_proposal.model_id ?? null, fresh);
        }
      } catch (err) {
        patchFixed(note.reviewId, (n) => ({ ...n, reload: 'failed', reloadError: pollErrorText(err) }));
      }
    },
    [fetchRecord, followFixCatalog, patchFixed, showWritten],
  );

  /**
   * FS-876 — a held review's fix landed: close the dialog and the banner,
   * say what moved or was cleared, record a removed photo as any removal is
   * recorded (its note carries the Undo), and reload the record in place.
   */
  const handleReviewFixed = useCallback(
    (reviewId: string, result: ReviewFixResult, reason: string) => {
      setOpenReview(null);
      dropReview(reviewId);
      afterWrite();
      const note: FixedNote = {
        kind: 'fixed', reviewId, result, reload: 'loading', reloadError: null, wrongLabel: null, rightLabel: null,
        catalog: result.catalog_proposal ? 'waiting' : 'none',
      };
      putNote(note);
      if (result.photo?.action === 'removed' && result.photo.removal) {
        handlePhotoRemoved(result.photo.removal, { reason, reasonKind: 'wrong_photo' });
      }
      void reloadAfterFix(note);
      refreshDeviceEnrichment();
    },
    [afterWrite, dropReview, handlePhotoRemoved, putNote, reloadAfterFix],
  );

  /**
   * §8.5 / Codex F14 — load the record again after an Apply, in place (never
   * `load()`, which would unmount every card and its unsaved edits). Only a
   * reload that worked moves the operator to the card and rings the fields;
   * one that failed says the values were saved but could not be shown, and
   * offers to try again right there.
   */
  const reloadAfterApply = useCallback(
    async (note: AppliedNote) => {
      patchApplied(note.reviewId, (n) => ({ ...n, reload: 'loading', reloadError: null }));
      try {
        const fresh = await fetchRecord();
        const label = appliedTargetLabel(fresh, note.targetPath, note.created);
        patchApplied(note.reviewId, (n) => ({ ...n, reload: 'ok', label }));
        if (note.targetPath) showWritten(note.targetPath, note.fields, note.ringLabel);
      } catch (err) {
        patchApplied(note.reviewId, (n) => ({ ...n, reload: 'failed', reloadError: pollErrorText(err) }));
      }
    },
    [patchApplied, serial, showWritten],
  );

  /** The first `applied` status for a review: close its dialog, reload, report. */
  const finishApplied = useCallback(
    (view: PhotoReview) => {
      if (openReviewRef.current === view.review_id) setOpenReview(null);
      afterWrite();
      const note: AppliedNote = {
        kind: 'applied',
        reviewId: view.review_id,
        ringLabel: reviewKindOf(view) === 'catalog' ? 'From the catalog' : undefined,
        targetPath: view.decision?.target_path ?? '',
        fields: view.decision?.fields_written ?? [],
        created: view.decision?.created === true,
        resync: resyncOutcome(view.resync),
        reload: 'loading',
        reloadError: null,
        label: null,
        placement: view.decision?.placement ?? null,
        sourceTab: view.source?.kind ? TAB_WORDS[view.source.kind] : undefined,
      };
      // FS-806 §8.4: a photo that moved leaves its source card at once.
      if (view.decision?.placement?.moved && view.photo?.url && view.source?.path) {
        const key = photoKey(view.source.path, view.photo.url);
        setMovedOff((prev) => (prev.has(key) ? prev : new Set(prev).add(key)));
      }
      putNote(note);
      void reloadAfterApply(note);
      refreshDeviceEnrichment();
    },
    [afterWrite, putNote, reloadAfterApply],
  );

  /**
   * Every status the drawer learns for a review — from its poll, or from a
   * full review its dialog loaded. The drawer is the one place an Apply is
   * followed, so this runs whether or not the dialog is open, and only these
   * authoritative answers end a tracked Apply (Codex N2): the record's list
   * of open reviews never does.
   */
  const handleReviewStatus = useCallback(
    (view: PhotoReview) => {
      const id = view.review_id;
      const had = reviewsRef.current[id];
      setLatestViews((prev) => ({ ...prev, [id]: view }));
      const readEnded = reviewReadOutcome(view);
      // The first answer past reading is the read's outcome; later statuses
      // (an Apply, a supersede) never rewrite it.
      if (readEnded) setReviewReads((prev) => (prev[id] ? prev : { ...prev, [id]: readEnded }));
      if (view.status === 'applied') {
        // FS-787 R5: one completion per Apply, not per review. A catalog
        // review keeps its id across versions and can be applied again in
        // the same drawer; each Apply gets its own reload, rings and note.
        const applied = `${id}:${view.last_apply?.apply_id ?? `v${view.version}`}`;
        if (!appliedHandled.current.has(applied)) {
          appliedHandled.current.add(applied);
          finishApplied(view);
        } else if (resyncOutcome(view.resync) !== 'pending') {
          // Codex F7 / N3: this review's refresh reported; its own note says so.
          const outcome = resyncOutcome(view.resync);
          patchApplied(id, (n) => ({ ...n, resync: outcome }));
        }
        if (resyncOutcome(view.resync) !== 'pending') {
          dropReview(id);
        } else {
          setReviews((prev) => ({
            ...prev,
            [id]: {
              ...(prev[id] ?? newEntry(id, view.dropped_on)),
              status: 'applied',
              awaitingResync: true,
              checking: false,
              pollEpoch: (prev[id]?.pollEpoch ?? 0) + (prev[id]?.awaitingResync ? 0 : 1),
            },
          }));
        }
        return;
      }
      if (!view.open) {
        // Codex N4: a tracked Apply that ended without writing — the Clip moved
        // to another registration, the photo went, the read failed — says so.
        if (had && isTracked(had)) {
          const code = view.superseded?.reason ?? view.last_apply?.code ?? null;
          putNote({
            kind: 'ended',
            reviewId: id,
            code: code ?? view.status,
            text:
              (code && code !== 'photo_removed' && EQUIPMENT_EDIT_ERRORS[code]) ||
              (view.status === 'superseded'
                ? 'This photo is no longer on the record, so nothing was changed.'
                : `The values were not applied (${view.status}). Nothing was changed.`),
          });
        }
        dropReview(id);
        return;
      }
      setReviews((prev) => {
        const base = prev[id] ?? newEntry(id, view.dropped_on);
        // FS-787: a status view names its kind and model too; a catalog
        // review the list has not reported yet still gets the right banner.
        const entry: ReviewEntry =
          reviewKindOf(view) === 'catalog' && base.kind !== 'catalog'
            ? {
              ...base,
              kind: 'catalog',
              catalogModel: view.catalog?.model_display ?? base.catalogModel,
              catalogType: view.catalog?.equipment_type ?? base.catalogType,
              appliesFromSerial: view.applies_from_serial ?? base.appliesFromSerial,
            }
            : base;
        const stuck = view.applying?.stuck === true;
        const holdReason = view.hold?.reason ?? entry.holdReason;
        const version = typeof view.version === 'number' ? view.version : entry.version;
        const lastApplyResult = view.last_apply?.result ?? null;
        if (
          entry === prev[id] &&
          entry.status === view.status &&
          entry.stuck === stuck &&
          entry.holdReason === holdReason &&
          entry.version === version &&
          entry.lastApplyResult === lastApplyResult &&
          !entry.checking
        ) {
          return prev;
        }
        return {
          ...prev,
          [id]: { ...entry, status: view.status, stuck, holdReason, checking: false, version, lastApplyResult },
        };
      });
      // The dialog opens by itself only for a photo this drawer session
      // uploaded, only once, when its read lands — and never over a dialog
      // that is already open (Codex F11): then its banner waits instead.
      if (
        had?.mine &&
        had.status === 'reading' &&
        view.status !== 'reading' &&
        !autoOpened.current.has(id) &&
        openReviewRef.current === null
      ) {
        autoOpened.current.add(id);
        setOpenReview(id);
      }
    },
    [dropReview, finishApplied, patchApplied, putNote],
  );

  const handlePollState = useCallback(
    (reviewId: string, state: ReviewPollState) => {
      const entry = reviewsRef.current[reviewId];
      if (entry?.awaitingResync && state.gaveUp) {
        // The refresh never reported within its window: said, not assumed.
        patchApplied(reviewId, (n) => (n.resync === 'pending' ? { ...n, resync: 'unreported' } : n));
        dropReview(reviewId);
        return;
      }
      if (entry && state.refused) {
        // Codex M1: a refusal on an accepted Apply is not its end — the Clip
        // may have moved, and the server answers with the review's terminal
        // status once the Function has recorded it. The Apply stays tracked,
        // and the write hold stays, until a terminal status or the deadline;
        // only at the deadline is "couldn't confirm" final.
        if (entry.status === 'applying' && !entry.awaitingResync && state.gaveUp) {
          putNote({
            kind: 'ended',
            reviewId,
            code: state.refused.code,
            text: `Couldn't confirm how applying the values ended: ${state.refused.message} Close the drawer and open it again to see the record as it is now.`,
          });
          dropReview(reviewId);
          return;
        }
        // A one-off look at a review the list stopped naming: out of scope
        // or gone is answer enough for a review this drawer is not applying.
        if (entry.checking && !isTracked(entry)) {
          dropReview(reviewId);
          return;
        }
      }
      setReviews((prev) => {
        const current = prev[reviewId];
        if (!current || current.poll === state) return prev;
        // A check that found nothing out in its window leaves the banner as it was.
        if (current.checking && state.gaveUp) return { ...prev, [reviewId]: { ...current, poll: IDLE_POLL, checking: false } };
        return { ...prev, [reviewId]: { ...current, poll: state } };
      });
    },
    [dropReview, patchApplied, putNote],
  );

  const handleCheckAgain = useCallback((reviewId: string) => {
    setReviews((prev) =>
      prev[reviewId]
        ? { ...prev, [reviewId]: { ...prev[reviewId], pollEpoch: prev[reviewId].pollEpoch + 1, poll: IDLE_POLL } }
        : prev,
    );
  }, []);

  /** The API accepted an Apply: follow it from here, with or without its dialog. */
  const handleApplyStarted = useCallback((reviewId: string) => {
    setLatestViews((prev) => {
      const next = { ...prev };
      delete next[reviewId];
      return next;
    });
    setReviews((prev) => {
      const entry = prev[reviewId] ?? newEntry(reviewId, 'hvac');
      return {
        ...prev,
        [reviewId]: { ...entry, status: 'applying', stuck: false, pollEpoch: entry.pollEpoch + 1, poll: IDLE_POLL },
      };
    });
  }, []);

  /** Don't apply, or Dismiss: the review is closed and its banner goes. */
  const handleReviewFinished = useCallback(
    (reviewId: string) => {
      setOpenReview(null);
      dropReview(reviewId);
    },
    [dropReview],
  );

  /**
   * A review being applied holds every write and every upload in the drawer
   * (Codex F12): its own Apply, one already running when the drawer opened,
   * or one whose dialog was closed. It stops holding when the server reports
   * the Apply stuck, or the drawer's 11-minute window for it runs out.
   */
  const reviewApplyHold = Object.values(reviews).some(
    (entry) => entry.status === 'applying' && !entry.stuck && !entry.poll.gaveUp,
  );

  /**
   * A photo is in the server's hands in a way that can change the record
   * without this drawer asking: on its way up, being read into the record
   * (a read that fills fields itself), or a review being applied. Every write
   * the drawer offers waits for those — a correction sent into one is refused
   * as stale at best, and lost under the other write at worst. A read into a
   * review writes nothing, so it holds nothing.
   *
   * A read this drawer gave up following does not count: the operator would
   * be locked out of a record for as long as a stuck job takes.
   */
  const photoWorking = anyPolling || reviewApplyHold || Object.values(photoUploading).some(Boolean);

  /** FS-807 — how a review's read ended, for the upload block's duplicate line. */
  const reviewReadOf = useCallback(
    (reviewId: string): DuplicateRead | null => reviewReads[reviewId] ?? null,
    [reviewReads],
  );

  const readStateFor = useCallback(
    (path: string): PhotoReadState => {
      const mine = Object.values(watches).filter((w) => w.path === path);
      if (mine.some((w) => w.state === 'polling')) return 'polling';
      if (mine.some((w) => w.state === 'gave_up')) return 'gave_up';
      return null;
    },
    [watches],
  );

  const optionsFor = useCallback(
    (spec: EquipmentFieldSpec): string[] => {
      // Plan 123 §4.1: the absence vocabulary is the plan's, not the record's
      // — the server takes exactly these two names and rejects the rest.
      if (spec.options === 'absent_components') return ABSENT_COMPONENT_OPTIONS;
      // FS-593: the slot kinds are the app's two, fixed; the GET sends no list.
      if (spec.options === 'filter_types') return FILTER_TYPE_OPTIONS;
      if (!spec.options || !record) return [];
      return record.options?.[spec.options] ?? [];
    },
    [record],
  );

  const readOnly = record?.status === 'ambiguous' || record?.status === 'missing';
  const reviewMode = record?.options?.photo_review === true;
  const reviewList = Object.values(reviews);
  /** Reviews with a banner: open ones, not one already applied. */
  const bannerList = reviewList.filter((entry) => !entry.awaitingResync && entry.status !== 'applied');
  const highlightFor = (path: string) =>
    highlight && highlight.path === path
      ? { fields: highlight.fields, nonce: highlight.nonce, label: highlight.label }
      : null;

  /**
   * FS-783 — Remove is offered only when the server's mode is on. Undo and
   * the protection retry are offered whatever the mode, so a rollback of the
   * mode never strands a removal.
   */
  const removalMode = record?.options?.photo_removal === true;
  /** FS-806 — Detach is offered only when the server's placement mode is on. */
  const placementMode = record?.options?.photo_placement === true;
  const removedUrls = new Set(
    Object.values(sessionRemovals)
      .filter((entry) => !undoneRemovals.has(entry.result.removal_id))
      .map((entry) => entry.result.url),
  );
  const removalCtx: PhotoRemovalCtx = {
    serial,
    record,
    canRemove: removalMode && !readOnly,
    hold: photoWorking,
    removedUrls,
    sessionRemovals,
    undoneRemovals,
    onBeforeWrite: handleBeforeWrite,
    onRemoved: handlePhotoRemoved,
    onUndone: handlePhotoRestored,
    onWorking: handlePhotoWorking,
    reload: () => void reloadInPlace(),
    canDetach: placementMode && !readOnly,
    detachedKeys,
    onDetached: handlePhotoDetached,
    movedOff,
    reviewReadOf,
    canRotate: !readOnly,
  };

  /** FS-876 — the dismissed reviews of one record's model, as quiet lines on its card. */
  const dismissedOn = (path: string): React.ReactNode => {
    const entries = (record?.dismissed_reviews ?? []).filter((entry) => entry.source_path === path);
    if (entries.length === 0 || readOnly) return null;
    return entries.map((entry) => (
      <DismissedReviewLine
        key={entry.review_id}
        entry={entry}
        state={reopening[entry.review_id]}
        onReviewAgain={(e) => void handleReviewAgain(e)}
      />
    ));
  };

  /** The card props every equipment card shares. */
  const cardCommon = (doc: EquipmentDocument) => ({
    removal: removalCtx,
    serial,
    doc,
    reviewNotice: dismissedOn(doc.path),
    optionsFor,
    onSaved: handleSaved,
    onBeforeWrite: handleBeforeWrite,
    onPhotoUploaded: handlePhotoUploaded,
    photoWorking,
    onPhotoWorking: (working: boolean) => handlePhotoWorking(doc.path, working),
    reviewMode,
    uploadHold: reviewApplyHold,
    highlight: highlightFor(doc.path),
  });

  /** FS-781 — what an empty Outdoor unit or Indoor coil tab offers. */
  const emptyTab = (hint: PhotoComponentHint, legacyText: React.ReactNode) => {
    if (!record) return null;
    if (!reviewMode) return <p className="text-sm text-muted-foreground">{legacyText}</p>;
    if (!record.hvac) {
      return (
        <p className="text-sm text-muted-foreground" data-testid="empty-tab-no-hvac">
          This system has no HVAC record, so a photo has nowhere to go. Add the system in the app first.
        </p>
      );
    }
    const hvac = record.hvac;
    return (
      <EmptyTabPhotoZone
        serial={serial}
        hvac={hvac}
        hint={hint}
        disabled={reviewApplyHold}
        onUploaded={handlePhotoUploaded}
        onWorking={(working) => handlePhotoWorking(`${hvac.path}#${hint}`, working)}
        removal={removalCtx}
      />
    );
  };

  const panel = (key: EquipmentTargetKind): React.ReactNode => {
    if (!record) return null;
    if (key === 'hvac') {
      return record.hvac ? (
        <EquipmentDocCard
          key={`${record.hvac.path}#${reloadNonce}`}
          {...cardCommon(record.hvac)}
          kind="hvac"
          title="Furnace or air handler"
          readState={readStateFor(record.hvac.path)}
        />
      ) : (
        <p className="text-sm text-muted-foreground">
          The current app registration has no furnace or air handler record.
        </p>
      );
    }
    if (key === 'ac') {
      return record.ac_systems.length > 0 ? (
        <div className="space-y-3">
          {record.ac_systems.map((doc) => (
            <EquipmentDocCard
              key={`${doc.path}#${reloadNonce}`}
              {...cardCommon(doc)}
              kind="ac"
              title={String(doc.fields.name || '').trim() || 'A/C record with no name'}
              readState={readStateFor(doc.path)}
            />
          ))}
        </div>
      ) : (
        emptyTab('outdoor_condenser', 'This system has no A/C record under the current app registration.')
      );
    }
    if (key === 'coil') {
      // Plan 120 — the coil children of the current HVAC system. Field edits
      // only: the card renders no photo block, and the drawer posts to
      // firebase-api with target kind `coil`.
      return (record.coils ?? []).length > 0 ? (
        <div className="space-y-3">
          {(record.coils ?? []).map((doc) => (
            <EquipmentDocCard
              key={`${doc.path}#${reloadNonce}`}
              {...cardCommon(doc)}
              kind="coil"
              title={String(doc.fields.modelNumber || '').trim() || 'Coil record with no model number'}
              readState={null}
            />
          ))}
        </div>
      ) : (
        emptyTab(
          'indoor_coil',
          'This system has no indoor coil record. A coil record is created by the server when a coil label photo is attached to the indoor unit and read; it is not created here.',
        )
      );
    }
    const current = record.filters.find((f) => filterRowAccess(f, serial) === 'own');
    return (
      <div className="space-y-3">
        {record.filters.some((f) => typeof f.own === 'boolean') ? (
          <FilterSlotCards
            serial={serial}
            filters={record.filters}
            reloadNonce={reloadNonce}
            clipLinks={clipLinks}
            optionsFor={optionsFor}
            onSaved={handleSaved}
            onBeforeWrite={handleBeforeWrite}
            readStateFor={readStateFor}
            onPhotoUploaded={handlePhotoUploaded}
            photoWorking={photoWorking}
            onPhotoWorking={handlePhotoWorking}
            uploadHold={reviewApplyHold}
            removal={removalCtx}
          />
        ) : current ? (
          <EquipmentDocCard
            key={`${current.path}#${reloadNonce}`}
            {...cardCommon(current)}
            kind="filter"
            title="Current filter"
            readState={readStateFor(current.path)}
          />
        ) : (
          <p className="text-sm text-muted-foreground">{EQUIPMENT_EDIT_ERRORS.no_current_filter}</p>
        )}
        <FilterChangeForm
          serial={serial}
          currentFilter={current}
          brands={brands}
          deviceUpdateTime={record.current_claim?.device_update_time ?? null}
          onSaved={handleSaved}
          onBeforeWrite={handleBeforeWrite}
          photoWorking={photoWorking}
        />
      </div>
    );
  };

  const body = () => {
    if (loading) return <p className="text-muted-foreground">{LOADING_TEXT}</p>;
    if (loadError) return <p className="text-red-700" role="alert">{loadError}</p>;
    if (!record) return null;

    if (record.status === 'missing') {
      return (
        <Banner tone="warn">
          <p>This device is not registered to an app account.</p>
          <p className="mt-1">
            There are no equipment or filter records to correct until somebody claims it in
            the MyFluxHive app.
          </p>
        </Banner>
      );
    }

    if (record.status === 'ambiguous') {
      return (
        <div className="space-y-3">
          <Banner tone="warn">
            <p>{EQUIPMENT_EDIT_ERRORS.ambiguous_claims}</p>
            <p className="mt-2">
              <Link
                to={`/devices/${device.id}/claims`}
                className="text-blue-700 underline"
              >
                Open the App Claims tab
              </Link>
            </p>
          </Banner>
          <div>
            <p className="text-sm font-medium">
              {record.claim_count} registrations on this device
            </p>
            <ul className="mt-1 space-y-1 text-xs font-mono text-muted-foreground break-all">
              {record.claims.map((claim) => (
                <li key={claim.path}>
                  {claim.path}
                  {claim.is_current && <span className="ml-1 text-blue-700">· current</span>}
                </li>
              ))}
            </ul>
          </div>
        </div>
      );
    }

    const mounted = new Set([...visited, tab]);
    return (
      <div className="space-y-3">
        {record.status === 'stale' && (
          <Banner tone="warn">
            The current registration has missing or orphaned parent records. Check the App
            Claims tab if a value here looks wrong.
          </Banner>
        )}

        <Tabs value={tab} onValueChange={(value) => {
          const selected = TABS.find((entry) => entry.key === value);
          if (selected) showTab(selected.key);
        }}>
        <TabsList variant="line" aria-label="Equipment records" className="flex w-full flex-wrap justify-start border-b">
          {TABS.map((t) => {
            const waiting = bannerList.some((r) => r.droppedOn === t.key);
            return (
              <TabsTrigger
                key={t.key}
                value={t.key}
                title={waiting ? `${TAB_TITLES[t.key]}. A label photo on this tab is waiting for review.` : TAB_TITLES[t.key]}
                className="shrink-0 flex-none px-3"
              >
                {t.label}
                {waiting && (
                  <span aria-hidden="true" className="ml-1 inline-block h-2 w-2 rounded-full bg-status-warning-border align-middle" />
                )}
              </TabsTrigger>
            );
          })}
        </TabsList>

        {/* FS-784 — every tab opened so far stays mounted and is hidden when
            it is not the one in view, so an unsaved edit on any card survives
            a tab switch and the reload an Apply causes. */}
        {TABS.filter((t) => mounted.has(t.key)).map((t) => (
          <TabsContent
            key={t.key}
            value={t.key}
            keepMounted
            aria-label={TAB_WORDS[t.key]}
            data-testid={`equipment-panel-${t.key}`}
            className="space-y-3"
          >
            {bannerList
              .filter((entry) => entry.droppedOn === t.key)
              .map((entry) => (
                <ReviewBanner
                  key={entry.reviewId}
                  serial={serial}
                  entry={entry}
                  onOpen={setOpenReview}
                  onCheckAgain={handleCheckAgain}
                  onDismiss={(id) => void handleDismiss(id)}
                  onRebind={(id) => void handleRebind(id)}
                />
              ))}
            {panel(t.key)}
          </TabsContent>
        ))}
        </Tabs>
      </div>
    );
  };

  return (
    <SideDrawer
      title="Edit equipment"
      ariaLabel="Edit equipment"
      closeLabel="Close the equipment editor"
      onClose={onClose}
      subtitle={
        <>
          Correcting the app records for{' '}
          <span className="font-mono">{serial}</span> on the homeowner's behalf.
          {!readOnly && ' Every change is recorded against your account.'}
        </>
      }
    >
      {/*
        A photo missing the drop zone must not become a page the browser
        navigates to: that closes the drawer and loses whatever correction was
        half typed in it. The drop zone stops its own events; this catches
        every other one over the panel and does nothing with it.
      */}
      <div
        ref={panelRef}
        data-testid="equipment-drawer-panel"
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => e.preventDefault()}
      >
        {saved && (
          <div role="status" className="border-b bg-green-50 px-4 py-3 text-sm text-green-900">
            <span className="font-medium">Saved.</span>{' '}
            <span>The page behind this drawer catches up with the change within a few seconds.</span>
          </div>
        )}

        <div
          aria-live="polite"
          data-testid="photo-review-live"
          className={notes.length > 0 ? 'border-b px-4 py-3 text-sm space-y-1' : 'sr-only'}
        >
          {notes.map((note) =>
            note.kind === 'applied' ? (
              <p
                key={note.reviewId}
                data-testid="photo-review-note"
                className={note.reload === 'failed' ? 'text-amber-900' : 'text-green-900'}
              >
                {appliedNoteText(note)}
                {note.reload === 'failed' && (
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => void reloadAfterApply(note)}
                    title="Load the record again to see the saved values; anything typed on the cards is kept"
                    className="ml-2"
                  >
                    Reload
                  </Button>
                )}
              </p>
            ) : note.kind === 'fixed' ? (
              <p
                key={note.reviewId}
                data-testid="photo-review-note"
                className={note.reload === 'failed' || note.result.photo?.action === 'not_removed' ? 'text-amber-900' : 'text-green-900'}
              >
                {fixedNoteText(note)}
                {note.reload === 'failed' && (
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => void reloadAfterFix(note)}
                    title="Load the record again to see it as it is now; anything typed on the cards is kept"
                    className="ml-2"
                  >
                    Reload
                  </Button>
                )}
              </p>
            ) : (
              <p
                key={note.reviewId}
                data-testid="photo-review-note"
                className="text-amber-900"
                title={note.code ? `Code: ${note.code}` : undefined}
              >
                {note.text}
              </p>
            ),
          )}
        </div>

        {/* FS-783 §8.3 / §8.5 — what each removal and Undo did, said as it lands. */}
        <div
          aria-live="polite"
          data-testid="photo-removal-live"
          className={removalNotes.length > 0 || plainReloadError ? 'border-b px-4 py-3 text-sm space-y-2' : 'sr-only'}
        >
          {plainReloadError && (
            <p className="text-amber-900" data-testid="photo-removal-reload-error">
              {`Couldn't reload the record: ${plainReloadError}.`}
              <Button
                variant="secondary"
                size="sm"
                onClick={() => void reloadInPlace()}
                title="Load the record again to see it as it is now; anything typed on the cards is kept"
                className="ml-2"
              >
                Reload
              </Button>
            </p>
          )}
          {removalNotes.map((note) => (
            <RemovalSummary
              key={note.key}
              note={note}
              record={record}
              onGoToField={goToField}
              onRetryProtection={(n) => void handleRetryProtection(n)}
              onReload={(key) => void reloadAfterRemoval(key)}
            />
          ))}
        </div>

        <div className="p-4">{body()}</div>

        {reviewList
          .filter((entry) => pollModeOf(entry) !== null)
          .map((entry) => (
            <ReviewPoller
              key={entry.reviewId}
              serial={serial}
              entry={entry}
              onStatus={handleReviewStatus}
              onPollState={handlePollState}
            />
          ))}

        {openReview && (
          <PhotoReviewDialog
            key={openReview}
            serial={serial}
            reviewId={openReview}
            photoRotation={
              readOnly ? null : { degreesOf: (url) => photoRotationOn(record, url), onRotated: () => void reloadInPlace() }
            }
            uploadReason={reviews[openReview]?.typedReason ?? null}
            liveStatus={latestViews[openReview] ?? null}
            poll={reviews[openReview]?.poll ?? IDLE_POLL}
            heldByOther={Object.values(reviews).some(
              (entry) =>
                entry.reviewId !== openReview && entry.status === 'applying' && !entry.stuck && !entry.poll.gaveUp,
            )}
            onClose={() => setOpenReview(null)}
            onFinished={handleReviewFinished}
            onApplyStarted={handleApplyStarted}
            onReviewLoaded={handleReviewStatus}
            onCheckAgain={handleCheckAgain}
            onBeforeApply={handleBeforeWrite}
            onFixed={handleReviewFixed}
          />
        )}
      </div>
    </SideDrawer>
  );
};

export default EditEquipmentDrawer;
